import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlignCenter, AlignLeft, AlignRight, ArrowDown, ArrowLeft, ArrowRight, ArrowUp, BarChart3, Bold, Copy, Download, ExternalLink, ImagePlus, Info, Italic, MapPin, PieChart as PieChartIcon, Plus, RotateCcw, Save, Sparkles, TableProperties, Trash2, TrendingUp, Underline, X } from 'lucide-react';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import SheetAnalyticsBuilder from './SheetAnalyticsBuilder';
import SheetChart from './SheetChart';
import toast from 'react-hot-toast';
import { storage } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { sheetFormService } from '../../services/sheetFormService';
import { sheetService } from '../../services/sheetService';
import { sheetWorkspaceService } from '../../services/sheetWorkspaceService';
import { userService } from '../../services/userService';
import organizationService from '../../services/organizationService';
import { sanitizeAnalytics, suggestAnalytics } from '../../utils/sheetAnalytics';
import { getSheetPath, getWorkspacePath, isLikelyWorkspaceCustomHost } from '../../utils/sheetDomains';
import {
  buildFormulaFromConfig,
  FILTER_LIST_OPERATOR_OPTIONS,
  getColumnLetter,
  getFilterListOperatorLabel,
  hasHeaderBaseValue,
  hasHeaderFormula,
  parseFormulaToConfig,
  resolveRowFormulas,
  sanitizeHeaderConditionalFormulaRules,
  VISUAL_FORMULA_OPTIONS
} from '../../utils/sheetFormula';
import SmartSensorField from '../forms/SmartSensorField';
import {
  evaluateHeaderConditionalState,
  sanitizeHeaderConditionalLogic
} from '../../utils/sheetConditionalLogic';
import {
  normalizeSheetOptionSource,
  normalizeSheetOptionSourceFilters,
  resolveHeaderOptions,
  sanitizeHeaderConditionalOptions
} from '../../utils/sheetOptions';
import { buildSheetCsvTemplate, getSheetCsvColumns } from '../../utils/sheetCsv';

const createHeaderId = () => `column_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const ACTIVE_ACTIONS_HEADER_ID = 'system_active_actions';
const createOptionSourceFilterDraft = (sourceFilter = null) => ({
  sourceHeaderId: `${sourceFilter?.sourceHeaderId || ''}`.trim(),
  operator: `${sourceFilter?.operator || 'equals'}`.trim() || 'equals',
  valueMode: `${sourceFilter?.valueMode || 'header'}`.trim() === 'static' ? 'static' : 'header',
  headerId: `${sourceFilter?.headerId || ''}`.trim(),
  value: `${sourceFilter?.value ?? ''}`
});
const getHeaderOptionSourceDraft = (optionSource) => ({
  sheetId: `${optionSource?.sheetId || ''}`.trim(),
  headerId: `${optionSource?.headerId || ''}`.trim(),
  filterLogic: `${optionSource?.filterLogic || ''}`.trim().toLowerCase() === 'or' ? 'or' : 'and',
  sourceFilters: (() => {
    if (Array.isArray(optionSource?.sourceFilters)) {
      return optionSource.sourceFilters.map((sourceFilter) => createOptionSourceFilterDraft(sourceFilter));
    }

    const normalizedLegacyFilters = normalizeSheetOptionSourceFilters(
      optionSource?.sourceFilter || optionSource?.filter
    );
    return normalizedLegacyFilters.map((sourceFilter) => createOptionSourceFilterDraft(sourceFilter));
  })()
});
const createConditionalOptionRuleDraft = () => ({
  whenHeaderId: '',
  operator: 'equals',
  value: '',
  options: [],
  pendingOption: '',
  optionSource: getHeaderOptionSourceDraft(null)
});
const createConditionalDisplayNameRuleDraft = () => ({
  whenHeaderId: '',
  operator: 'equals',
  value: '',
  displayName: ''
});
const normalizeConditionalDisplayNameRuleForEditor = (rule) => ({
  whenHeaderId: `${rule?.whenHeaderId || rule?.headerId || rule?.fieldId || ''}`.trim(),
  operator: `${rule?.operator || 'equals'}`.trim() || 'equals',
  value: `${rule?.value ?? ''}`,
  displayName: `${rule?.displayName || rule?.label || rule?.displayLabel || ''}`
});
const normalizeConditionalOptionRuleForEditor = (rule) => ({
  whenHeaderId: `${rule?.whenHeaderId || rule?.headerId || rule?.fieldId || ''}`.trim(),
  operator: `${rule?.operator || 'equals'}`.trim() || 'equals',
  value: `${rule?.value ?? ''}`,
  options: Array.isArray(rule?.options) ? rule.options.map((option) => `${option || ''}`.trim()).filter(Boolean) : [],
  pendingOption: '',
  optionSource: getHeaderOptionSourceDraft(rule?.optionSource)
});
const getProfileConfigDraft = (profileConfig) => ({
  imageHeaderId: `${profileConfig?.imageHeaderId || ''}`.trim(),
  labelHeaderId: `${profileConfig?.labelHeaderId || ''}`.trim()
});
const clearOptionSourceHeaderReferences = (optionSource, currentSheetId, removedHeaderId) => {
  const nextSource = getHeaderOptionSourceDraft(optionSource);
  if (nextSource.sheetId === currentSheetId && nextSource.headerId === removedHeaderId) {
    nextSource.headerId = '';
  }
  nextSource.sourceFilters = (nextSource.sourceFilters || []).map((sourceFilter) => {
    const nextFilter = { ...sourceFilter };
    if (nextSource.sheetId === currentSheetId && nextFilter.sourceHeaderId === removedHeaderId) {
      nextFilter.sourceHeaderId = '';
    }
    if (nextFilter.headerId === removedHeaderId) {
      nextFilter.headerId = '';
    }
    return nextFilter;
  });
  return nextSource;
};
const SHEET_RENDER_PRESET_OPTIONS = [
  { value: 'default', label: 'Default rows' },
  { value: 'profiles', label: 'Profiles UI' },
  { value: 'list-card', label: 'List cards' },
  { value: 'kanban', label: 'Kanban board' },
  { value: 'calendar', label: 'Calendar' }
];
const normalizeSheetRenderPreset = (value) => (
  SHEET_RENDER_PRESET_OPTIONS.some((option) => option.value === `${value || ''}`.trim())
    ? `${value || ''}`.trim()
    : 'default'
);
const SHEET_RENDER_FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter (Modern Sans-serif)' },
  { value: 'Roboto', label: 'Roboto (Google Sans-serif)' },
  { value: 'Open Sans', label: 'Open Sans (Friendly Sans-serif)' },
  { value: 'Montserrat', label: 'Montserrat (Geometric Sans-serif)' },
  { value: 'Poppins', label: 'Poppins (Rounded Sans-serif)' },
  { value: 'Lato', label: 'Lato (Humanist Sans-serif)' },
  { value: 'Nunito', label: 'Nunito (Rounded Sans-serif)' },
  { value: 'Source Sans Pro', label: 'Source Sans Pro (Clean Sans-serif)' },
  { value: 'Raleway', label: 'Raleway (Elegant Sans-serif)' },
  { value: 'Ubuntu', label: 'Ubuntu (Modern Sans-serif)' },
  { value: 'Playfair Display', label: 'Playfair Display (Elegant Serif)' },
  { value: 'Merriweather', label: 'Merriweather (Reading Serif)' },
  { value: 'Georgia', label: 'Georgia (Classic Serif)' },
  { value: 'Times New Roman', label: 'Times New Roman (Traditional Serif)' },
  { value: 'Crimson Text', label: 'Crimson Text (Book Serif)' }
];
const normalizeSheetRenderFont = (value) => (
  SHEET_RENDER_FONT_OPTIONS.some((option) => option.value === `${value || ''}`.trim())
    ? `${value || ''}`.trim()
    : 'Inter'
);
const normalizePresetUiColumns = (value, fallback = 3) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(1, Math.min(4, Math.round(numeric)));
};
const normalizeListCardImageLayout = (value) => (
  `${value || ''}`.trim() === 'full-width' ? 'full-width' : 'inline'
);
const getSheetFormUrl = (formId) => `${window.location.origin}/sheet-form/${formId}`;
const getSheetTitleKey = (value = '') => `${value || ''}`.trim().toLowerCase();
const SHEET_FORM_WIDTH_OPTIONS = [
  { value: 'full', label: 'Full row' },
  { value: 'two-thirds', label: 'Two thirds' },
  { value: 'half', label: 'Half row' },
  { value: 'third', label: 'Third row' }
];
const SHEET_FORM_RICH_TEXT_ALIGN_OPTIONS = [
  { value: 'left', icon: AlignLeft, label: 'Left' },
  { value: 'center', icon: AlignCenter, label: 'Center' },
  { value: 'right', icon: AlignRight, label: 'Right' }
];
const SHEET_FORM_SLIDE_MODE_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'auto', label: 'Auto' }
];
const normalizeSheetFormFieldWidth = (value) => (
  SHEET_FORM_WIDTH_OPTIONS.some((option) => option.value === `${value || ''}`.trim())
    ? `${value || ''}`.trim()
    : 'full'
);
const normalizeSheetFormRepeatableFieldWidths = (widths = {}, headerIds = []) => {
  const headerIdSet = new Set((Array.isArray(headerIds) ? headerIds : []).map((headerId) => `${headerId || ''}`.trim()).filter(Boolean));
  const sourceWidths = widths && typeof widths === 'object' && !Array.isArray(widths) ? widths : {};
  return Object.entries(sourceWidths).reduce((acc, [headerId, width]) => {
    const normalizedHeaderId = `${headerId || ''}`.trim();
    if (normalizedHeaderId && (!headerIdSet.size || headerIdSet.has(normalizedHeaderId))) {
      acc[normalizedHeaderId] = normalizeSheetFormFieldWidth(width);
    }
    return acc;
  }, {});
};
const getSheetFormRepeatableFieldWidthsFromForm = (form = {}) => {
  const repeatableHeaderIds = Array.isArray(form?.repeatableHeaderIds) ? form.repeatableHeaderIds : [];
  return normalizeSheetFormRepeatableFieldWidths(
    {
      ...(form?.repeatableFieldWidths && typeof form.repeatableFieldWidths === 'object' ? form.repeatableFieldWidths : {}),
      ...(Array.isArray(form?.repeatableFields)
        ? form.repeatableFields.reduce((acc, field) => {
            const headerId = `${field?.id || ''}`.trim();
            if (headerId) acc[headerId] = field?.formWidth;
            return acc;
          }, {})
        : {})
    },
    repeatableHeaderIds
  );
};
const normalizeSheetFormTextBlockAlignment = (value) => (
  SHEET_FORM_RICH_TEXT_ALIGN_OPTIONS.some((option) => option.value === `${value || ''}`.trim())
    ? `${value || ''}`.trim()
    : 'left'
);
const normalizeSheetFormSlideMode = (value) => (
  SHEET_FORM_SLIDE_MODE_OPTIONS.some((option) => option.value === `${value || ''}`.trim())
    ? `${value || ''}`.trim()
    : 'manual'
);
const normalizeSheetFormSlideIntervalSeconds = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 5;
  return Math.max(2, Math.min(30, Math.round(numeric)));
};
const normalizeSheetFormLogoUrl = (value) => `${value || ''}`.trim();
const normalizeSheetFormDateTimeInput = (value) => {
  const normalized = `${value || ''}`.trim();
  if (!normalized) return '';
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return '';
  const offsetDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  return offsetDate.toISOString().slice(0, 16);
};
const getSheetSensorFieldType = (inputMode) => {
  switch (`${inputMode || ''}`.trim()) {
    case 'barcode':
      return 'barcodeInput';
    case 'qrcode':
      return 'qrCodeInput';
    case 'nfc':
      return 'nfcInput';
    default:
      return '';
  }
};
const SHEET_FORM_INPUT_MODE_OPTIONS = [
  { value: 'default', label: 'Default Input' },
  { value: 'barcode', label: 'Barcode Scanner' },
  { value: 'qrcode', label: 'QR Code Scanner' },
  { value: 'nfc', label: 'NFC Reader' }
];
const normalizeSheetFormInputMode = (value) => (
  SHEET_FORM_INPUT_MODE_OPTIONS.some((option) => option.value === `${value || ''}`.trim())
    ? `${value || ''}`.trim()
    : 'default'
);
const normalizeLegacyHeaderType = (value) => {
  const normalized = `${value || ''}`.trim();
  if (['barcode', 'qrcode', 'nfc'].includes(normalized)) return 'text';
  return normalized;
};
const getLegacyInputModeFromHeaderType = (value) => {
  const normalized = `${value || ''}`.trim();
  return ['barcode', 'qrcode', 'nfc'].includes(normalized) ? normalized : 'default';
};
const canUseSensorInputMode = (headerType) => (
  ['text', 'email', 'telephone', 'address', 'hyperlink'].includes(`${headerType || ''}`.trim())
);
const createSheetFormFieldLayoutItem = (headerId, width = 'full') => ({
  id: `field_${headerId}`,
  type: 'field',
  headerId,
  width: normalizeSheetFormFieldWidth(width)
});
const createSheetFormSectionDividerItem = () => ({
  id: `section_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  type: 'sectionDivider',
  label: 'Section'
});
const createSheetFormPageBreakItem = () => ({
  id: `page_break_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  type: 'pageBreak'
});
const createSheetFormTextBlockItem = () => ({
  id: `text_block_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  type: 'textBlock',
  contentHtml: '<p>Add supporting text here.</p>',
  alignment: 'left'
});
const createSheetFormImageSlideItem = () => ({
  id: `image_slide_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  type: 'imageSlide',
  slides: [],
  slideMode: 'manual',
  autoAdvanceSeconds: 5
});
const sanitizeSheetFormSlideItems = (slides = []) => (
  (Array.isArray(slides) ? slides : [])
    .map((slide, index) => {
      const url = `${slide?.url || slide?.src || ''}`.trim();
      if (!url) return null;
      return {
        id: `${slide?.id || `slide_${index + 1}`}`.trim() || `slide_${index + 1}`,
        url,
        alt: `${slide?.alt || slide?.caption || ''}`.trim()
      };
    })
    .filter(Boolean)
);

const sanitizeSheetFormRichTextHtml = (html) => {
  const source = `${html || ''}`.trim();
  if (!source) return '<p></p>';
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return source;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${source}</div>`, 'text/html');
  const root = doc.body.firstElementChild || doc.body;
  const allowedTags = new Set(['p', 'div', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a']);

  const sanitizeNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return doc.createTextNode(node.textContent || '');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return doc.createDocumentFragment();
    }

    const tag = node.tagName.toLowerCase();
    const fragment = doc.createDocumentFragment();
    const sanitizedChildren = Array.from(node.childNodes).map((child) => sanitizeNode(child));

    if (!allowedTags.has(tag)) {
      sanitizedChildren.forEach((child) => fragment.appendChild(child));
      return fragment;
    }

    const element = doc.createElement(tag === 'b' ? 'strong' : tag === 'i' ? 'em' : tag);
    if (tag === 'a') {
      const href = `${node.getAttribute('href') || ''}`.trim();
      if (/^https?:\/\//i.test(href)) {
        element.setAttribute('href', href);
        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noreferrer');
      }
    }

    sanitizedChildren.forEach((child) => element.appendChild(child));

    if (['p', 'div'].includes(tag)) {
      const textAlign = `${node.style?.textAlign || node.getAttribute('align') || ''}`.trim().toLowerCase();
      if (['left', 'center', 'right'].includes(textAlign)) {
        element.style.textAlign = textAlign;
      }
    }

    return element;
  };

  const output = doc.createElement('div');
  Array.from(root.childNodes).forEach((child) => {
    output.appendChild(sanitizeNode(child));
  });

  const normalized = output.innerHTML.trim();
  return normalized || '<p></p>';
};

const SheetFormRichTextEditor = ({ value, onChange, itemId }) => {
  const editorRef = useRef(null);
  const safeValue = useMemo(() => sanitizeSheetFormRichTextHtml(value), [value]);

  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== safeValue) {
      editorRef.current.innerHTML = safeValue;
    }
  }, [safeValue]);

  const handleInput = useCallback(() => {
    if (!editorRef.current) return;
    const nextHtml = sanitizeSheetFormRichTextHtml(editorRef.current.innerHTML);
    if (editorRef.current.innerHTML !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
    }
    onChange(nextHtml);
  }, [onChange]);

  const handleCommand = useCallback((command, commandValue = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, commandValue);
    handleInput();
  }, [handleInput]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-3">
        {[
          { command: 'bold', label: 'Bold', icon: Bold },
          { command: 'italic', label: 'Italic', icon: Italic },
          { command: 'underline', label: 'Underline', icon: Underline }
        ].map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={`${itemId}_${action.command}`}
              type="button"
              onClick={() => handleCommand(action.command)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 transition hover:border-[#008080]/35 hover:text-[#008080]"
              title={action.label}
              aria-label={action.label}
            >
              <Icon className="h-4 w-4" />
            </button>
          );
        })}
        {SHEET_FORM_RICH_TEXT_ALIGN_OPTIONS.map((option) => {
          const Icon = option.icon;
          const command = option.value === 'center'
            ? 'justifyCenter'
            : option.value === 'right'
              ? 'justifyRight'
              : 'justifyLeft';
          return (
            <button
              key={`${itemId}_${option.value}`}
              type="button"
              onClick={() => handleCommand(command)}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-[#008080]/35 hover:text-[#008080]"
              title={`Align ${option.label}`}
              aria-label={`Align ${option.label}`}
            >
              <Icon className="h-4 w-4" />
              <span>{option.label}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => handleCommand('insertUnorderedList')}
          className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-[#008080]/35 hover:text-[#008080]"
        >
          List
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData?.getData('text/plain') || '';
          if (text) {
            document.execCommand('insertText', false, text);
          }
          handleInput();
        }}
        className="min-h-[160px] rounded-b-2xl px-4 py-4 text-sm leading-6 text-slate-700 outline-none [&_a]:text-[#008080] [&_a]:underline [&_ol]:ml-5 [&_ol]:list-decimal [&_p]:mb-3 [&_ul]:ml-5 [&_ul]:list-disc"
      />
    </div>
  );
};
const getEffectiveSheetFormAccessMode = (_sheetVisibility, accessMode = 'public') => (
  `${accessMode || ''}`.trim() === 'internal' ? 'internal' : 'public'
);
const isEligibleFormHeader = (header) => (
  Boolean(
    header &&
    `${header?.id || ''}`.trim() &&
    `${header?.label || ''}`.trim() &&
    !`${header?.systemField || ''}`.trim() &&
    `${header?.id || ''}`.trim() !== ACTIVE_ACTIONS_HEADER_ID &&
    `${header?.type || ''}`.trim() !== 'profile' &&
    !`${header?.formula || ''}`.trim() &&
    !header?.formulaConfig
  )
);
const createDefaultSheetFormConfig = (headers = [], sheetVisibility = 'public') => sheetFormService.getDefaultConfig(headers, sheetVisibility);
const normalizeSheetFormSubmissionMode = (value) => (
  `${value || ''}`.trim() === 'modular' ? 'modular' : 'singleRow'
);
const normalizeMinimumRepeatableRows = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  return Math.max(1, Math.min(25, Math.round(numeric)));
};
const normalizeRepeatableEntryMode = (value) => (
  `${value || ''}`.trim() === 'allAtOnce' ? 'allAtOnce' : 'stepper'
);
const normalizeSheetFormPrefillSelectorConfig = (config = {}) => ({
  enabled: config?.enabled === true,
  sourceSheetId: `${config?.sourceSheetId || ''}`.trim(),
  actionId: `${config?.actionId || ''}`.trim()
});
const sanitizeSheetRenderPresetConfig = (preset, config, headers = []) => {
  if (preset === 'profiles') {
    const validProfileHeaderIds = new Set(
      (Array.isArray(headers) ? headers : [])
        .filter((header) => header?.type === 'profile')
        .map((header) => `${header?.id || ''}`.trim())
        .filter(Boolean)
    );
    const validHeaderIds = new Set(
      (Array.isArray(headers) ? headers : [])
        .map((header) => `${header?.id || ''}`.trim())
        .filter(Boolean)
    );
    const profileHeaderId = `${config?.profileHeaderId || ''}`.trim();
    const bodyHeaderId = `${config?.bodyHeaderId || ''}`.trim();
    const actionHeaderId = `${config?.actionHeaderId || ''}`.trim();

    return {
      profileHeaderId: validProfileHeaderIds.has(profileHeaderId) ? profileHeaderId : '',
      bodyHeaderId: validHeaderIds.has(bodyHeaderId) ? bodyHeaderId : '',
      actionHeaderId: validHeaderIds.has(actionHeaderId) ? actionHeaderId : '',
      uiColumns: normalizePresetUiColumns(config?.uiColumns, 3)
    };
  }

  if (preset === 'list-card') {
    const normalizedHeaders = Array.isArray(headers) ? headers : [];
    const validHeaderIds = new Set(
      normalizedHeaders
        .map((header) => `${header?.id || ''}`.trim())
        .filter(Boolean)
    );
    const validCategoryHeaderIds = new Set(
      normalizedHeaders
        .filter((header) => !['multiselect', 'list', 'image', 'textarea'].includes(`${header?.type || ''}`.trim()))
        .map((header) => `${header?.id || ''}`.trim())
        .filter(Boolean)
    );
    const validTagHeaderIds = new Set(
      normalizedHeaders
        .filter((header) => header?.type === 'list' || header?.type === 'multiselect')
        .map((header) => `${header?.id || ''}`.trim())
        .filter(Boolean)
    );
    const imageHeaderId = `${config?.imageHeaderId || ''}`.trim();
    const categoryHeaderId = `${config?.categoryHeaderId || ''}`.trim();
    const titleHeaderId = `${config?.titleHeaderId || ''}`.trim();
    const bodyHeaderId = `${config?.bodyHeaderId || ''}`.trim();
    const tagHeaderId = `${config?.tagHeaderId || ''}`.trim();

    return {
      imageHeaderId: validHeaderIds.has(imageHeaderId) ? imageHeaderId : '',
      categoryHeaderId: validCategoryHeaderIds.has(categoryHeaderId) ? categoryHeaderId : '',
      titleHeaderId: validHeaderIds.has(titleHeaderId) ? titleHeaderId : '',
      bodyHeaderId: validHeaderIds.has(bodyHeaderId) ? bodyHeaderId : '',
      tagHeaderId: validTagHeaderIds.has(tagHeaderId) ? tagHeaderId : '',
      imageLayout: normalizeListCardImageLayout(config?.imageLayout),
      uiColumns: normalizePresetUiColumns(config?.uiColumns, 3)
    };
  }

  if (preset === 'kanban') {
    const normalizedHeaders = Array.isArray(headers) ? headers : [];
    const validHeaderIds = new Set(normalizedHeaders.map((header) => `${header?.id || ''}`.trim()).filter(Boolean));
    const validStatusHeaderIds = new Set(
      normalizedHeaders
        .filter((header) => ['select', 'buttons', 'yesno', 'rog', 'text'].includes(`${header?.type || ''}`.trim()))
        .map((header) => `${header?.id || ''}`.trim())
        .filter(Boolean)
    );
    const statusHeaderId = `${config?.statusHeaderId || ''}`.trim();
    const titleHeaderId = `${config?.titleHeaderId || ''}`.trim();
    const bodyHeaderId = `${config?.bodyHeaderId || ''}`.trim();
    const metaHeaderId = `${config?.metaHeaderId || ''}`.trim();

    return {
      statusHeaderId: validStatusHeaderIds.has(statusHeaderId) ? statusHeaderId : '',
      titleHeaderId: validHeaderIds.has(titleHeaderId) ? titleHeaderId : '',
      bodyHeaderId: validHeaderIds.has(bodyHeaderId) ? bodyHeaderId : '',
      metaHeaderId: validHeaderIds.has(metaHeaderId) ? metaHeaderId : '',
      uiColumns: normalizePresetUiColumns(config?.uiColumns, 3)
    };
  }

  if (preset === 'calendar') {
    const normalizedHeaders = Array.isArray(headers) ? headers : [];
    const validHeaderIds = new Set(normalizedHeaders.map((header) => `${header?.id || ''}`.trim()).filter(Boolean));
    const validDateHeaderIds = new Set(
      normalizedHeaders
        .filter((header) => ['date', 'datetime'].includes(`${header?.type || ''}`.trim()))
        .map((header) => `${header?.id || ''}`.trim())
        .filter(Boolean)
    );
    const imageHeaderId = `${config?.imageHeaderId || ''}`.trim();
    const dateHeaderId = `${config?.dateHeaderId || ''}`.trim();
    const titleHeaderId = `${config?.titleHeaderId || ''}`.trim();
    const metaHeaderId = `${config?.metaHeaderId || ''}`.trim();

    return {
      imageHeaderId: validHeaderIds.has(imageHeaderId) ? imageHeaderId : '',
      dateHeaderId: validDateHeaderIds.has(dateHeaderId) ? dateHeaderId : '',
      titleHeaderId: validHeaderIds.has(titleHeaderId) ? titleHeaderId : '',
      metaHeaderId: validHeaderIds.has(metaHeaderId) ? metaHeaderId : ''
    };
  }

  return {};
};
const sanitizeProfileConfig = (profileConfig, headers = [], currentHeaderId = '') => {
  const validHeaderIds = new Set(
    (Array.isArray(headers) ? headers : [])
      .map((header) => `${header?.id || ''}`.trim())
      .filter((headerId) => headerId && headerId !== currentHeaderId)
  );
  const imageHeaderId = `${profileConfig?.imageHeaderId || ''}`.trim();
  const labelHeaderId = `${profileConfig?.labelHeaderId || ''}`.trim();
  return {
    imageHeaderId: validHeaderIds.has(imageHeaderId) ? imageHeaderId : '',
    labelHeaderId: validHeaderIds.has(labelHeaderId) ? labelHeaderId : ''
  };
};
const getProfileDisplayData = (header, rowValues = {}, headers = []) => {
  const profileConfig = sanitizeProfileConfig(header?.profileConfig, headers, header?.id);
  const imageUrl = `${rowValues?.[profileConfig.imageHeaderId] ?? ''}`.trim();
  const label = `${rowValues?.[profileConfig.labelHeaderId] ?? ''}`.trim();
  return { imageUrl, label };
};
const LOG_SYSTEM_FIELD_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'logScope', label: 'Log scope' },
  { value: 'sourceSheetId', label: 'Source sheet id' },
  { value: 'sourceSheetTitle', label: 'Source sheet title' },
  { value: 'sourceRowId', label: 'Source row id' },
  { value: 'sourceColumnId', label: 'Source column id' },
  { value: 'sourceRowLabel', label: 'Source row label' },
  { value: 'sourceColumnLabel', label: 'Source column label' },
  { value: 'loggedAt', label: 'Logged at' },
  { value: 'loggedBy', label: 'Logged by uid' },
  { value: 'loggedByEmail', label: 'Logged by email' }
];
const REQUIRED_LOG_SYSTEM_HEADERS = [
  { systemField: 'logScope', label: 'Log Scope', type: 'text' },
  { systemField: 'sourceSheetId', label: 'Source Sheet Id', type: 'text' },
  { systemField: 'sourceSheetTitle', label: 'Source Sheet Title', type: 'text' },
  { systemField: 'sourceRowId', label: 'Source Row Id', type: 'text' },
  { systemField: 'sourceColumnId', label: 'Source Column Id', type: 'text' },
  { systemField: 'sourceRowLabel', label: 'Source Row Label', type: 'text' },
  { systemField: 'sourceColumnLabel', label: 'Source Column Label', type: 'text' },
  { systemField: 'loggedAt', label: 'Logged At', type: 'datetime' },
  { systemField: 'loggedBy', label: 'Logged By Uid', type: 'text' },
  { systemField: 'loggedByEmail', label: 'Logged By Email', type: 'email' }
];
const normalizeLogSystemField = (value) => (
  LOG_SYSTEM_FIELD_OPTIONS.some((option) => option.value === `${value || ''}`.trim()) ? `${value || ''}`.trim() : ''
);
const buildManagedLogSystemHeaders = () => REQUIRED_LOG_SYSTEM_HEADERS.map((item) => ({
  id: `system_${item.systemField}`,
  label: item.label,
  type: item.type,
  width: 180,
  columnAccess: 'hidden',
  systemField: item.systemField,
  defaultValueMode: 'none',
  options: [],
  conditionalOptions: [],
  textBold: false,
  textItalic: false,
  textUnderline: false,
  textAlign: 'left',
  renderColor: '',
  summaryType: 'none'
}));
const ACTION_FORM_TYPE_OPTIONS = [
  { value: 'booking', label: 'Booking' },
  { value: 'checkout', label: 'Checkout' },
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'custom', label: 'Custom' }
];
const ACTION_CONFIG_MODE_OPTIONS = [
  { value: 'linked_form', label: 'Add row' },
  { value: 'update_rows', label: 'Update rows' }
];
const ACTION_UPDATE_TARGET_CURRENT_SHEET = '__current_sheet__';
const ACTION_UPDATE_CONDITION_OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Does not equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'isEmpty', label: 'Is empty' },
  { value: 'isNotEmpty', label: 'Is not empty' }
];
const ACTION_UPDATE_VALUE_MODE_OPTIONS = [
  { value: 'static', label: 'Static value' },
  { value: 'sourceHeader', label: 'Current row column' }
];
const normalizeActionFormType = (value) => (
  ACTION_FORM_TYPE_OPTIONS.some((option) => option.value === `${value || ''}`.trim())
    ? `${value || ''}`.trim()
    : 'custom'
);
const normalizeActionConfigMode = (value) => (
  ACTION_CONFIG_MODE_OPTIONS.some((option) => option.value === `${value || ''}`.trim())
    ? `${value || ''}`.trim()
    : 'linked_form'
);
const ACTION_LINKED_FORM_BEHAVIOR_OPTIONS = [
  { value: 'open_form', label: 'Open prefilled form' },
  { value: 'instant_save', label: 'Save row on click' }
];
const normalizeActionLinkedFormBehavior = (value) => (
  ACTION_LINKED_FORM_BEHAVIOR_OPTIONS.some((option) => option.value === `${value || ''}`.trim())
    ? `${value || ''}`.trim()
    : 'open_form'
);
const normalizeActionUpdateConditionOperator = (value) => (
  ACTION_UPDATE_CONDITION_OPERATOR_OPTIONS.some((option) => option.value === `${value || ''}`.trim())
    ? `${value || ''}`.trim()
    : 'equals'
);
const normalizeActionUpdateValueMode = (value) => (
  ACTION_UPDATE_VALUE_MODE_OPTIONS.some((option) => option.value === `${value || ''}`.trim())
    ? `${value || ''}`.trim()
    : 'static'
);
const createActionFormFieldMappingDraft = () => ({
  id: `action_form_mapping_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  valueMode: 'sourceHeader',
  value: '',
  sourceHeaderId: '',
  targetHeaderId: ''
});
const createActionUpdateConditionDraft = () => ({
  id: `action_update_condition_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  targetHeaderId: '',
  operator: 'equals',
  valueMode: 'static',
  value: '',
  sourceHeaderId: ''
});
const createActionUpdateValueDraft = () => ({
  id: `action_update_value_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  targetHeaderId: '',
  valueMode: 'static',
  value: '',
  sourceHeaderId: ''
});
const sanitizeActionFormFieldMappingsForEditor = (fieldMappings = []) => (
  (Array.isArray(fieldMappings) ? fieldMappings : [])
    .map((fieldMapping, index) => ({
      id: `${fieldMapping?.id || `action_form_mapping_${index + 1}`}`.trim() || `action_form_mapping_${index + 1}`,
      valueMode: normalizeActionUpdateValueMode(fieldMapping?.valueMode || (fieldMapping?.sourceHeaderId ? 'sourceHeader' : 'static')),
      value: `${fieldMapping?.value ?? ''}`,
      sourceHeaderId: `${fieldMapping?.sourceHeaderId || ''}`.trim(),
      targetHeaderId: `${fieldMapping?.targetHeaderId || ''}`.trim()
    }))
);
const sanitizeActionUpdateConditionsForEditor = (conditions = []) => (
  (Array.isArray(conditions) ? conditions : [])
    .map((condition, index) => ({
      id: `${condition?.id || `action_update_condition_${index + 1}`}`.trim() || `action_update_condition_${index + 1}`,
      targetHeaderId: `${condition?.targetHeaderId || ''}`.trim(),
      operator: normalizeActionUpdateConditionOperator(condition?.operator),
      valueMode: normalizeActionUpdateValueMode(condition?.valueMode),
      value: `${condition?.value ?? ''}`,
      sourceHeaderId: `${condition?.sourceHeaderId || ''}`.trim()
    }))
);
const sanitizeActionUpdateValuesForEditor = (updates = []) => (
  (Array.isArray(updates) ? updates : [])
    .map((update, index) => ({
      id: `${update?.id || `action_update_value_${index + 1}`}`.trim() || `action_update_value_${index + 1}`,
      targetHeaderId: `${update?.targetHeaderId || ''}`.trim(),
      valueMode: normalizeActionUpdateValueMode(update?.valueMode),
      value: `${update?.value ?? ''}`,
      sourceHeaderId: `${update?.sourceHeaderId || ''}`.trim()
    }))
);
const createActionFormDraft = () => ({
  id: `action_form_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  mode: 'linked_form',
  type: 'booking',
  name: '',
  description: '',
  targetSheetId: '',
  buttonLabel: '',
  submitLabel: '',
  linkedFormBehavior: 'open_form',
  allowTargetRowSelection: false,
  fieldMappings: [],
  conditions: [],
  updates: []
});
const sanitizeActionFormsForEditor = (actionForms = []) => (
  (Array.isArray(actionForms) ? actionForms : [])
    .map((actionForm, index) => ({
      id: `${actionForm?.id || `action_form_${index + 1}`}`.trim() || `action_form_${index + 1}`,
      mode: normalizeActionConfigMode(actionForm?.mode),
      type: normalizeActionFormType(actionForm?.type),
      name: `${actionForm?.name || actionForm?.label || ''}`.trim(),
      description: `${actionForm?.description || ''}`.trim(),
      targetSheetId: `${actionForm?.targetSheetId || ''}`.trim(),
      buttonLabel: `${actionForm?.buttonLabel || ''}`.trim(),
      submitLabel: `${actionForm?.submitLabel || ''}`.trim(),
      linkedFormBehavior: normalizeActionLinkedFormBehavior(actionForm?.linkedFormBehavior),
      allowTargetRowSelection: actionForm?.allowTargetRowSelection === true,
      fieldMappings: sanitizeActionFormFieldMappingsForEditor(actionForm?.fieldMappings),
      conditions: sanitizeActionUpdateConditionsForEditor(actionForm?.conditions),
      updates: sanitizeActionUpdateValuesForEditor(actionForm?.updates)
    }))
);
const sanitizeActionFormsForSave = (actionForms = [], availableSheets = [], currentSheetId = '', currentHeaders = []) => {
  const validSheetIds = new Set(
    (Array.isArray(availableSheets) ? availableSheets : [])
      .map((sheetItem) => `${sheetItem?.id || ''}`.trim())
      .filter((sheetId) => sheetId && sheetId !== currentSheetId)
  );

  return (Array.isArray(actionForms) ? actionForms : [])
    .map((actionForm, index) => {
      const id = `${actionForm?.id || `action_form_${index + 1}`}`.trim();
      const targetSheetId = `${actionForm?.targetSheetId || ''}`.trim();
      const mode = normalizeActionConfigMode(actionForm?.mode);
      const isCurrentSheetTarget = targetSheetId === ACTION_UPDATE_TARGET_CURRENT_SHEET;
      const isValidTargetSheet = mode === 'update_rows'
        ? Boolean(targetSheetId) && (isCurrentSheetTarget || validSheetIds.has(targetSheetId))
        : Boolean(targetSheetId) && validSheetIds.has(targetSheetId);
      if (!id || !isValidTargetSheet) return null;
      const validCurrentHeaderIds = new Set(
        (Array.isArray(currentHeaders) ? currentHeaders : [])
          .map((header) => `${header?.id || ''}`.trim())
          .filter(Boolean)
      );
      const validTargetHeaderIds = new Set(
        (isCurrentSheetTarget
          ? (Array.isArray(currentHeaders) ? currentHeaders : [])
          : ((Array.isArray(availableSheets) ? availableSheets : [])
              .find((sheetItem) => `${sheetItem?.id || ''}`.trim() === targetSheetId)
              ?.headers || [])
        )
          .map((header) => `${header?.id || ''}`.trim())
          .filter(Boolean)
      );
      const fieldMappings = sanitizeActionFormFieldMappingsForEditor(actionForm?.fieldMappings)
        .filter((fieldMapping) => (
          validTargetHeaderIds.has(fieldMapping.targetHeaderId)
          && (
            fieldMapping.valueMode === 'static'
              ? true
              : validCurrentHeaderIds.has(fieldMapping.sourceHeaderId)
          )
        ));
      const conditions = sanitizeActionUpdateConditionsForEditor(actionForm?.conditions)
        .filter((condition) => (
          validTargetHeaderIds.has(condition.targetHeaderId) &&
          (
            condition.valueMode === 'static'
              ? (condition.operator === 'isEmpty' || condition.operator === 'isNotEmpty' || `${condition.value ?? ''}` !== '')
              : validCurrentHeaderIds.has(condition.sourceHeaderId)
          )
        ));
      const updates = sanitizeActionUpdateValuesForEditor(actionForm?.updates)
        .filter((update) => (
          validTargetHeaderIds.has(update.targetHeaderId) &&
          (
            update.valueMode === 'static'
              ? `${update.value ?? ''}` !== ''
              : validCurrentHeaderIds.has(update.sourceHeaderId)
          )
        ));

      return {
        id,
        mode,
        type: normalizeActionFormType(actionForm?.type),
        name: `${actionForm?.name || ''}`.trim(),
        description: `${actionForm?.description || ''}`.trim(),
        targetSheetId,
        buttonLabel: `${actionForm?.buttonLabel || ''}`.trim(),
        submitLabel: `${actionForm?.submitLabel || ''}`.trim(),
        linkedFormBehavior: normalizeActionLinkedFormBehavior(actionForm?.linkedFormBehavior),
        allowTargetRowSelection: normalizeActionLinkedFormBehavior(actionForm?.linkedFormBehavior) === 'open_form' && actionForm?.allowTargetRowSelection === true,
        fieldMappings,
        conditions,
        updates
      };
    })
    .filter(Boolean);
};
const COLUMN_ACCESS_OPTIONS = [
  { value: 'public', label: 'Public' },
  { value: 'restricted', label: 'Restricted' },
  { value: 'private', label: 'Private' },
  { value: 'hidden', label: 'Hidden' }
];
const normalizeColumnAccess = (value) => (
  COLUMN_ACCESS_OPTIONS.some((option) => option.value === `${value || ''}`.trim()) ? `${value || ''}`.trim() : 'public'
);
const normalizeSheetThemeColor = (value) => {
  const normalized = `${value || ''}`.trim();
  if (!normalized) return '';
  const compact = normalized.startsWith('#') ? normalized.slice(1) : normalized;
  return /^(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(compact) ? `#${compact}` : '';
};
const normalizeHeaderRenderColor = (value) => {
  return normalizeSheetThemeColor(value);
};

const blankHeader = () => ({
  id: createHeaderId(),
  label: '',
  displayName: '',
  width: 180,
  type: 'text',
  isRowLabel: false,
  columnAccess: 'public',
  systemField: '',
  textBold: false,
  textItalic: false,
  textUnderline: false,
  textAlign: 'left',
  renderColor: '',
  decimalDigits: 2,
  summaryType: 'none',
  defaultValueMode: 'none',
  defaultValue: '',
  options: [],
  optionsFromHeaderId: '',
  optionSource: { sheetId: '', headerId: '' },
  conditionalOptions: [],
  profileConfig: { imageHeaderId: '', labelHeaderId: '' },
  baseValue: '',
  baseItems: '',
  formula: '',
  formulaConfig: null,
  conditionalFormulaRules: [],
  conditionalDisplayNames: [],
  conditionalLogic: null,
  pendingOption: '',
  rogOverlay: false,
  formInputMode: 'default'
});
const buildManagedActiveActionsHeader = () => ({
  id: ACTIVE_ACTIONS_HEADER_ID,
  label: 'Active Actions',
  displayName: '',
  width: 220,
  type: 'text',
  isRowLabel: false,
  columnAccess: 'public',
  systemField: '',
  textBold: false,
  textItalic: false,
  textUnderline: false,
  textAlign: 'left',
  renderColor: '',
  decimalDigits: 2,
  summaryType: 'none',
  defaultValueMode: 'none',
  defaultValue: '',
  options: [],
  optionsFromHeaderId: '',
  optionSource: { sheetId: '', headerId: '' },
  conditionalOptions: [],
  profileConfig: { imageHeaderId: '', labelHeaderId: '' },
  baseValue: '',
  baseItems: '',
  formula: '',
  formulaConfig: null,
  conditionalFormulaRules: [],
  conditionalDisplayNames: [],
  conditionalLogic: null,
  pendingOption: '',
  rogOverlay: false,
  formInputMode: 'default'
});

const HeaderSection = ({ title, description, children }) => (
  <details className="rounded-2xl border border-slate-200 bg-white [&_summary::-webkit-details-marker]:hidden">
    <summary className="flex cursor-pointer items-start justify-between gap-3 px-4 py-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
        {description ? (
          <p className="mt-1 text-xs text-slate-500">{description}</p>
        ) : null}
      </div>
      <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <ArrowRight className="h-3.5 w-3.5" />
        Toggle
      </span>
    </summary>
    <div className="border-t border-slate-200 p-4">
      {children}
    </div>
  </details>
);

const buildRowValues = (headers, values = {}) =>
  headers.reduce((acc, header) => {
    const currentValue = values?.[header.id];
    acc[header.id] = currentValue === undefined ? '' : currentValue;
    return acc;
  }, {});

const formatDefaultDateTokenValue = (headerType, date = new Date()) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');

  switch (headerType) {
    case 'date':
      return `${year}-${month}-${day}`;
    case 'time':
      return `${hours}:${minutes}`;
    case 'datetime':
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    default:
      return '';
  }
};

const getDynamicDefaultTokenForHeader = (headerType, rawValue) => {
  const normalizedValue = `${rawValue ?? ''}`.trim().toUpperCase();
  if (headerType === 'date' && normalizedValue === 'TODAY()') return 'TODAY()';
  if (headerType === 'time' && normalizedValue === 'TIMENOW()') return 'TIMENOW()';
  if (headerType === 'datetime' && normalizedValue === 'NOW()') return 'NOW()';
  return '';
};

const resolveHeaderDefaultValue = (header) => {
  if (!header || header.defaultValueMode === 'none') {
    return header?.type === 'multiselect' || header?.type === 'list' ? [] : header?.type === 'checkbox' ? false : '';
  }

  if (header.type === 'checkbox') return Boolean(header.defaultValue);
  if (header.type === 'multiselect' || header.type === 'list') return Array.isArray(header.defaultValue) ? header.defaultValue : [];

  const dynamicToken = getDynamicDefaultTokenForHeader(header.type, header.defaultValue);
  if (dynamicToken) return formatDefaultDateTokenValue(header.type);

  return header.defaultValue ?? '';
};

const buildInitialRowValues = (headers = [], values = {}) =>
  headers.reduce((acc, header) => {
    const currentValue = values?.[header.id];
    if (currentValue !== '' && currentValue !== null && currentValue !== undefined) {
      acc[header.id] = currentValue;
      return acc;
    }

    acc[header.id] = resolveHeaderDefaultValue(header);
    return acc;
  }, {});

const sanitizeFilterHeaderIds = (filterHeaderIds = [], headers = []) => {
  const validHeaderIds = new Set((Array.isArray(headers) ? headers : []).map((header) => header.id));
  return Array.from(new Set(
    (Array.isArray(filterHeaderIds) ? filterHeaderIds : [])
      .map((headerId) => `${headerId || ''}`.trim())
      .filter((headerId) => headerId && validHeaderIds.has(headerId))
  ));
};

const COLUMN_MIN_WIDTH = 180;
const RESIZE_HANDLE_WIDTH = 10;
const YES_NO_OPTIONS = ['Yes', 'No'];
const ROG_OPTIONS = ['Red', 'Orange', 'Green'];
const FIXED_HEADER_OPTIONS = {
  yesno: YES_NO_OPTIONS,
  rog: ROG_OPTIONS
};
const SUPPORTED_FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'checkbox',
  'select',
  'buttons',
  'yesno',
  'rog',
  'multiselect',
  'rating',
  'percent',
  'email',
  'telephone',
  'address',
  'hyperlink',
  'youtube',
  'document',
  'image',
  'profile',
  'list',
  'date',
  'time',
  'datetime',
  'currency'
];

const normalizeListItems = (rawValue) => (
  Array.from(new Set(
    (Array.isArray(rawValue) ? rawValue : `${rawValue || ''}`.split(','))
      .map((item) => `${item || ''}`.trim())
      .filter(Boolean)
  ))
);

const parsePendingOptionsInput = (rawValue) => (
  Array.from(new Set(
    `${rawValue || ''}`
      .split(/[\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean)
  ))
);

const getMapSearchUrl = (value) => {
  const query = `${value || ''}`.trim();
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : '';
};

const parseEmailList = (raw = '') => raw
  .split(',')
  .map((item) => item.trim().toLowerCase())
  .filter(Boolean)
  .filter((item, index, all) => all.indexOf(item) === index)
  .filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));

const NUMERIC_HEADER_TYPES = new Set(['number', 'rating', 'currency', 'percent']);
const DECIMAL_FIELD_TYPES = new Set(['number', 'currency', 'percent']);
const SUMMARY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'sum', label: 'Sum' },
  { value: 'average', label: 'Average' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'count', label: 'Count' }
];
const DEFAULT_VALUE_MODE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'editable', label: 'Editable default' },
  { value: 'fixed', label: 'Fixed value' }
];
const getAllowedSummaryTypes = (headerType) => {
  if (NUMERIC_HEADER_TYPES.has(headerType)) return SUMMARY_OPTIONS;
  return SUMMARY_OPTIONS.filter((option) => option.value === 'none' || option.value === 'count');
};
const createChartId = () => `legacy_chart_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const REPORT_SCOPE_OPTIONS = [
  { value: 'active-row', label: 'Active row report' },
  { value: 'sheet-overview', label: 'Sheet overview report' }
];
const REPORT_TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'executive', label: 'Executive' },
  { value: 'analytical', label: 'Analytical' },
  { value: 'operational', label: 'Operational' }
];
const REPORT_FORMAT_OPTIONS = [
  { value: 'markdown', label: 'Markdown' },
  { value: 'summary', label: 'Plain summary' }
];
const createDefaultReportSkill = () => ({
  enabled: false,
  title: '',
  objective: '',
  scope: 'active-row',
  tone: 'professional',
  format: 'markdown',
  focusHeaderIds: [],
  includeVisibleRows: true,
  includeRelatedLogs: true,
  includeSheetLogs: false,
  maxVisibleRows: 15,
  maxLogs: 12,
  instructions: ''
});
const AUTOMATION_TYPE_OPTIONS = [
  { value: 'report', label: 'Report' },
  { value: 'email_digest', label: 'Email digest' }
];
const AUTOMATION_TRIGGER_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'schedule', label: 'Schedule' }
];
const AUTOMATION_SCHEDULE_CADENCE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' }
];
const AUTOMATION_WEEKDAY_OPTIONS = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' }
];
const createAutomationId = () => `automation_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const createDefaultAutomation = (type = 'report') => ({
  id: createAutomationId(),
  enabled: true,
  name: '',
  type,
  triggerType: 'manual',
  scheduleCadence: 'daily',
  scheduleDay: 'fri',
  scheduleTime: '07:00',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Africa/Johannesburg',
  scope: type === 'email_digest' ? 'sheet-overview' : 'active-row',
  tone: 'professional',
  format: 'markdown',
  focusHeaderIds: [],
  includeVisibleRows: true,
  includeRelatedLogs: true,
  includeSheetLogs: type === 'email_digest',
  maxVisibleRows: type === 'email_digest' ? 25 : 15,
  maxLogs: 12,
  instructions: '',
  recipientsText: ''
});

const normalizeReportSkillForEditor = (reportSkill, headers = []) => {
  const validHeaderIds = new Set((Array.isArray(headers) ? headers : []).map((header) => `${header?.id || ''}`.trim()).filter(Boolean));
  const nextSkill = createDefaultReportSkill();
  const scope = `${reportSkill?.scope || ''}`.trim();
  const tone = `${reportSkill?.tone || ''}`.trim();
  const format = `${reportSkill?.format || ''}`.trim();

  return {
    enabled: Boolean(reportSkill?.enabled),
    title: `${reportSkill?.title || ''}`,
    objective: `${reportSkill?.objective || ''}`,
    scope: REPORT_SCOPE_OPTIONS.some((option) => option.value === scope) ? scope : nextSkill.scope,
    tone: REPORT_TONE_OPTIONS.some((option) => option.value === tone) ? tone : nextSkill.tone,
    format: REPORT_FORMAT_OPTIONS.some((option) => option.value === format) ? format : nextSkill.format,
    focusHeaderIds: Array.from(new Set(
      (Array.isArray(reportSkill?.focusHeaderIds) ? reportSkill.focusHeaderIds : [])
        .map((headerId) => `${headerId || ''}`.trim())
        .filter((headerId) => validHeaderIds.has(headerId))
    )),
    includeVisibleRows: reportSkill?.includeVisibleRows !== false,
    includeRelatedLogs: reportSkill?.includeRelatedLogs !== false,
    includeSheetLogs: Boolean(reportSkill?.includeSheetLogs),
    maxVisibleRows: Math.max(1, Math.min(50, Number(reportSkill?.maxVisibleRows) || nextSkill.maxVisibleRows)),
    maxLogs: Math.max(1, Math.min(50, Number(reportSkill?.maxLogs) || nextSkill.maxLogs)),
    instructions: `${reportSkill?.instructions || ''}`
  };
};

const normalizeAutomationsForEditor = (automations = [], headers = []) => {
  const validHeaderIds = new Set((Array.isArray(headers) ? headers : []).map((header) => `${header?.id || ''}`.trim()).filter(Boolean));
  return (Array.isArray(automations) ? automations : [])
    .filter((automation) => automation && typeof automation === 'object')
    .map((automation) => {
      const base = createDefaultAutomation(`${automation?.type || ''}`.trim() === 'email_digest' ? 'email_digest' : 'report');
      const type = AUTOMATION_TYPE_OPTIONS.some((option) => option.value === `${automation?.type || ''}`.trim())
        ? `${automation.type}`.trim()
        : base.type;
      const triggerType = AUTOMATION_TRIGGER_OPTIONS.some((option) => option.value === `${automation?.triggerType || ''}`.trim())
        ? `${automation.triggerType}`.trim()
        : base.triggerType;
      const scheduleCadence = AUTOMATION_SCHEDULE_CADENCE_OPTIONS.some((option) => option.value === `${automation?.scheduleCadence || ''}`.trim())
        ? `${automation.scheduleCadence}`.trim()
        : base.scheduleCadence;
      const scheduleDay = AUTOMATION_WEEKDAY_OPTIONS.some((option) => option.value === `${automation?.scheduleDay || ''}`.trim())
        ? `${automation.scheduleDay}`.trim()
        : base.scheduleDay;

      return {
        ...base,
        id: `${automation?.id || ''}`.trim() || base.id,
        enabled: automation?.enabled !== false,
        name: `${automation?.name || ''}`,
        type,
        triggerType,
        scheduleCadence,
        scheduleDay,
        scheduleTime: `${automation?.scheduleTime || ''}`.trim() || base.scheduleTime,
        timezone: `${automation?.timezone || ''}`.trim() || base.timezone,
        scope: REPORT_SCOPE_OPTIONS.some((option) => option.value === `${automation?.scope || ''}`.trim()) ? `${automation.scope}`.trim() : base.scope,
        tone: REPORT_TONE_OPTIONS.some((option) => option.value === `${automation?.tone || ''}`.trim()) ? `${automation.tone}`.trim() : base.tone,
        format: REPORT_FORMAT_OPTIONS.some((option) => option.value === `${automation?.format || ''}`.trim()) ? `${automation.format}`.trim() : base.format,
        focusHeaderIds: Array.from(new Set(
          (Array.isArray(automation?.focusHeaderIds) ? automation.focusHeaderIds : [])
            .map((headerId) => `${headerId || ''}`.trim())
            .filter((headerId) => validHeaderIds.has(headerId))
        )),
        includeVisibleRows: automation?.includeVisibleRows !== false,
        includeRelatedLogs: automation?.includeRelatedLogs !== false,
        includeSheetLogs: Boolean(automation?.includeSheetLogs),
        maxVisibleRows: Math.max(1, Math.min(50, Number(automation?.maxVisibleRows) || base.maxVisibleRows)),
        maxLogs: Math.max(1, Math.min(50, Number(automation?.maxLogs) || base.maxLogs)),
        instructions: `${automation?.instructions || ''}`,
        recipientsText: Array.isArray(automation?.recipients)
          ? automation.recipients.join(', ')
          : `${automation?.recipientsText || ''}`
      };
    });
};

const sanitizeAutomationsForSave = (automations = [], headers = []) => normalizeAutomationsForEditor(automations, headers)
  .map((automation) => ({
    id: automation.id,
    enabled: Boolean(automation.enabled),
    name: automation.name.trim(),
    type: automation.type,
    triggerType: automation.triggerType,
    scheduleCadence: automation.scheduleCadence,
    scheduleDay: automation.scheduleDay,
    scheduleTime: automation.scheduleTime,
    timezone: automation.timezone.trim(),
    scope: automation.scope,
    tone: automation.tone,
    format: automation.format,
    focusHeaderIds: automation.focusHeaderIds,
    includeVisibleRows: Boolean(automation.includeVisibleRows),
    includeRelatedLogs: Boolean(automation.includeRelatedLogs),
    includeSheetLogs: Boolean(automation.includeSheetLogs),
    maxVisibleRows: Math.max(1, Math.min(50, Number(automation.maxVisibleRows) || 15)),
    maxLogs: Math.max(1, Math.min(50, Number(automation.maxLogs) || 12)),
    instructions: automation.instructions.trim(),
    recipients: parseEmailList(automation.recipientsText)
  }))
  .filter((automation) => automation.name);

const sanitizeReportSkillForSave = (reportSkill, headers = []) => {
  const normalized = normalizeReportSkillForEditor(reportSkill, headers);
  return {
    enabled: Boolean(normalized.enabled),
    title: normalized.title.trim(),
    objective: normalized.objective.trim(),
    scope: normalized.scope,
    tone: normalized.tone,
    format: normalized.format,
    focusHeaderIds: normalized.focusHeaderIds,
    includeVisibleRows: Boolean(normalized.includeVisibleRows),
    includeRelatedLogs: Boolean(normalized.includeRelatedLogs),
    includeSheetLogs: Boolean(normalized.includeSheetLogs),
    maxVisibleRows: Math.max(1, Math.min(50, Number(normalized.maxVisibleRows) || 15)),
    maxLogs: Math.max(1, Math.min(50, Number(normalized.maxLogs) || 12)),
    instructions: normalized.instructions.trim()
  };
};

const normalizeDecimalDigits = (value, fallback = 2) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.min(6, Math.round(numeric)));
};

const normalizeHeaderTextAlign = (value) => (
  ['left', 'center', 'right'].includes(`${value || ''}`.trim()) ? `${value}`.trim() : 'left'
);

const getColumnTextStyle = (header) => ({
  fontWeight: header?.textBold ? 700 : 400,
  fontStyle: header?.textItalic ? 'italic' : 'normal',
  textDecoration: header?.textUnderline ? 'underline' : 'none',
  textAlign: normalizeHeaderTextAlign(header?.textAlign),
  color: normalizeHeaderRenderColor(header?.renderColor) || undefined
});

const getColumnAlignmentClass = (alignment) => (
  alignment === 'center' ? 'text-center' : alignment === 'right' ? 'text-right' : 'text-left'
);

const formatDisplayValue = (header, value) => {
  if (value === '' || value === null || value === undefined) return '';
  if (!DECIMAL_FIELD_TYPES.has(header?.type)) {
    if (header?.type === 'percent') return `${value}%`;
    return `${value}`;
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return `${value}`;
  const digits = normalizeDecimalDigits(header?.decimalDigits, 2);
  const formatted = numeric.toFixed(digits);
  return header?.type === 'percent' ? `${formatted}%` : formatted;
};

const getFixedOptionsForHeaderType = (headerType) => FIXED_HEADER_OPTIONS[`${headerType || ''}`.trim()] || [];

const getHeaderOptionList = (header, optionSourceRowsBySheetId = {}, rowValues = {}, headers = []) => {
  const fixedOptions = getFixedOptionsForHeaderType(header?.type);
  if (fixedOptions.length) return fixedOptions;
  return resolveHeaderOptions(header, optionSourceRowsBySheetId, rowValues, headers);
};

const isOptionDrivenHeaderType = (headerType) => (
  ['select', 'buttons', 'yesno', 'rog', 'multiselect'].includes(`${headerType || ''}`.trim())
);

const getRogTone = (value) => {
  switch (`${value || ''}`.trim().toLowerCase()) {
    case 'red':
      return {
        solid: 'border-rose-500 bg-rose-500 text-white',
        soft: 'border-rose-200 bg-rose-50 text-rose-700',
        overlay: 'bg-rose-50/80'
      };
    case 'orange':
      return {
        solid: 'border-orange-500 bg-orange-500 text-white',
        soft: 'border-orange-200 bg-orange-50 text-orange-700',
        overlay: 'bg-orange-50/80'
      };
    case 'green':
      return {
        solid: 'border-emerald-500 bg-emerald-500 text-white',
        soft: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        overlay: 'bg-emerald-50/80'
      };
    default:
      return null;
  }
};

const getChoiceButtonClasses = ({ headerType, option, selectedValue, disabled = false }) => {
  const isSelected = `${selectedValue ?? ''}` === option;
  if (`${headerType || ''}` === 'rog') {
    const tone = getRogTone(option);
    return `rounded-xl border px-3 py-2 text-xs font-semibold transition ${
      disabled
        ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
        : isSelected
          ? tone?.solid || 'border-[#008080] bg-[#008080]/10 text-[#008080]'
          : tone?.soft || 'border-slate-200 bg-white text-slate-700 hover:border-[#008080]/40 hover:text-[#008080]'
    }`;
  }
  return `rounded-xl border px-3 py-2 text-xs font-semibold transition ${
    disabled
      ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
      : isSelected
        ? 'border-[#008080] bg-[#008080]/10 text-[#008080]'
        : 'border-slate-200 bg-white text-slate-700 hover:border-[#008080]/40 hover:text-[#008080]'
  }`;
};

const getRogOverlayClass = (header, value) => (
  header?.type === 'rog' && header?.rogOverlay ? (getRogTone(value)?.overlay || '') : ''
);

const normalizeHeaderDefaultValue = (header, rawValue) => {
  if (header?.defaultValueMode === 'none') return '';
  if (header?.type === 'checkbox') return Boolean(rawValue);
  if (header?.type === 'multiselect' || header?.type === 'list') return normalizeListItems(rawValue);
  const dynamicToken = getDynamicDefaultTokenForHeader(header?.type, rawValue);
  if (dynamicToken) return dynamicToken;
  if (NUMERIC_HEADER_TYPES.has(header?.type)) {
    if (rawValue === '' || rawValue === null || rawValue === undefined) return '';
    const numeric = Number(rawValue);
    return Number.isFinite(numeric) ? numeric : '';
  }
  return `${rawValue ?? ''}`;
};

const getFormulaConfigShape = (type) => (
  type === 'concat'
    ? { type, operands: [], separator: ' ' }
    : type === 'lookup'
      ? {
          type,
          lookupSheetId: '',
          lookupMatchSourceHeaderId: '',
          lookupMatchCurrentHeaderId: '',
          lookupReturnHeaderId: ''
        }
    : type === 'filter_list'
      ? {
          type,
          filterListSheetId: '',
          filterListReturnHeaderId: '',
          filterListFilters: []
        }
    : type === 'percentage'
      ? { type, numeratorType: 'single', numeratorOperands: [], denominator: '' }
      : type === 'duration_days'
        ? { type, operands: [], weekendMode: 'include' }
        : { type, operands: [] }
);

const DURATION_FORMULA_TYPES = new Set(['duration_days', 'duration_day_hours', 'duration_hours']);
const DATE_PART_FORMULA_TYPES = new Set(['day', 'week', 'month']);
const getDurationOperandTypes = (formulaType) => {
  if (formulaType === 'duration_days') return new Set(['date']);
  if (formulaType === 'duration_day_hours') return new Set(['datetime']);
  if (formulaType === 'duration_hours') return new Set(['time']);
  return new Set();
};
const getDatePartOperandTypes = (formulaType) => (
  DATE_PART_FORMULA_TYPES.has(formulaType) ? new Set(['date', 'datetime']) : new Set()
);

const getFormulaOperandList = (config = {}) => (
  config?.type === 'percentage'
    ? config?.numeratorOperands || []
    : config?.operands || []
);

const getPercentageOperandLimit = (numeratorType) => (numeratorType === 'single' ? 1 : null);
const getFormulaOperandLimit = (config = {}) => {
  if (config?.type === 'percentage') return getPercentageOperandLimit(config?.numeratorType);
  if (DURATION_FORMULA_TYPES.has(config?.type)) return 2;
  if (DATE_PART_FORMULA_TYPES.has(config?.type)) return 1;
  return null;
};

const normalizeConditionalFormulaRuleForEditor = (rule, headers = []) => ({
  headerId: `${rule?.headerId || rule?.fieldId || ''}`.trim(),
  operator: `${rule?.operator || 'equals'}`.trim() || 'equals',
  value: `${rule?.value ?? ''}`,
  formula: `${rule?.formula || ''}`.trim(),
  formulaConfig: parseFormulaToConfig(rule?.formula || '', headers) || null
});

const buildConditionalFormulaRulesForSave = (header, headers = []) => (
  sanitizeHeaderConditionalFormulaRules(header?.conditionalFormulaRules, headers, header?.id)
    .map((rule) => {
      const matchingEditorRule = (header?.conditionalFormulaRules || []).find((candidate) => (
        `${candidate?.headerId || candidate?.fieldId || ''}`.trim() === rule.headerId &&
        `${candidate?.operator || 'equals'}`.trim() === rule.operator &&
        `${candidate?.value ?? ''}` === rule.value
      ));
      const formula = buildFormulaFromConfig(matchingEditorRule?.formulaConfig, headers) || rule.formula;
      return { ...rule, formula };
    })
    .filter((rule) => `${rule.formula || ''}`.trim())
);

const getFormulaOperandLabel = (operandId, headers) => {
  const isBaseOperand = `${operandId || ''}`.startsWith('base:');
  const resolvedHeaderId = isBaseOperand ? `${operandId}`.slice(5) : operandId;
  const label = headers.find((header) => header.id === resolvedHeaderId)?.label || 'Column';
  return isBaseOperand ? `${label} base` : label;
};

const getLookupFormulaSummary = (config = {}, headers = [], availableHeadersBySheetId = {}, availableSheets = []) => {
  const sheetId = `${config?.lookupSheetId || ''}`.trim();
  const sourceHeaders = availableHeadersBySheetId?.[sheetId] || [];
  const sourceSheet = availableSheets.find((sheetItem) => sheetItem.id === sheetId);
  const matchSourceLabel = sourceHeaders.find((header) => header.id === config?.lookupMatchSourceHeaderId)?.label || 'Source column';
  const matchCurrentLabel = headers.find((header) => header.id === config?.lookupMatchCurrentHeaderId)?.label || 'Current row column';
  const returnLabel = sourceHeaders.find((header) => header.id === config?.lookupReturnHeaderId)?.label || 'Returned column';

  return [
    { kind: 'function', label: 'Lookup' },
    { kind: 'column', label: sourceSheet?.title || 'Table' },
    { kind: 'operator', label: `${matchSourceLabel} = ${matchCurrentLabel}` },
    { kind: 'column', label: returnLabel }
  ];
};

const getFilterListFormulaSummary = (config = {}, headers = [], availableHeadersBySheetId = {}, availableSheets = []) => {
  const sheetId = `${config?.filterListSheetId || ''}`.trim();
  const sourceHeaders = availableHeadersBySheetId?.[sheetId] || [];
  const sourceSheet = availableSheets.find((sheetItem) => sheetItem.id === sheetId);
  const returnLabel = sourceHeaders.find((header) => header.id === config?.filterListReturnHeaderId)?.label || 'Returned column';
  const filterTokens = (Array.isArray(config?.filterListFilters) ? config.filterListFilters : [])
    .map((filter) => {
      const sourceLabel = sourceHeaders.find((header) => header.id === filter?.sourceHeaderId)?.label || 'Source column';
      const currentLabel = headers.find((header) => header.id === filter?.currentHeaderId)?.label || 'Current row column';
      return { kind: 'operator', label: `${sourceLabel} ${getFilterListOperatorLabel(filter?.operator).toLowerCase()} ${currentLabel}` };
    });

  return [
    { kind: 'function', label: 'Filter List' },
    { kind: 'column', label: sourceSheet?.title || 'Table' },
    { kind: 'column', label: returnLabel },
    ...filterTokens
  ];
};

const getFormulaPreviewTokens = (config, headers) => {
  if (!config?.type) return [];
  const option = VISUAL_FORMULA_OPTIONS.find((item) => item.value === config.type);
  const operandLabels = getFormulaOperandList(config).map((operandId) => getFormulaOperandLabel(operandId, headers));

  if (!operandLabels.length) {
    return [{ kind: 'function', label: option?.label || 'Formula' }];
  }

  if (config.type === 'concat') {
    const separator = config.separator ?? ' ';
    return operandLabels.flatMap((label, index) => (
      index === 0
        ? [{ kind: 'column', label }]
        : [{ kind: 'separator', label: separator || 'No separator' }, { kind: 'column', label }]
    ));
  }

  if (config.type === 'sum') {
    return operandLabels.flatMap((label, index) => (
      index === 0 ? [{ kind: 'column', label }] : [{ kind: 'operator', label: '+' }, { kind: 'column', label }]
    ));
  }

  const operatorMap = {
    subtract: '-',
    multiply: 'x',
    divide: '/',
    average: 'AVG',
    min: 'MIN',
    max: 'MAX'
  };

  if (config.type === 'percentage') {
    const numeratorType = config.numeratorType || 'single';
    const denominatorLabel = config.denominator ? getFormulaOperandLabel(config.denominator, headers) : 'Column';
    const numeratorTokens = numeratorType === 'single'
      ? operandLabels.slice(0, 1).map((label) => ({ kind: 'column', label }))
      : getFormulaPreviewTokens({ type: numeratorType, operands: config.numeratorOperands || [] }, headers);

    return [
      ...numeratorTokens,
      { kind: 'operator', label: 'of' },
      { kind: 'column', label: denominatorLabel },
      { kind: 'operator', label: '%' }
    ];
  }

  if (config.type === 'lookup') {
    return [{ kind: 'function', label: 'Lookup from table' }];
  }

  if (DURATION_FORMULA_TYPES.has(config.type)) {
    const unitLabel = config.type === 'duration_days'
      ? 'days'
      : config.type === 'duration_day_hours'
        ? 'days + hours'
        : 'hours';
    const baseTokens = operandLabels.flatMap((label, index) => (
      index === 0
        ? [{ kind: 'column', label }]
        : [{ kind: 'operator', label: '-' }, { kind: 'column', label }]
    )).concat([{ kind: 'operator', label: unitLabel }]);
    if (config.type === 'duration_days') {
      return baseTokens.concat([{
        kind: 'operator',
        label: config.weekendMode === 'exclude' ? 'weekdays only' : 'include weekends'
      }]);
    }
    return baseTokens;
  }

  if (DATE_PART_FORMULA_TYPES.has(config.type)) {
    const partLabel = config.type === 'day' ? 'weekday' : config.type === 'week' ? 'week number' : 'month';
    return [
      { kind: 'column', label: operandLabels[0] || 'Column' },
      { kind: 'operator', label: partLabel }
    ];
  }

  if (config.type === 'subtract' || config.type === 'multiply' || config.type === 'divide') {
    return operandLabels.flatMap((label, index) => (
      index === 0
        ? [{ kind: 'column', label }]
        : [{ kind: 'operator', label: operatorMap[config.type] }, { kind: 'column', label }]
    ));
  }

  return [
    { kind: 'function', label: option?.label || 'Formula' },
    ...operandLabels.map((label) => ({ kind: 'column', label }))
  ];
};

const getFormulaOperandChoices = (targetHeader, headers, formulaConfigOverride = null) => {
  const formulaConfig = formulaConfigOverride || targetHeader?.formulaConfig || null;
  const isConcat = formulaConfig?.type === 'concat';
  const isListFormula = formulaConfig?.type === 'subtract_list';
  const allowedDurationTypes = getDurationOperandTypes(formulaConfig?.type);
  const allowedDatePartTypes = getDatePartOperandTypes(formulaConfig?.type);

  return headers
    .filter((candidate) => candidate.id !== targetHeader.id)
    .flatMap((candidate) => {
      if (allowedDurationTypes.size > 0) {
        return allowedDurationTypes.has(candidate.type)
          ? [{
              value: candidate.id,
              label: candidate.label || getColumnLetter(headers.findIndex((item) => item.id === candidate.id))
            }]
          : [];
      }

      if (allowedDatePartTypes.size > 0) {
        return allowedDatePartTypes.has(candidate.type)
          ? [{
              value: candidate.id,
              label: candidate.label || getColumnLetter(headers.findIndex((item) => item.id === candidate.id))
            }]
          : [];
      }

      if (isConcat) {
        return [{
          value: candidate.id,
          label: candidate.label || getColumnLetter(headers.findIndex((item) => item.id === candidate.id))
        }];
      }

      if (isListFormula) {
        return candidate.type === 'list' || candidate.type === 'multiselect'
          ? [{
              value: candidate.id,
              label: candidate.label || getColumnLetter(headers.findIndex((item) => item.id === candidate.id))
            }]
          : [];
      }

      if (!NUMERIC_HEADER_TYPES.has(candidate.type)) return [];

      const choices = [{
        value: candidate.id,
        label: `${candidate.label || 'Column'} value`
      }];

      if (hasHeaderBaseValue(candidate)) {
        choices.push({
          value: `base:${candidate.id}`,
          label: `${candidate.label || 'Column'} base`
        });
      }

      return choices;
    });
};

const SheetBuilder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id, workspaceSlug } = useParams();
  const { user, userProfile } = useAuth();
  const isEditing = Boolean(id);
  const isPlatformAdmin = `${userProfile?.role || ''}`.trim() === 'admin';

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [organizationIds, setOrganizationIds] = useState([]);
  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [adminBuilderAccessEnabled, setAdminBuilderAccessEnabled] = useState(false);
  const [sheetOwnerId, setSheetOwnerId] = useState('');
  const [allowedEmailsInput, setAllowedEmailsInput] = useState('');
  const [resolvedSharedUsers, setResolvedSharedUsers] = useState([]);
  const [selectedSharedUserIds, setSelectedSharedUserIds] = useState([]);
  const [collaboratorAccess, setCollaboratorAccess] = useState('view');
  const [isLogSheet, setIsLogSheet] = useState(false);
  const [customLogSheetId, setCustomLogSheetId] = useState('');
  const [actionForms, setActionForms] = useState([]);
  const [sheetRenderPreset, setSheetRenderPreset] = useState('default');
  const [sheetPrimaryColor, setSheetPrimaryColor] = useState('');
  const [sheetSecondaryColor, setSheetSecondaryColor] = useState('');
  const [sheetRenderFont, setSheetRenderFont] = useState('system');
  const [sheetOgImageUrl, setSheetOgImageUrl] = useState('');
  const [uploadingOgImage, setUploadingOgImage] = useState(false);
  const [sheetRenderPresetConfig, setSheetRenderPresetConfig] = useState({
    profileHeaderId: '',
    imageHeaderId: '',
    categoryHeaderId: '',
    titleHeaderId: '',
    bodyHeaderId: '',
    actionHeaderId: '',
    tagHeaderId: '',
    imageLayout: 'inline',
    statusHeaderId: '',
    metaHeaderId: '',
    dateHeaderId: '',
    uiColumns: 3
  });
  const [headers, setHeaders] = useState([blankHeader()]);
  const [filterHeaderIds, setFilterHeaderIds] = useState([]);
  const [firstRowValues, setFirstRowValues] = useState({});
  const [activeResize, setActiveResize] = useState(null);
  const [openBaseInfoHeaderId, setOpenBaseInfoHeaderId] = useState(null);
  const [headerEditorModes, setHeaderEditorModes] = useState({});
  const [activeBuilderPanel, setActiveBuilderPanel] = useState('setup');
  const [availableSheets, setAvailableSheets] = useState([]);
  const [adminAccessibleHeaderSheets, setAdminAccessibleHeaderSheets] = useState([]);
  const [availableWorkspaces, setAvailableWorkspaces] = useState([]);
  const [workspaceId, setWorkspaceId] = useState('');
  const [workspaceTitle, setWorkspaceTitle] = useState('');
  const [workspaceOnly, setWorkspaceOnly] = useState(false);
  const [newWorkspaceTitle, setNewWorkspaceTitle] = useState('');
  const [newWorkspaceDescription, setNewWorkspaceDescription] = useState('');
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [optionSourceRowsBySheetId, setOptionSourceRowsBySheetId] = useState({});
  const [headerDefaultListDrafts, setHeaderDefaultListDrafts] = useState({});
  const [previewListDrafts, setPreviewListDrafts] = useState({});
  const [analytics, setAnalytics] = useState([]);
  const charts = [];
  const setCharts = () => {};
  const [reportSkill, setReportSkill] = useState(createDefaultReportSkill);
  const [automations, setAutomations] = useState([]);
  const [sheetIdeaPrompt, setSheetIdeaPrompt] = useState('');
  const [selectedReferenceSheetIds, setSelectedReferenceSheetIds] = useState([]);
  const [generatingBlueprint, setGeneratingBlueprint] = useState(false);
  const [blueprintSummary, setBlueprintSummary] = useState('');
  const [showAiSheetAssistant, setShowAiSheetAssistant] = useState(false);
  const [sheetFormConfig, setSheetFormConfig] = useState(createDefaultSheetFormConfig);
  const [hadExistingSheetForm, setHadExistingSheetForm] = useState(false);
  const [pendingFormHeaderId, setPendingFormHeaderId] = useState('');
  const [pendingRepeatableFormHeaderId, setPendingRepeatableFormHeaderId] = useState('');
  const [uploadingFormLayoutItemId, setUploadingFormLayoutItemId] = useState('');
  const formAssetScopeRef = useRef(`draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    let active = true;
    organizationService.getOrganizationsByIds(userProfile?.approvedOrganizationIds || [])
      .then((items) => { if (active) setAvailableOrganizations(items); })
      .catch(() => { if (active) setAvailableOrganizations([]); });
    return () => { active = false; };
  }, [userProfile?.approvedOrganizationIds]);

  const handleReturnNavigation = useCallback(() => {
    const explicitReturnTo = `${location.state?.returnTo || ''}`.trim();
    if (explicitReturnTo) {
      navigate(explicitReturnTo, location.state?.returnToState ? { state: location.state.returnToState } : undefined);
      return;
    }

    const routeWorkspaceSlug = `${workspaceSlug || ''}`.trim();
    if (routeWorkspaceSlug) {
      navigate(getWorkspacePath(routeWorkspaceSlug));
      return;
    }

    navigate('/sheets');
  }, [location.state, navigate, workspaceSlug]);

  const handleSheetOgImageUpload = useCallback(async (file) => {
    if (!file) return;
    if (!user?.uid) {
      toast.error('Sign in to upload a share image.');
      return;
    }

    const isImage = `${file.type || ''}`.startsWith('image/');
    if (!isImage) {
      toast.error('Choose an image file for the share image.');
      return;
    }

    try {
      setUploadingOgImage(true);
      const targetSheetId = `${id || 'draft'}`.trim() || 'draft';
      const safeName = `${Date.now()}_${file.name || 'og-image'}`.replace(/[^\w.-]+/g, '_');
      const imageRef = ref(storage, `sheets/${targetSheetId}/${user.uid}/meta/og/${safeName}`);
      const snapshot = await uploadBytes(imageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      setSheetOgImageUrl(url);
      toast.success('Share image uploaded.');
    } catch (error) {
      console.error('OG image upload failed', error);
      toast.error(error?.message || 'Failed to upload share image.');
    } finally {
      setUploadingOgImage(false);
    }
  }, [id, user?.uid]);

  useEffect(() => {
    if (!isEditing) return;

    const loadSheet = async () => {
      setLoading(true);
      try {
        const sheet = await sheetService.getSheetById(id);
        const existingSheetForm = await sheetFormService.getSheetFormById(id).catch(() => null);
        setTitle(sheet.title || '');
        setDescription(sheet.description || '');
        setVisibility(sheet.visibility || 'private');
        setOrganizationIds(Array.isArray(sheet.organizationIds) ? sheet.organizationIds : []);
        setAdminBuilderAccessEnabled(sheet.adminBuilderAccessEnabled === true);
        setSheetOwnerId(`${sheet.userId || ''}`.trim());
        setAllowedEmailsInput(Array.isArray(sheet.allowedEmails) ? sheet.allowedEmails.join(', ') : '');
        setResolvedSharedUsers(Array.isArray(sheet.sharedUsers) ? sheet.sharedUsers : []);
        setSelectedSharedUserIds(Array.isArray(sheet.allowedUserIds) ? sheet.allowedUserIds : []);
        setCollaboratorAccess(sheet.collaboratorAccess || 'view');
        setIsLogSheet(Boolean(sheet.isLogSheet));
        setCustomLogSheetId(`${sheet.customLogSheetId || ''}`.trim());
        setActionForms(sanitizeActionFormsForEditor(sheet.actionForms || []));
        setSheetRenderPreset(normalizeSheetRenderPreset(sheet.renderPreset));
        setSheetPrimaryColor(normalizeSheetThemeColor(sheet.primaryColor));
        setSheetSecondaryColor(normalizeSheetThemeColor(sheet.secondaryColor));
        setSheetRenderFont(normalizeSheetRenderFont(sheet.renderFont));
        setSheetOgImageUrl(`${sheet.ogImageUrl || ''}`.trim());
        setWorkspaceId(`${sheet.workspaceId || ''}`.trim());
        setWorkspaceTitle(`${sheet.workspaceTitle || ''}`.trim());
        setWorkspaceOnly(Boolean(sheet.workspaceOnly) || Boolean(`${sheet.workspaceId || ''}`.trim()));
        const nextHeaders = Array.isArray(sheet.headers) && sheet.headers.length
          ? sheet.headers
            .filter((header) => !Boolean(sheet.isLogSheet && normalizeLogSystemField(header.systemField)))
            .map((header) => ({
              ...header,
              type: SUPPORTED_FIELD_TYPES.includes(normalizeLegacyHeaderType(header.type)) ? normalizeLegacyHeaderType(header.type) : 'text',
              displayName: `${header.displayName || header.displayLabel || ''}`,
              options: Array.isArray(header.options) ? header.options : [],
              optionSource: getHeaderOptionSourceDraft(header.optionSource),
              conditionalOptions: (Array.isArray(header.conditionalOptions) ? header.conditionalOptions : [])
                .map((rule) => normalizeConditionalOptionRuleForEditor(rule)),
              profileConfig: getProfileConfigDraft(header.profileConfig),
              isRowLabel: Boolean(header.isRowLabel),
              columnAccess: normalizeColumnAccess(header.columnAccess),
              systemField: normalizeLogSystemField(header.systemField),
              decimalDigits: normalizeDecimalDigits(header.decimalDigits, 2),
              summaryType: getAllowedSummaryTypes(header.type).some((option) => option.value === header.summaryType) ? header.summaryType : 'none',
              defaultValueMode: DEFAULT_VALUE_MODE_OPTIONS.some((option) => option.value === header.defaultValueMode) ? header.defaultValueMode : 'none',
              defaultValue: header.type === 'multiselect'
                ? Array.isArray(header.defaultValue) ? header.defaultValue : []
                : header.type === 'list'
                  ? normalizeListItems(header.defaultValue)
                  : header.type === 'checkbox'
                    ? Boolean(header.defaultValue)
                    : header.defaultValue ?? '',
              optionsFromHeaderId: `${header.optionsFromHeaderId || ''}`.trim(),
              baseValue: header.baseValue ?? '',
              baseItems: Array.isArray(header.baseItems) ? header.baseItems.join(', ') : `${header.baseItems || ''}`,
              formula: `${header.formula || ''}`,
              formulaConfig: parseFormulaToConfig(header.formula || '', sheet.headers || []),
              conditionalFormulaRules: (Array.isArray(header.conditionalFormulaRules) ? header.conditionalFormulaRules : [])
                .map((rule) => normalizeConditionalFormulaRuleForEditor(rule, sheet.headers || [])),
              conditionalDisplayNames: (Array.isArray(header.conditionalDisplayNames || header.conditionalDisplayLabels)
                ? (header.conditionalDisplayNames || header.conditionalDisplayLabels)
                : [])
                .map((rule) => normalizeConditionalDisplayNameRuleForEditor(rule)),
              conditionalLogic: sanitizeHeaderConditionalLogic(header.conditionalLogic, sheet.headers || [], header.id),
              formInputMode: normalizeSheetFormInputMode(header.formInputMode || getLegacyInputModeFromHeaderType(header.type)),
              pendingOption: '',
              rogOverlay: Boolean(header.rogOverlay),
              textBold: Boolean(header.textBold),
              textItalic: Boolean(header.textItalic),
              textUnderline: Boolean(header.textUnderline),
              textAlign: normalizeHeaderTextAlign(header.textAlign),
              renderColor: normalizeHeaderRenderColor(header.renderColor)
            }))
          : [blankHeader()];
        setSheetRenderPresetConfig(sanitizeSheetRenderPresetConfig(
          normalizeSheetRenderPreset(sheet.renderPreset),
          sheet.renderPresetConfig,
          nextHeaders
        ));
        setHeaders(nextHeaders);
        setFilterHeaderIds(sanitizeFilterHeaderIds(sheet.filterHeaderIds || [], nextHeaders));
        setAnalytics(Array.isArray(sheet.analytics) ? sheet.analytics : []);
        setReportSkill(normalizeReportSkillForEditor(sheet?.aiSkills?.report, nextHeaders));
        setAutomations(normalizeAutomationsForEditor(sheet?.aiAutomations, nextHeaders));
        const effectiveFormAccessMode = getEffectiveSheetFormAccessMode(
          sheet.visibility,
          existingSheetForm?.accessMode || 'public'
        );
        setHadExistingSheetForm(Boolean(existingSheetForm?.id));
        setSheetFormConfig(
          existingSheetForm
            ? {
                enabled: true,
                accessMode: effectiveFormAccessMode,
                showShellHeader: existingSheetForm.showShellHeader !== false,
                showFormIntro: existingSheetForm.showFormIntro !== false,
                logoUrl: normalizeSheetFormLogoUrl(existingSheetForm.logoUrl),
                title: existingSheetForm.title || '',
                description: existingSheetForm.description || '',
                submitLabel: existingSheetForm.submitLabel || 'Submit',
                successTitle: existingSheetForm.successTitle || 'Submission received',
                successMessage: existingSheetForm.successMessage || 'Thanks. Your response has been captured.',
                availabilityStartAt: normalizeSheetFormDateTimeInput(existingSheetForm.availabilityStartAt),
                availabilityEndAt: normalizeSheetFormDateTimeInput(existingSheetForm.availabilityEndAt),
                submissionMode: normalizeSheetFormSubmissionMode(existingSheetForm.submissionMode),
                allowDrafts: existingSheetForm.allowDrafts === true,
                enabledHeaderIds: Array.isArray(existingSheetForm.enabledHeaderIds) ? existingSheetForm.enabledHeaderIds : [],
                requiredHeaderIds: Array.isArray(existingSheetForm.requiredHeaderIds) ? existingSheetForm.requiredHeaderIds : [],
                repeatableHeaderIds: Array.isArray(existingSheetForm.repeatableHeaderIds) ? existingSheetForm.repeatableHeaderIds : [],
                repeatableRequiredHeaderIds: Array.isArray(existingSheetForm.repeatableRequiredHeaderIds) ? existingSheetForm.repeatableRequiredHeaderIds : [],
                repeatableFieldWidths: getSheetFormRepeatableFieldWidthsFromForm(existingSheetForm),
                minimumRepeatableRows: normalizeMinimumRepeatableRows(existingSheetForm.minimumRepeatableRows),
                repeatableEntryMode: normalizeRepeatableEntryMode(existingSheetForm.repeatableEntryMode),
                repeatableAddPrompt: `${existingSheetForm.repeatableAddPrompt || 'Add another row?'}`.trim(),
                repeatableAddLabel: `${existingSheetForm.repeatableAddLabel || 'Add another row'}`.trim(),
                repeatableDoneLabel: `${existingSheetForm.repeatableDoneLabel || 'Continue'}`.trim(),
                prefillSelectorConfig: normalizeSheetFormPrefillSelectorConfig(existingSheetForm.prefillSelectorConfig),
                modules: Array.isArray(existingSheetForm.modules) ? existingSheetForm.modules : [],
                repeatableLayout: sheetFormService.normalizeFormLayout(
                  existingSheetForm.repeatableLayout,
                  nextHeaders,
                  existingSheetForm.repeatableHeaderIds,
                  existingSheetForm.repeatableRequiredHeaderIds,
                  false
                ),
                formLayout: sheetFormService.normalizeFormLayout(
                  existingSheetForm.formLayout,
                  nextHeaders,
                  existingSheetForm.enabledHeaderIds,
                  existingSheetForm.requiredHeaderIds
                )
              }
            : createDefaultSheetFormConfig(nextHeaders, sheet.visibility)
        );

        const rows = await sheetService.getSheetRows(id, 1);
        const firstRow = rows[0]?.values || {};
        setFirstRowValues(buildRowValues(nextHeaders, firstRow));
      } catch (err) {
        toast.error(err?.message || 'Failed to load sheet.');
        handleReturnNavigation();
      } finally {
        setLoading(false);
      }
    };

    loadSheet();
  }, [handleReturnNavigation, id, isEditing]);

  useEffect(() => {
    if (!user?.uid) {
      setAvailableSheets([]);
      return;
    }

    let isActive = true;

    const loadAvailableSheets = async () => {
      try {
        const [ownedResult, sharedResult] = await Promise.allSettled([
          sheetService.getUserSheets(user.uid),
          sheetService.getSharedSheets(user.uid)
        ]);

        if (!isActive) return;

        const nextSheets = [
          ...(ownedResult.status === 'fulfilled' ? ownedResult.value : []),
          ...(sharedResult.status === 'fulfilled' ? sharedResult.value : [])
        ].reduce((acc, sheetItem) => {
          if (!sheetItem?.id || acc.some((existing) => existing.id === sheetItem.id)) return acc;
          acc.push(sheetItem);
          return acc;
        }, []);

        setAvailableSheets(nextSheets);
      } catch (error) {
        if (!isActive) return;
        setAvailableSheets([]);
      }
    };

    loadAvailableSheets();

    return () => {
      isActive = false;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || !isPlatformAdmin) {
      setAdminAccessibleHeaderSheets([]);
      return;
    }

    let isActive = true;

    sheetService.getAdminAccessibleSheets()
      .then((items) => {
        if (isActive) setAdminAccessibleHeaderSheets(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (isActive) setAdminAccessibleHeaderSheets([]);
      });

    return () => {
      isActive = false;
    };
  }, [isPlatformAdmin, user?.uid]);

  useEffect(() => {
    const eligibleHeaderIds = (Array.isArray(headers) ? headers : []).filter((header) => isEligibleFormHeader(header)).map((header) => header.id);
    const eligibleSet = new Set(eligibleHeaderIds);

    setSheetFormConfig((prev) => {
      const nextLayout = sheetFormService.normalizeFormLayout(
        prev.formLayout,
        headers,
        prev.enabledHeaderIds,
        prev.requiredHeaderIds
      );
      const currentLayout = Array.isArray(prev.formLayout) ? prev.formLayout : [];
      const nextEnabled = nextLayout.filter((item) => item.type === 'field').map((item) => item.headerId).filter((headerId) => eligibleSet.has(headerId));
      const nextRequired = (Array.isArray(prev.requiredHeaderIds) ? prev.requiredHeaderIds : []).filter((headerId) => eligibleSet.has(headerId) && nextEnabled.includes(headerId));
      const nextRepeatable = (Array.isArray(prev.repeatableHeaderIds) ? prev.repeatableHeaderIds : []).filter((headerId) => eligibleSet.has(headerId) && !nextEnabled.includes(headerId));
      const nextRepeatableRequired = (Array.isArray(prev.repeatableRequiredHeaderIds) ? prev.repeatableRequiredHeaderIds : []).filter((headerId) => nextRepeatable.includes(headerId));
      const nextRepeatableLayout = sheetFormService.normalizeFormLayout(
        prev.repeatableLayout,
        headers,
        nextRepeatable,
        nextRepeatableRequired,
        false
      );
      const nextRepeatableFieldWidths = normalizeSheetFormRepeatableFieldWidths(prev.repeatableFieldWidths, nextRepeatable);
      const enabledUnchanged = (
        nextEnabled.length === (prev.enabledHeaderIds || []).length &&
        nextEnabled.every((headerId, index) => headerId === (prev.enabledHeaderIds || [])[index])
      );
      const requiredUnchanged = (
        nextRequired.length === (prev.requiredHeaderIds || []).length &&
        nextRequired.every((headerId, index) => headerId === (prev.requiredHeaderIds || [])[index])
      );
      const repeatableUnchanged = (
        nextRepeatable.length === (prev.repeatableHeaderIds || []).length &&
        nextRepeatable.every((headerId, index) => headerId === (prev.repeatableHeaderIds || [])[index])
      );
      const repeatableRequiredUnchanged = (
        nextRepeatableRequired.length === (prev.repeatableRequiredHeaderIds || []).length &&
        nextRepeatableRequired.every((headerId, index) => headerId === (prev.repeatableRequiredHeaderIds || [])[index])
      );
      const repeatableFieldWidthsUnchanged = (
        JSON.stringify(nextRepeatableFieldWidths) === JSON.stringify(normalizeSheetFormRepeatableFieldWidths(prev.repeatableFieldWidths, prev.repeatableHeaderIds))
      );
      const layoutUnchanged = (
        nextLayout.length === currentLayout.length &&
        nextLayout.every((item, index) => {
          const currentItem = currentLayout[index];
          return (
            currentItem?.id === item.id &&
            currentItem?.type === item.type &&
            currentItem?.headerId === item.headerId &&
            currentItem?.width === item.width &&
            currentItem?.label === item.label &&
            currentItem?.contentHtml === item.contentHtml &&
            currentItem?.alignment === item.alignment &&
            currentItem?.slideMode === item.slideMode &&
            currentItem?.autoAdvanceSeconds === item.autoAdvanceSeconds &&
            JSON.stringify(currentItem?.slides || []) === JSON.stringify(item?.slides || [])
          );
        })
      );
      const currentRepeatableLayout = Array.isArray(prev.repeatableLayout) ? prev.repeatableLayout : [];
      const repeatableLayoutUnchanged = (
        nextRepeatableLayout.length === currentRepeatableLayout.length &&
        nextRepeatableLayout.every((item, index) => {
          const currentItem = currentRepeatableLayout[index];
          return (
            currentItem?.id === item.id &&
            currentItem?.type === item.type &&
            currentItem?.headerId === item.headerId &&
            currentItem?.width === item.width &&
            currentItem?.label === item.label &&
            currentItem?.contentHtml === item.contentHtml &&
            currentItem?.alignment === item.alignment &&
            currentItem?.slideMode === item.slideMode &&
            currentItem?.autoAdvanceSeconds === item.autoAdvanceSeconds &&
            JSON.stringify(currentItem?.slides || []) === JSON.stringify(item?.slides || [])
          );
        })
      );

      if (!prev.enabled && prev.enabledHeaderIds?.length === 0 && prev.requiredHeaderIds?.length === 0 && !prev.formLayout?.length) {
        return prev;
      }

      if (
        layoutUnchanged &&
        enabledUnchanged &&
        requiredUnchanged &&
        repeatableUnchanged &&
        repeatableRequiredUnchanged &&
        repeatableFieldWidthsUnchanged &&
        repeatableLayoutUnchanged
      ) {
        return prev;
      }

      return {
        ...prev,
        formLayout: nextLayout,
        enabledHeaderIds: nextEnabled,
        requiredHeaderIds: nextRequired,
        repeatableHeaderIds: nextRepeatable,
        repeatableRequiredHeaderIds: nextRepeatableRequired,
        repeatableLayout: nextRepeatableLayout,
        repeatableFieldWidths: nextRepeatableFieldWidths
      };
    });
  }, [headers]);

  useEffect(() => {
    setSheetFormConfig((prev) => {
      const nextAccessMode = getEffectiveSheetFormAccessMode(visibility, prev.accessMode);
      if (nextAccessMode === prev.accessMode) return prev;
      return {
        ...prev,
        accessMode: nextAccessMode
      };
    });
  }, [visibility]);

  useEffect(() => {
    if (!user?.uid) {
      setAvailableWorkspaces([]);
      return;
    }

    let isActive = true;

    const loadWorkspaces = async () => {
      try {
        const workspaces = await sheetWorkspaceService.getAccessibleWorkspaces(user.uid);
        if (!isActive) return;

        setAvailableWorkspaces(workspaces);

        if (isEditing) return;

        const routeWorkspace = workspaceSlug
          ? workspaces.find((workspace) => workspace.slug === workspaceSlug)
          : (isLikelyWorkspaceCustomHost()
              ? workspaces.find((workspace) => `${workspace.customDomain || ''}`.trim().toLowerCase() === window.location.hostname.toLowerCase())
              : null);
        const stateWorkspaceId = `${location.state?.workspaceId || ''}`.trim();
        const stateWorkspace = stateWorkspaceId
          ? workspaces.find((workspace) => workspace.id === stateWorkspaceId)
          : null;
        const preferredWorkspace = routeWorkspace || stateWorkspace || null;

        if (preferredWorkspace) {
          setWorkspaceId(preferredWorkspace.id);
          setWorkspaceTitle(preferredWorkspace.title || '');
          setWorkspaceOnly(true);
        }
      } catch (error) {
        if (!isActive) return;
        setAvailableWorkspaces([]);
      }
    };

    loadWorkspaces();

    return () => {
      isActive = false;
    };
  }, [isEditing, location.state?.workspaceId, user?.uid, workspaceSlug]);

  useEffect(() => {
    if (!user?.uid) {
      setResolvedSharedUsers([]);
      setSelectedSharedUserIds([]);
      return;
    }

    if (visibility !== 'restricted') {
      setResolvedSharedUsers([]);
      setSelectedSharedUserIds([]);
      return;
    }

    const emails = parseEmailList(allowedEmailsInput);
    if (!emails.length) {
      setResolvedSharedUsers([]);
      setSelectedSharedUserIds([]);
      return;
    }

    let isActive = true;

    const loadResolvedSharedUsers = async () => {
      try {
        const users = await userService.findUsersByEmails(emails);
        if (!isActive) return;

        const matchedUsers = users
          .map((item) => ({
            uid: item.id || item.uid,
            email: `${item.email || ''}`.trim().toLowerCase(),
            displayName: `${item.displayName || ''}`.trim()
          }))
          .filter((item) => item.uid && emails.includes(item.email));

        setResolvedSharedUsers(matchedUsers);
        setSelectedSharedUserIds((prev) => {
          const existing = Array.isArray(prev) ? prev : [];
          const stillMatched = existing.filter((uid) => matchedUsers.some((user) => user.uid === uid));
          const newMatches = matchedUsers.map((user) => user.uid).filter((uid) => !stillMatched.includes(uid));
          return [...stillMatched, ...newMatches];
        });
      } catch (error) {
        if (!isActive) return;
        setResolvedSharedUsers([]);
      }
    };

    loadResolvedSharedUsers();

    return () => {
      isActive = false;
    };
  }, [allowedEmailsInput, user?.uid, visibility]);

  const sanitizedHeaders = useMemo(
    () => {
      let hasAssignedRowLabel = false;

      return headers
        .map((header) => {
          const isRowLabel = Boolean(header.isRowLabel) && !hasAssignedRowLabel;
          if (isRowLabel) {
            hasAssignedRowLabel = true;
          }

          return {
            ...header,
            label: `${header.label || ''}`.trim(),
            displayName: `${header.displayName || header.displayLabel || ''}`.trim(),
            width: Math.max(120, Number(header.width) || 180),
            type: SUPPORTED_FIELD_TYPES.includes(normalizeLegacyHeaderType(header.type)) ? normalizeLegacyHeaderType(header.type) : 'text',
            isRowLabel,
            columnAccess: normalizeColumnAccess(header.columnAccess),
            systemField: normalizeLogSystemField(header.systemField),
            textBold: Boolean(header.textBold),
            textItalic: Boolean(header.textItalic),
            textUnderline: Boolean(header.textUnderline),
            textAlign: normalizeHeaderTextAlign(header.textAlign),
            renderColor: normalizeHeaderRenderColor(header.renderColor),
            decimalDigits: DECIMAL_FIELD_TYPES.has(header.type)
              ? normalizeDecimalDigits(header.decimalDigits, 2)
              : undefined,
            summaryType: getAllowedSummaryTypes(header.type).some((option) => option.value === header.summaryType) ? header.summaryType : 'none',
            defaultValueMode: DEFAULT_VALUE_MODE_OPTIONS.some((option) => option.value === header.defaultValueMode) ? header.defaultValueMode : 'none',
            defaultValue: normalizeHeaderDefaultValue(header, header.defaultValue),
            options: isOptionDrivenHeaderType(header.type)
              ? Array.from(new Set(
                  ((getFixedOptionsForHeaderType(header.type).length ? getFixedOptionsForHeaderType(header.type) : (Array.isArray(header.options) ? header.options : [])))
                    .map((option) => `${option || ''}`.trim())
                    .filter(Boolean)
                ))
              : [],
            optionSource: isOptionDrivenHeaderType(header.type)
              ? normalizeSheetOptionSource(header.optionSource)
              : null,
            conditionalOptions: isOptionDrivenHeaderType(header.type)
              ? sanitizeHeaderConditionalOptions(header.conditionalOptions, headers, header.id)
              : [],
            rogOverlay: header.type === 'rog' ? Boolean(header.rogOverlay) : false,
            profileConfig: header.type === 'profile'
              ? sanitizeProfileConfig(header.profileConfig, headers, header.id)
              : undefined,
            baseValue: NUMERIC_HEADER_TYPES.has(header.type)
              ? (header.baseValue === '' || header.baseValue === null || header.baseValue === undefined
                  ? ''
                  : Number(header.baseValue))
              : undefined,
            baseItems: NUMERIC_HEADER_TYPES.has(header.type)
              ? Array.from(new Set(
                  `${header.baseItems || ''}`
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean)
                ))
              : undefined,
            formula: buildFormulaFromConfig(header.formulaConfig, headers) || `${header.formula || ''}`.trim(),
            conditionalFormulaRules: buildConditionalFormulaRulesForSave(header, headers),
            conditionalDisplayNames: (Array.isArray(header.conditionalDisplayNames || header.conditionalDisplayLabels)
              ? (header.conditionalDisplayNames || header.conditionalDisplayLabels)
              : [])
              .map((rule) => ({
                whenHeaderId: `${rule?.whenHeaderId || ''}`.trim(),
                operator: `${rule?.operator || 'equals'}`.trim() || 'equals',
                value: `${rule?.value ?? ''}`,
                displayName: `${rule?.displayName || rule?.label || ''}`.trim()
              }))
              .filter((rule) => rule.whenHeaderId && rule.displayName && headers.some((candidate) => candidate.id === rule.whenHeaderId && candidate.id !== header.id)),
            conditionalLogic: sanitizeHeaderConditionalLogic(header.conditionalLogic, headers, header.id),
            formInputMode: canUseSensorInputMode(normalizeLegacyHeaderType(header.type))
              ? normalizeSheetFormInputMode(header.formInputMode)
              : 'default'
          };
        })
        .filter((header) => header.label);
    },
    [headers]
  );
  const eligibleFormHeaders = useMemo(
    () => sanitizedHeaders.filter((header) => isEligibleFormHeader(header)),
    [sanitizedHeaders]
  );
  const sheetFormLayoutItems = useMemo(
    () => sheetFormService.normalizeFormLayout(
      sheetFormConfig.formLayout,
      sanitizedHeaders,
      sheetFormConfig.enabledHeaderIds,
      sheetFormConfig.requiredHeaderIds
    ),
    [sanitizedHeaders, sheetFormConfig.enabledHeaderIds, sheetFormConfig.formLayout, sheetFormConfig.requiredHeaderIds]
  );
  const repeatableFormLayoutItems = useMemo(
    () => sheetFormService.normalizeFormLayout(
      sheetFormConfig.repeatableLayout,
      sanitizedHeaders,
      sheetFormConfig.repeatableHeaderIds,
      sheetFormConfig.repeatableRequiredHeaderIds,
      false
    ),
    [sanitizedHeaders, sheetFormConfig.repeatableHeaderIds, sheetFormConfig.repeatableLayout, sheetFormConfig.repeatableRequiredHeaderIds]
  );
  const availableFormHeadersToAdd = useMemo(() => {
    const configuredFieldIds = new Set(
      sheetFormLayoutItems
        .filter((item) => item.type === 'field')
        .map((item) => item.headerId)
    );
    const repeatableFieldIds = new Set(Array.isArray(sheetFormConfig.repeatableHeaderIds) ? sheetFormConfig.repeatableHeaderIds : []);
    return eligibleFormHeaders.filter((header) => !configuredFieldIds.has(header.id) && !repeatableFieldIds.has(header.id));
  }, [eligibleFormHeaders, sheetFormConfig.repeatableHeaderIds, sheetFormLayoutItems]);
  const availableRepeatableFormHeadersToAdd = useMemo(() => {
    const sharedFieldIds = new Set(
      sheetFormLayoutItems
        .filter((item) => item.type === 'field')
        .map((item) => item.headerId)
    );
    const repeatableFieldIds = new Set(Array.isArray(sheetFormConfig.repeatableHeaderIds) ? sheetFormConfig.repeatableHeaderIds : []);
    return eligibleFormHeaders.filter((header) => !sharedFieldIds.has(header.id) && !repeatableFieldIds.has(header.id));
  }, [eligibleFormHeaders, sheetFormConfig.repeatableHeaderIds, sheetFormLayoutItems]);
  const selectedFormFieldCount = useMemo(
    () => sheetFormLayoutItems.filter((item) => item.type === 'field').length,
    [sheetFormLayoutItems]
  );
  const selectedRepeatableFormFieldCount = useMemo(
    () => repeatableFormLayoutItems.filter((item) => item.type === 'field').length,
    [repeatableFormLayoutItems]
  );
  useEffect(() => {
    if (!sheetFormConfig.enabled) return;
    const availableFieldIds = availableFormHeadersToAdd.map((header) => header.id);
    if (pendingFormHeaderId && availableFieldIds.includes(pendingFormHeaderId)) return;
    const nextAvailableHeader = availableFormHeadersToAdd[0];
    setPendingFormHeaderId(nextAvailableHeader?.id || '');
  }, [availableFormHeadersToAdd, pendingFormHeaderId, sheetFormConfig.enabled]);
  useEffect(() => {
    if (!sheetFormConfig.enabled || normalizeSheetFormSubmissionMode(sheetFormConfig.submissionMode) !== 'modular') return;
    const availableFieldIds = availableRepeatableFormHeadersToAdd.map((header) => header.id);
    if (pendingRepeatableFormHeaderId && availableFieldIds.includes(pendingRepeatableFormHeaderId)) return;
    const nextAvailableHeader = availableRepeatableFormHeadersToAdd[0];
    setPendingRepeatableFormHeaderId(nextAvailableHeader?.id || '');
  }, [
    availableRepeatableFormHeadersToAdd,
    pendingRepeatableFormHeaderId,
    sheetFormConfig.enabled,
    sheetFormConfig.submissionMode
  ]);
  const isSheetFormAccessLocked = false;
  const effectiveSanitizedHeaders = useMemo(
    () => (
      isLogSheet
        ? [...sanitizedHeaders, ...buildManagedLogSystemHeaders()]
        : sanitizedHeaders
    ),
    [isLogSheet, sanitizedHeaders]
  );
  const sanitizedSheetRenderPresetConfig = useMemo(
    () => sanitizeSheetRenderPresetConfig(sheetRenderPreset, sheetRenderPresetConfig, sanitizedHeaders),
    [sanitizedHeaders, sheetRenderPreset, sheetRenderPresetConfig]
  );
  const referencedOptionSourceSheetIds = useMemo(
    () => Array.from(new Set(
      headers.flatMap((header) => ([
        getHeaderOptionSourceDraft(header.optionSource).sheetId,
        ...(Array.isArray(header.conditionalOptions) ? header.conditionalOptions : []).map((rule) => (
          getHeaderOptionSourceDraft(rule?.optionSource).sheetId
        ))
      ]).filter(Boolean))
    )),
    [headers]
  );
  const referencedFormulaSheetIds = useMemo(
    () => Array.from(new Set(
      headers.flatMap((header) => {
        const directSheetId = `${header?.formulaConfig?.lookupSheetId || header?.formulaConfig?.filterListSheetId || ''}`.trim();
        const conditionalSheetIds = (Array.isArray(header?.conditionalFormulaRules) ? header.conditionalFormulaRules : [])
          .map((rule) => `${rule?.formulaConfig?.lookupSheetId || rule?.formulaConfig?.filterListSheetId || ''}`.trim())
          .filter(Boolean);
        return [directSheetId, ...conditionalSheetIds].filter(Boolean);
      })
    )),
    [headers]
  );
  const referencedExternalSheetIds = useMemo(
    () => Array.from(new Set([
      ...referencedOptionSourceSheetIds,
      ...referencedFormulaSheetIds
    ])),
    [referencedFormulaSheetIds, referencedOptionSourceSheetIds]
  );
  const currentWorkspaceId = `${workspaceId || ''}`.trim();
  const headerAvailableSheets = useMemo(
    () => [...availableSheets, ...adminAccessibleHeaderSheets].reduce((acc, sheetItem) => {
      if (!sheetItem?.id || acc.some((existing) => existing.id === sheetItem.id)) return acc;
      acc.push(sheetItem);
      return acc;
    }, []),
    [adminAccessibleHeaderSheets, availableSheets]
  );
  const organizationOptions = useMemo(() => {
    if (!currentWorkspaceId) return availableOrganizations;
    const selectedWorkspace = availableWorkspaces.find((workspace) => workspace.id === currentWorkspaceId);
    const workspaceOrganizationIds = Array.isArray(selectedWorkspace?.organizationIds) ? selectedWorkspace.organizationIds : [];
    return availableOrganizations.filter((organization) => workspaceOrganizationIds.includes(organization.id));
  }, [availableOrganizations, availableWorkspaces, currentWorkspaceId]);
  const workspaceScopedAvailableSheets = useMemo(
    () => availableSheets.filter((sheetItem) => {
      const sheetWorkspaceId = `${sheetItem.workspaceId || ''}`.trim();
      if (!currentWorkspaceId) return true;
      if (!sheetWorkspaceId) return false;
      return sheetWorkspaceId === currentWorkspaceId;
    }),
    [availableSheets, currentWorkspaceId]
  );
  const workspaceScopedHeaderAvailableSheets = useMemo(
    () => headerAvailableSheets.filter((sheetItem) => {
      const sheetWorkspaceId = `${sheetItem.workspaceId || ''}`.trim();
      if (!currentWorkspaceId) return true;
      if (!sheetWorkspaceId) return false;
      return sheetWorkspaceId === currentWorkspaceId;
    }),
    [currentWorkspaceId, headerAvailableSheets]
  );
  const availableFormPrefillSelectorSources = useMemo(
    () => workspaceScopedAvailableSheets
      .filter((sheetItem) => `${sheetItem?.id || ''}`.trim() && `${sheetItem?.id || ''}`.trim() !== `${id || ''}`.trim())
      .map((sheetItem) => {
        const matchingActions = (Array.isArray(sheetItem?.actionForms) ? sheetItem.actionForms : [])
          .filter((actionForm) => (
            `${actionForm?.targetSheetId || ''}`.trim() === `${id || ''}`.trim()
            && `${actionForm?.mode || ''}`.trim() === 'linked_form'
            && actionForm?.allowTargetRowSelection === true
          ))
          .map((actionForm) => ({
            id: `${actionForm?.id || ''}`.trim(),
            label: `${actionForm?.buttonLabel || actionForm?.name || 'Action'}`.trim() || 'Action'
          }))
          .filter((actionForm) => actionForm.id);

        return matchingActions.length
          ? {
              sheetId: `${sheetItem.id}`.trim(),
              sheetTitle: `${sheetItem.title || 'Untitled Sheet'}`.trim() || 'Untitled Sheet',
              actions: matchingActions
            }
          : null;
      })
      .filter(Boolean),
    [id, workspaceScopedAvailableSheets]
  );
  const selectedFormPrefillSelectorSource = useMemo(
    () => availableFormPrefillSelectorSources.find((source) => source.sheetId === normalizeSheetFormPrefillSelectorConfig(sheetFormConfig.prefillSelectorConfig).sourceSheetId) || null,
    [availableFormPrefillSelectorSources, sheetFormConfig.prefillSelectorConfig]
  );
  useEffect(() => {
    const validSheetIds = new Set(
      workspaceScopedAvailableSheets
        .map((sheetItem) => `${sheetItem?.id || ''}`.trim())
        .filter((sheetId) => sheetId && sheetId !== id)
    );
    const validCurrentHeaderIds = new Set(
      headers
        .map((header) => `${header?.id || ''}`.trim())
        .filter(Boolean)
    );
    const availableHeadersBySheetId = workspaceScopedAvailableSheets.reduce((acc, sheetItem) => {
      acc[sheetItem.id] = new Set(
        (Array.isArray(sheetItem?.headers) ? sheetItem.headers : [])
          .map((header) => `${header?.id || ''}`.trim())
          .filter(Boolean)
      );
      return acc;
    }, {});
    availableHeadersBySheetId[ACTION_UPDATE_TARGET_CURRENT_SHEET] = validCurrentHeaderIds;

    setActionForms((prev) => {
      const next = prev.map((actionForm) => {
        const mode = normalizeActionConfigMode(actionForm?.mode);
        const targetSheetId = mode === 'update_rows'
          ? (
              actionForm.targetSheetId === ACTION_UPDATE_TARGET_CURRENT_SHEET || validSheetIds.has(actionForm.targetSheetId)
                ? actionForm.targetSheetId
                : ''
            )
          : (validSheetIds.has(actionForm.targetSheetId) ? actionForm.targetSheetId : '');
        const validTargetHeaderIds = availableHeadersBySheetId[targetSheetId] || new Set();
        return {
          ...actionForm,
          mode,
          targetSheetId,
          fieldMappings: sanitizeActionFormFieldMappingsForEditor(actionForm?.fieldMappings).filter((fieldMapping) => (
            (
              fieldMapping.valueMode === 'static' ||
              !fieldMapping.sourceHeaderId ||
              validCurrentHeaderIds.has(fieldMapping.sourceHeaderId)
            ) &&
            (!fieldMapping.targetHeaderId || validTargetHeaderIds.has(fieldMapping.targetHeaderId))
          )),
          conditions: sanitizeActionUpdateConditionsForEditor(actionForm?.conditions).filter((condition) => (
            (!condition.targetHeaderId || validTargetHeaderIds.has(condition.targetHeaderId)) &&
            (
              condition.valueMode === 'static' ||
              !condition.sourceHeaderId ||
              validCurrentHeaderIds.has(condition.sourceHeaderId)
            )
          )),
          updates: sanitizeActionUpdateValuesForEditor(actionForm?.updates).filter((update) => (
            (!update.targetHeaderId || validTargetHeaderIds.has(update.targetHeaderId)) &&
            (
              update.valueMode === 'static' ||
              !update.sourceHeaderId ||
              validCurrentHeaderIds.has(update.sourceHeaderId)
            )
          ))
        };
      });

      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
  }, [headers, id, workspaceScopedAvailableSheets]);
  useEffect(() => {
    const hasLinkedActionForms = actionForms.some((actionForm) => `${actionForm?.targetSheetId || ''}`.trim());
    setHeaders((prev) => {
      const existingIndex = prev.findIndex((header) => header.id === ACTIVE_ACTIONS_HEADER_ID);
      if (!hasLinkedActionForms && existingIndex === -1) return prev;
      if (!hasLinkedActionForms) {
        return prev.filter((header) => header.id !== ACTIVE_ACTIONS_HEADER_ID);
      }
      if (existingIndex === -1) {
        return [...prev, buildManagedActiveActionsHeader()];
      }
      return prev.map((header) => (
        header.id === ACTIVE_ACTIONS_HEADER_ID
          ? { ...header, ...buildManagedActiveActionsHeader() }
          : header
      ));
    });
    setFirstRowValues((prev) => {
      if (!hasLinkedActionForms && !Object.prototype.hasOwnProperty.call(prev, ACTIVE_ACTIONS_HEADER_ID)) return prev;
      if (!hasLinkedActionForms) {
        const next = { ...prev };
        delete next[ACTIVE_ACTIONS_HEADER_ID];
        return next;
      }
      return Object.prototype.hasOwnProperty.call(prev, ACTIVE_ACTIONS_HEADER_ID)
        ? prev
        : { ...prev, [ACTIVE_ACTIONS_HEADER_ID]: '' };
    });
  }, [actionForms]);
  useEffect(() => {
    setSheetRenderPresetConfig((prev) => sanitizeSheetRenderPresetConfig(sheetRenderPreset, prev, headers));
  }, [headers, sheetRenderPreset]);

  useEffect(() => {
    if (!referencedExternalSheetIds.length) {
      setOptionSourceRowsBySheetId({});
      return;
    }

    let isActive = true;

    const loadOptionSourceRows = async () => {
      const results = await Promise.allSettled(
        referencedExternalSheetIds.map(async (sheetId) => {
          const sourceSheet = await sheetService.getSheetById(sheetId);
          const rows = await sheetService.getSheetRows(sheetId, 500);
          return [sheetId, { rows, headers: sourceSheet?.headers || [] }];
        })
      );

      if (!isActive) return;

      const nextRowsBySheetId = results.reduce((acc, result) => {
        if (result.status !== 'fulfilled') return acc;
        const [sheetId, sourceData] = result.value;
        acc[sheetId] = sourceData;
        return acc;
      }, {});

      setOptionSourceRowsBySheetId(nextRowsBySheetId);
    };

    loadOptionSourceRows();

    return () => {
      isActive = false;
    };
  }, [referencedExternalSheetIds]);

  const availableOptionSourceHeadersBySheetId = useMemo(
    () => {
      const nextMap = headerAvailableSheets.reduce((acc, sheetItem) => {
        acc[sheetItem.id] = (Array.isArray(sheetItem.headers) ? sheetItem.headers : []).filter((header) => header?.id && header?.label);
        return acc;
      }, {});

      if (id) {
        nextMap[id] = headers.filter((header) => header?.id && `${header?.label || ''}`.trim());
      }

      return nextMap;
    },
    [headerAvailableSheets, headers, id]
  );
  const sanitizedFilterHeaders = useMemo(
    () => sanitizeFilterHeaderIds(filterHeaderIds, sanitizedHeaders),
    [filterHeaderIds, sanitizedHeaders]
  );
  const updateActionFormDraft = useCallback((actionId, updater) => {
    setActionForms((prev) => prev.map((item) => (
      item.id === actionId ? updater(item) : item
    )));
  }, []);
const moveActionFormDraft = useCallback((actionId, direction) => {
  setActionForms((prev) => {
    const currentIndex = prev.findIndex((item) => item.id === actionId);
    if (currentIndex === -1) return prev;
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;

      const next = [...prev];
      const [movedItem] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, movedItem);
    return next;
  });
}, []);
const duplicateActionFormDraft = useCallback((actionId) => {
  setActionForms((prev) => {
    const currentIndex = prev.findIndex((item) => item.id === actionId);
    if (currentIndex === -1) return prev;

    const source = prev[currentIndex];
    const duplicated = {
      ...source,
      id: createActionFormDraft().id,
      name: `${source?.name || ''}`.trim() ? `${`${source.name}`.trim()} Copy` : source?.name || '',
      buttonLabel: `${source?.buttonLabel || ''}`.trim() ? `${`${source.buttonLabel}`.trim()} Copy` : source?.buttonLabel || '',
      fieldMappings: sanitizeActionFormFieldMappingsForEditor(source?.fieldMappings).map((fieldMapping) => ({
        ...fieldMapping,
        id: createActionFormFieldMappingDraft().id
      })),
      conditions: sanitizeActionUpdateConditionsForEditor(source?.conditions).map((condition) => ({
        ...condition,
        id: createActionUpdateConditionDraft().id
      })),
      updates: sanitizeActionUpdateValuesForEditor(source?.updates).map((update) => ({
        ...update,
        id: createActionUpdateValueDraft().id
      }))
    };

    const next = [...prev];
    next.splice(currentIndex + 1, 0, duplicated);
    return next;
  });
}, []);
const removeActionFormDraft = useCallback((actionId) => {
  setActionForms((prev) => prev.filter((item) => item.id !== actionId));
}, []);
  const getActionTargetHeaders = useCallback((targetSheetId) => (
    targetSheetId === ACTION_UPDATE_TARGET_CURRENT_SHEET
      ? sanitizedHeaders.filter((header) => header.id !== ACTIVE_ACTIONS_HEADER_ID && `${header?.label || ''}`.trim())
      : (availableOptionSourceHeadersBySheetId[targetSheetId] || [])
  ), [availableOptionSourceHeadersBySheetId, sanitizedHeaders]);

  useEffect(() => {
    setFilterHeaderIds((prev) => {
      const next = sanitizeFilterHeaderIds(prev, sanitizedHeaders);
      return next.length === prev.length && next.every((item, index) => item === prev[index]) ? prev : next;
    });
  }, [sanitizedHeaders]);

  useEffect(() => {
    setReportSkill((prev) => {
      const next = normalizeReportSkillForEditor(prev, sanitizedHeaders);
      const prevFocusIds = Array.isArray(prev?.focusHeaderIds) ? prev.focusHeaderIds : [];
      const focusIdsUnchanged = next.focusHeaderIds.length === prevFocusIds.length &&
        next.focusHeaderIds.every((item, index) => item === prevFocusIds[index]);
      if (focusIdsUnchanged) return prev;
      return { ...prev, focusHeaderIds: next.focusHeaderIds };
    });
  }, [sanitizedHeaders]);

  useEffect(() => {
    setAutomations((prev) => normalizeAutomationsForEditor(prev, sanitizedHeaders));
  }, [sanitizedHeaders]);

  const tableWidth = sanitizedHeaders.reduce((total, header) => total + header.width, 0);
  const previewRowValues = useMemo(
    () => resolveRowFormulas(sanitizedHeaders, buildRowValues(sanitizedHeaders, firstRowValues), optionSourceRowsBySheetId),
    [firstRowValues, optionSourceRowsBySheetId, sanitizedHeaders]
  );
  const csvColumns = useMemo(() => getSheetCsvColumns(sanitizedHeaders), [sanitizedHeaders]);
  const resolvedHeaderOptionsById = useMemo(
    () => sanitizedHeaders.reduce((acc, header) => {
      acc[header.id] = getHeaderOptionList(header, optionSourceRowsBySheetId, previewRowValues, sanitizedHeaders);
      return acc;
    }, {}),
    [optionSourceRowsBySheetId, previewRowValues, sanitizedHeaders]
  );
  const previewHeaderStatesById = useMemo(
    () => sanitizedHeaders.reduce((acc, header) => {
      acc[header.id] = evaluateHeaderConditionalState(header, previewRowValues, sanitizedHeaders);
      return acc;
    }, {}),
    [previewRowValues, sanitizedHeaders]
  );

  const applyGeneratedSheetBlueprint = useCallback((blueprint = {}) => {
    const generatedHeaders = Array.isArray(blueprint.headers) && blueprint.headers.length
      ? blueprint.headers.map((header, index) => ({
        ...blankHeader(),
        id: createHeaderId(),
        label: `${header?.label || `Column ${index + 1}`}`.trim(),
        type: SUPPORTED_FIELD_TYPES.includes(normalizeLegacyHeaderType(`${header?.type || 'text'}`.trim())) ? normalizeLegacyHeaderType(`${header?.type || 'text'}`.trim()) : 'text',
        isRowLabel: Boolean(header?.isRowLabel),
        options: Array.isArray(header?.options) ? header.options.map((option) => `${option || ''}`.trim()).filter(Boolean) : [],
        summaryType: `${header?.summaryType || 'none'}`.trim(),
        defaultValueMode: `${header?.defaultValueMode || 'none'}`.trim(),
        defaultValue: header?.defaultValue ?? '',
        formInputMode: normalizeSheetFormInputMode(header?.formInputMode || getLegacyInputModeFromHeaderType(header?.type)),
        pendingOption: ''
      }))
      : [blankHeader()];

    const headerIdByLabel = generatedHeaders.reduce((acc, header) => {
      acc[`${header.label || ''}`.trim().toLowerCase()] = header.id;
      return acc;
    }, {});

    const generatedFilterHeaderIds = Array.from(new Set(
      (Array.isArray(blueprint.filterHeaderLabels) ? blueprint.filterHeaderLabels : [])
        .map((label) => headerIdByLabel[`${label || ''}`.trim().toLowerCase()])
        .filter(Boolean)
    ));

    const generatedAnalytics = suggestAnalytics(generatedHeaders);

    const generatedReportSkill = normalizeReportSkillForEditor({
      ...(blueprint?.aiSkills?.report || {}),
      focusHeaderIds: (Array.isArray(blueprint?.aiSkills?.report?.focusHeaderLabels)
        ? blueprint.aiSkills.report.focusHeaderLabels
        : [])
        .map((label) => headerIdByLabel[`${label || ''}`.trim().toLowerCase()])
        .filter(Boolean)
    }, generatedHeaders);

    const generatedAutomations = normalizeAutomationsForEditor(
      (Array.isArray(blueprint.aiAutomations) ? blueprint.aiAutomations : []).map((automation) => ({
        ...automation,
        focusHeaderIds: (Array.isArray(automation?.focusHeaderLabels) ? automation.focusHeaderLabels : [])
          .map((label) => headerIdByLabel[`${label || ''}`.trim().toLowerCase()])
          .filter(Boolean),
        recipientsText: Array.isArray(automation?.recipients) ? automation.recipients.join(', ') : ''
      })),
      generatedHeaders
    );
    const nextRenderPreset = normalizeSheetRenderPreset(blueprint?.renderPreset);
    const generatedRenderPresetConfig = sanitizeSheetRenderPresetConfig(
      nextRenderPreset,
      {
        profileHeaderId: headerIdByLabel[`${blueprint?.renderPresetProfileHeaderLabel || ''}`.trim().toLowerCase()] || ''
      },
      generatedHeaders
    );

    setTitle(`${blueprint?.title || ''}`.trim());
    setDescription(`${blueprint?.description || ''}`.trim());
    setSheetRenderPreset(nextRenderPreset);
    setSheetRenderPresetConfig(generatedRenderPresetConfig);
    setHeaders(generatedHeaders);
    setFilterHeaderIds(generatedFilterHeaderIds);
    setAnalytics(generatedAnalytics);
    setReportSkill(generatedReportSkill);
    setAutomations(generatedAutomations);
    setFirstRowValues(buildRowValues(generatedHeaders, {}));
    setBlueprintSummary(`${blueprint?.referenceSummary || ''}`.trim());
    setActiveBuilderPanel('headers');
  }, []);

  const handleGenerateBlueprint = useCallback(async () => {
    const prompt = `${sheetIdeaPrompt || ''}`.trim();
    if (!prompt) {
      toast.error('Describe what operation this sheet should support first.');
      return;
    }

    setGeneratingBlueprint(true);
    try {
      const selectedReferenceSheets = availableSheets
        .filter((sheetItem) => selectedReferenceSheetIds.includes(sheetItem.id))
        .map((sheetItem) => ({
          id: sheetItem.id,
          title: sheetItem.title || '',
          description: sheetItem.description || '',
          isLogSheet: Boolean(sheetItem.isLogSheet),
          workspaceTitle: sheetItem.workspaceTitle || '',
          headers: Array.isArray(sheetItem.headers) ? sheetItem.headers.map((header) => ({
            id: header.id,
            label: header.label,
            type: header.type,
            isRowLabel: Boolean(header.isRowLabel)
          })) : [],
          filterHeaderIds: sheetItem.filterHeaderIds || [],
          analytics: sheetItem.analytics || []
        }));

      const recentSheets = workspaceScopedAvailableSheets
        .slice(0, 8)
        .map((sheetItem) => ({
          id: sheetItem.id,
          title: sheetItem.title || '',
          description: sheetItem.description || '',
          isLogSheet: Boolean(sheetItem.isLogSheet),
          workspaceTitle: sheetItem.workspaceTitle || '',
          headers: Array.isArray(sheetItem.headers) ? sheetItem.headers.map((header) => ({
            id: header.id,
            label: header.label,
            type: header.type,
            isRowLabel: Boolean(header.isRowLabel)
          })) : [],
          filterHeaderIds: sheetItem.filterHeaderIds || [],
          analytics: sheetItem.analytics || []
        }));

      const blueprint = await sheetService.generateSheetBlueprint({
        prompt,
        workspaceTitle,
        referenceSheets: selectedReferenceSheets,
        recentSheets
      });
      applyGeneratedSheetBlueprint(blueprint);
      toast.success('Sheet draft generated. Review and adjust before saving.');
    } catch (error) {
      toast.error(error?.message || 'Failed to generate a sheet draft.');
    } finally {
      setGeneratingBlueprint(false);
    }
  }, [applyGeneratedSheetBlueprint, availableSheets, selectedReferenceSheetIds, sheetIdeaPrompt, workspaceScopedAvailableSheets, workspaceTitle]);

  const toggleReferenceSheet = (sheetId) => {
    setSelectedReferenceSheetIds((prev) => (
      prev.includes(sheetId)
        ? prev.filter((item) => item !== sheetId)
        : [...prev, sheetId]
    ));
  };

  const handleInsertHeader = (targetIndex = headers.length) => {
    const nextHeader = blankHeader();
    setHeaders((prev) => {
      const next = [...prev];
      next.splice(targetIndex, 0, nextHeader);
      return next;
    });
    setFirstRowValues((prev) => ({ ...prev, [nextHeader.id]: '' }));
  };

  const handleAddHeader = () => {
    handleInsertHeader(headers.length);
  };

  const handleHeaderChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId ? { ...header, label: value } : header
    )));
  };

  const handleHeaderDisplayNameChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId ? { ...header, displayName: value } : header
    )));
  };

  const handleHeaderWidthChange = (headerId, value) => {
    const width = Math.max(120, Number(value) || 180);
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId ? { ...header, width } : header
    )));
  };

  const handleHeaderSystemFieldChange = (headerId, value) => {
    const normalizedValue = normalizeLogSystemField(value);
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? { ...header, systemField: normalizedValue }
        : normalizedValue && header.systemField === normalizedValue
          ? { ...header, systemField: '' }
          : header
    )));
  };

  const handleHeaderColumnAccessChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? { ...header, columnAccess: normalizeColumnAccess(value) }
        : header
    )));
  };

  const handleHeaderRowLabelChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      value
        ? { ...header, isRowLabel: header.id === headerId }
        : header.id === headerId
          ? { ...header, isRowLabel: false }
          : header
    )));
  };

  const handleHeaderTextStyleChange = (headerId, field, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? {
            ...header,
            [field]: field === 'textAlign' ? normalizeHeaderTextAlign(value) : Boolean(value)
          }
        : header
    )));
  };

  const handleHeaderRenderColorChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? { ...header, renderColor: value }
        : header
    )));
  };

  const handleHeaderDecimalDigitsChange = (headerId, value) => {
    const decimalDigits = normalizeDecimalDigits(value, 2);
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId ? { ...header, decimalDigits } : header
    )));
  };

  const handleHeaderSummaryTypeChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? { ...header, summaryType: getAllowedSummaryTypes(header.type).some((option) => option.value === value) ? value : 'none' }
        : header
    )));
  };

  const handleHeaderDefaultValueModeChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? {
            ...header,
            defaultValueMode: DEFAULT_VALUE_MODE_OPTIONS.some((option) => option.value === value) ? value : 'none',
            defaultValue: value === 'none' ? (header.type === 'checkbox' ? false : header.type === 'multiselect' ? [] : '') : header.defaultValue
          }
        : header
    )));
  };

  const handleHeaderDefaultValueChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? { ...header, defaultValue: normalizeHeaderDefaultValue(header, value) }
        : header
    )));
  };

  const handleHeaderRogOverlayChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId ? { ...header, rogOverlay: Boolean(value) } : header
    )));
  };

  const handleHeaderDefaultListDraftChange = (headerId, value) => {
    setHeaderDefaultListDrafts((prev) => ({ ...prev, [headerId]: value }));
  };

  const handleAddHeaderDefaultListItem = (headerId) => {
    const nextItems = parsePendingOptionsInput(headerDefaultListDrafts[headerId]);
    if (!nextItems.length) return;

    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const existingItems = normalizeListItems(header.defaultValue);
      const itemsToAdd = nextItems.filter((item) => !existingItems.includes(item));
      return {
        ...header,
        defaultValue: itemsToAdd.length ? [...existingItems, ...itemsToAdd] : existingItems
      };
    }));
    setHeaderDefaultListDrafts((prev) => ({ ...prev, [headerId]: '' }));
  };

  const handleRemoveHeaderDefaultListItem = (headerId, itemIndex) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? {
            ...header,
            defaultValue: normalizeListItems(header.defaultValue).filter((_, index) => index !== itemIndex)
          }
        : header
    )));
  };

  const handleHeaderBaseValueChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId ? { ...header, baseValue: value } : header
    )));
  };

  const handleHeaderBaseItemsChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId ? { ...header, baseItems: value } : header
    )));
  };

  const handleToggleSharedUser = (userId) => {
    setSelectedSharedUserIds((prev) => (
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    ));
  };

  const handleToggleFilterHeader = (headerId) => {
    setFilterHeaderIds((prev) => (
      prev.includes(headerId)
        ? prev.filter((id) => id !== headerId)
        : [...prev, headerId]
    ));
  };

  const handleHeaderEditorModeChange = (headerId, mode) => {
    setHeaderEditorModes((prev) => ({ ...prev, [headerId]: mode }));
  };

  const handleHeaderTypeChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? {
            ...header,
            type: value,
            decimalDigits: DECIMAL_FIELD_TYPES.has(value) ? normalizeDecimalDigits(header.decimalDigits, 2) : undefined,
            summaryType: getAllowedSummaryTypes(value).some((option) => option.value === header.summaryType) ? header.summaryType : 'none',
            defaultValue: value === 'checkbox'
              ? Boolean(header.defaultValue)
              : value === 'multiselect' || value === 'list'
                ? (Array.isArray(header.defaultValue) ? header.defaultValue : [])
                : NUMERIC_HEADER_TYPES.has(value)
                  ? normalizeHeaderDefaultValue({ ...header, type: value, defaultValueMode: header.defaultValueMode }, header.defaultValue)
                  : `${Array.isArray(header.defaultValue) ? header.defaultValue.join(', ') : (header.defaultValue ?? '')}`,
            baseValue: NUMERIC_HEADER_TYPES.has(value) ? (header.baseValue ?? '') : '',
            baseItems: NUMERIC_HEADER_TYPES.has(value) ? (header.baseItems ?? '') : '',
            options: isOptionDrivenHeaderType(value)
              ? (getFixedOptionsForHeaderType(value).length ? getFixedOptionsForHeaderType(value) : (header.options || []))
              : [],
            optionsFromHeaderId: isOptionDrivenHeaderType(value) ? `${header.optionsFromHeaderId || ''}`.trim() : '',
            optionSource: isOptionDrivenHeaderType(value)
              ? getHeaderOptionSourceDraft(header.optionSource)
              : { sheetId: '', headerId: '' },
            conditionalOptions: isOptionDrivenHeaderType(value)
              ? (Array.isArray(header.conditionalOptions) ? header.conditionalOptions : [])
              : [],
            rogOverlay: value === 'rog' ? Boolean(header.rogOverlay) : false,
            formInputMode: canUseSensorInputMode(value)
              ? normalizeSheetFormInputMode(header.formInputMode)
              : 'default',
            profileConfig: value === 'profile'
              ? getProfileConfigDraft(header.profileConfig)
              : { imageHeaderId: '', labelHeaderId: '' },
            pendingOption: ''
          }
        : header
    )));
  };

  const handleHeaderFormulaTypeChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? {
            ...header,
            type: value === 'percentage'
              ? 'percent'
              : value === 'duration_days' || value === 'duration_hours'
                ? 'number'
                : value === 'week'
                  ? 'number'
                  : value === 'day' || value === 'month'
                    ? 'text'
                : value === 'duration_day_hours'
                  ? 'text'
                  : header.type,
            formulaConfig: value ? getFormulaConfigShape(value) : null,
            formula: ''
          }
        : header
    )));
  };

  const handleHeaderOptionsFromHeaderChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? { ...header, optionsFromHeaderId: `${value || ''}`.trim() }
        : header
    )));
  };

  const handleFormulaOperandAdd = (headerId, operandId) => {
    if (!operandId) return;
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId || !header.formulaConfig) return header;
      const operandList = getFormulaOperandList(header.formulaConfig);
      if (operandList.includes(operandId)) return header;
      if (header.formulaConfig.type === 'percentage') {
        const denominatorId = `${header.formulaConfig.denominator || ''}`.startsWith('base:')
          ? `${header.formulaConfig.denominator}`.slice(5)
          : header.formulaConfig.denominator;
        const incomingId = `${operandId || ''}`.startsWith('base:') ? `${operandId}`.slice(5) : operandId;
        if (denominatorId && incomingId === denominatorId) return header;
        const limit = getPercentageOperandLimit(header.formulaConfig.numeratorType);
        if (limit && operandList.length >= limit) return header;
        return {
          ...header,
          formulaConfig: {
            ...header.formulaConfig,
            numeratorOperands: [...operandList, operandId]
          },
          formula: ''
        };
      }
      const limit = getFormulaOperandLimit(header.formulaConfig);
      if (limit && operandList.length >= limit) return header;
      return {
        ...header,
        formulaConfig: {
          ...header.formulaConfig,
          operands: [...operandList, operandId]
        },
        formula: ''
      };
    }));
  };

  const handlePercentageNumeratorTypeChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId || header.formulaConfig?.type !== 'percentage') return header;
      const existingOperands = header.formulaConfig.numeratorOperands || [];
      const limit = getPercentageOperandLimit(value);
      const denominatorId = `${header.formulaConfig.denominator || ''}`.startsWith('base:')
        ? `${header.formulaConfig.denominator}`.slice(5)
        : header.formulaConfig.denominator;
      const filteredOperands = denominatorId
        ? existingOperands.filter((operandId) => {
            const resolvedOperandId = `${operandId || ''}`.startsWith('base:') ? `${operandId}`.slice(5) : operandId;
            return resolvedOperandId !== denominatorId;
          })
        : existingOperands;
      return {
        ...header,
        formulaConfig: {
          ...header.formulaConfig,
          numeratorType: value,
          numeratorOperands: limit ? filteredOperands.slice(0, limit) : filteredOperands
        },
        formula: ''
      };
    }));
  };

  const handlePercentageDenominatorChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId && header.formulaConfig?.type === 'percentage'
        ? {
            ...header,
            formulaConfig: {
              ...header.formulaConfig,
              denominator: value,
              numeratorOperands: (header.formulaConfig.numeratorOperands || []).filter((operandId) => {
                const resolvedOperandId = `${operandId || ''}`.startsWith('base:') ? `${operandId}`.slice(5) : operandId;
                const resolvedDenominatorId = `${value || ''}`.startsWith('base:') ? `${value}`.slice(5) : value;
                return resolvedOperandId !== resolvedDenominatorId;
              })
            },
            formula: ''
          }
        : header
    )));
  };

  const handleFormulaOperandRemove = (headerId, operandId) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId && header.formulaConfig
        ? header.formulaConfig.type === 'percentage'
          ? {
              ...header,
              formulaConfig: {
                ...header.formulaConfig,
                numeratorOperands: (header.formulaConfig.numeratorOperands || []).filter((item) => item !== operandId)
              },
              formula: ''
            }
          : {
              ...header,
              formulaConfig: {
                ...header.formulaConfig,
                operands: (header.formulaConfig.operands || []).filter((item) => item !== operandId)
              },
              formula: ''
            }
        : header
    )));
  };

  const handleFormulaOperandMove = (headerId, operandId, direction) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId || !header.formulaConfig) return header;
      const operandList = getFormulaOperandList(header.formulaConfig);
      const currentIndex = operandList.findIndex((item) => item === operandId);
      if (currentIndex === -1) return header;
      const nextIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0 || nextIndex >= operandList.length) return header;
      const nextOperands = [...operandList];
      const [moved] = nextOperands.splice(currentIndex, 1);
      nextOperands.splice(nextIndex, 0, moved);
      return {
        ...header,
        formulaConfig: {
          ...header.formulaConfig,
          ...(header.formulaConfig.type === 'percentage'
            ? { numeratorOperands: nextOperands }
            : { operands: nextOperands })
        },
        formula: ''
      };
    }));
  };

  const handleFormulaSeparatorChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId && header.formulaConfig?.type === 'concat'
        ? {
            ...header,
            formulaConfig: {
              ...header.formulaConfig,
              separator: value
            },
            formula: ''
          }
        : header
    )));
  };

  const handleLookupFormulaChange = (headerId, field, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId || header.formulaConfig?.type !== 'lookup') return header;
      const nextConfig = {
        ...header.formulaConfig,
        [field]: value
      };

      if (field === 'lookupSheetId') {
        nextConfig.lookupMatchSourceHeaderId = '';
        nextConfig.lookupReturnHeaderId = '';
      }

      return {
        ...header,
        formulaConfig: nextConfig,
        formula: ''
      };
    }));
  };

  const handleFilterListFormulaChange = (headerId, field, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId || header.formulaConfig?.type !== 'filter_list') return header;
      const nextConfig = {
        ...header.formulaConfig,
        [field]: value
      };
      if (field === 'filterListSheetId') {
        nextConfig.filterListReturnHeaderId = '';
        nextConfig.filterListFilters = [];
      }
      return {
        ...header,
        formulaConfig: nextConfig,
        formula: ''
      };
    }));
  };

  const handleFilterListRuleAdd = (headerId) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId && header.formulaConfig?.type === 'filter_list'
        ? {
            ...header,
            formulaConfig: {
              ...header.formulaConfig,
              filterListFilters: [
                ...(Array.isArray(header.formulaConfig.filterListFilters) ? header.formulaConfig.filterListFilters : []),
                { sourceHeaderId: '', operator: 'equals', currentHeaderId: '' }
              ]
            },
            formula: ''
          }
        : header
    )));
  };

  const handleFilterListRuleChange = (headerId, ruleIndex, field, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId || header.formulaConfig?.type !== 'filter_list') return header;
      const rules = [...(Array.isArray(header.formulaConfig.filterListFilters) ? header.formulaConfig.filterListFilters : [])];
      const currentRule = rules[ruleIndex] || { sourceHeaderId: '', operator: 'equals', currentHeaderId: '' };
      rules[ruleIndex] = {
        ...currentRule,
        [field]: value
      };
      return {
        ...header,
        formulaConfig: {
          ...header.formulaConfig,
          filterListFilters: rules
        },
        formula: ''
      };
    }));
  };

  const handleFilterListRuleRemove = (headerId, ruleIndex) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId && header.formulaConfig?.type === 'filter_list'
        ? {
            ...header,
            formulaConfig: {
              ...header.formulaConfig,
              filterListFilters: (Array.isArray(header.formulaConfig.filterListFilters) ? header.formulaConfig.filterListFilters : [])
                .filter((_, index) => index !== ruleIndex)
            },
            formula: ''
          }
        : header
    )));
  };

  const handleDurationWeekendModeChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId && header.formulaConfig?.type === 'duration_days'
        ? {
            ...header,
            formulaConfig: {
              ...header.formulaConfig,
              weekendMode: value === 'exclude' ? 'exclude' : 'include'
            },
            formula: ''
          }
        : header
    )));
  };

  const handleClearFormula = (headerId) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? { ...header, formula: '', formulaConfig: null }
        : header
    )));
  };

  const handleAddConditionalFormulaRule = (headerId) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? {
            ...header,
            conditionalFormulaRules: [
              ...(Array.isArray(header.conditionalFormulaRules) ? header.conditionalFormulaRules : []),
              { headerId: '', operator: 'equals', value: '', formula: '', formulaConfig: null }
            ]
          }
        : header
    )));
  };

  const handleConditionalFormulaRuleChange = (headerId, ruleIndex, field, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalFormulaRules) ? header.conditionalFormulaRules : [])];
      const currentRule = rules[ruleIndex] || { headerId: '', operator: 'equals', value: '', formula: '', formulaConfig: null };
      rules[ruleIndex] = { ...currentRule, [field]: value };
      return { ...header, conditionalFormulaRules: rules };
    }));
  };

  const handleConditionalFormulaTypeChange = (headerId, ruleIndex, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalFormulaRules) ? header.conditionalFormulaRules : [])];
      const currentRule = rules[ruleIndex] || { headerId: '', operator: 'equals', value: '', formula: '', formulaConfig: null };
      rules[ruleIndex] = {
        ...currentRule,
        formulaConfig: value ? getFormulaConfigShape(value) : null,
        formula: ''
      };
      return {
        ...header,
        type: value === 'percentage'
          ? 'percent'
          : value === 'duration_days' || value === 'duration_hours'
            ? 'number'
            : value === 'duration_day_hours'
              ? 'text'
              : header.type,
        conditionalFormulaRules: rules
      };
    }));
  };

  const handleConditionalFormulaOperandAdd = (headerId, ruleIndex, operandId) => {
    if (!operandId) return;
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalFormulaRules) ? header.conditionalFormulaRules : [])];
      const rule = rules[ruleIndex];
      if (!rule?.formulaConfig) return header;
      const operandList = getFormulaOperandList(rule.formulaConfig);
      if (operandList.includes(operandId)) return header;
      if (rule.formulaConfig.type === 'percentage') {
        const denominatorId = `${rule.formulaConfig.denominator || ''}`.startsWith('base:')
          ? `${rule.formulaConfig.denominator}`.slice(5)
          : rule.formulaConfig.denominator;
        const incomingId = `${operandId || ''}`.startsWith('base:') ? `${operandId}`.slice(5) : operandId;
        if (denominatorId && incomingId === denominatorId) return header;
        const limit = getPercentageOperandLimit(rule.formulaConfig.numeratorType);
        if (limit && operandList.length >= limit) return header;
        rules[ruleIndex] = {
          ...rule,
          formulaConfig: {
            ...rule.formulaConfig,
            numeratorOperands: [...operandList, operandId]
          },
          formula: ''
        };
        return { ...header, conditionalFormulaRules: rules };
      }
      const limit = getFormulaOperandLimit(rule.formulaConfig);
      if (limit && operandList.length >= limit) return header;
      rules[ruleIndex] = {
        ...rule,
        formulaConfig: {
          ...rule.formulaConfig,
          operands: [...operandList, operandId]
        },
        formula: ''
      };
      return { ...header, conditionalFormulaRules: rules };
    }));
  };

  const handleConditionalFormulaOperandRemove = (headerId, ruleIndex, operandId) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalFormulaRules) ? header.conditionalFormulaRules : [])];
      const rule = rules[ruleIndex];
      if (!rule?.formulaConfig) return header;
      rules[ruleIndex] = rule.formulaConfig.type === 'percentage'
        ? {
            ...rule,
            formulaConfig: {
              ...rule.formulaConfig,
              numeratorOperands: (rule.formulaConfig.numeratorOperands || []).filter((item) => item !== operandId)
            },
            formula: ''
          }
        : {
            ...rule,
            formulaConfig: {
              ...rule.formulaConfig,
              operands: (rule.formulaConfig.operands || []).filter((item) => item !== operandId)
            },
            formula: ''
          };
      return { ...header, conditionalFormulaRules: rules };
    }));
  };

  const handleConditionalFormulaRuleRemove = (headerId, ruleIndex) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? { ...header, conditionalFormulaRules: (header.conditionalFormulaRules || []).filter((_, index) => index !== ruleIndex) }
        : header
    )));
  };

  const handleConditionalFormulaSeparatorChange = (headerId, ruleIndex, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(header.conditionalFormulaRules || [])];
      const rule = rules[ruleIndex];
      if (!rule?.formulaConfig || rule.formulaConfig.type !== 'concat') return header;
      rules[ruleIndex] = {
        ...rule,
        formulaConfig: {
          ...rule.formulaConfig,
          separator: value
        },
        formula: ''
      };
      return { ...header, conditionalFormulaRules: rules };
    }));
  };

  const handleConditionalLookupFormulaChange = (headerId, ruleIndex, field, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(header.conditionalFormulaRules || [])];
      const rule = rules[ruleIndex];
      if (rule?.formulaConfig?.type !== 'lookup') return header;
      const nextConfig = {
        ...rule.formulaConfig,
        [field]: value
      };

      if (field === 'lookupSheetId') {
        nextConfig.lookupMatchSourceHeaderId = '';
        nextConfig.lookupReturnHeaderId = '';
      }

      rules[ruleIndex] = {
        ...rule,
        formulaConfig: nextConfig,
        formula: ''
      };
      return { ...header, conditionalFormulaRules: rules };
    }));
  };

  const handleConditionalDurationWeekendModeChange = (headerId, ruleIndex, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(header.conditionalFormulaRules || [])];
      const rule = rules[ruleIndex];
      if (rule?.formulaConfig?.type !== 'duration_days') return header;
      rules[ruleIndex] = {
        ...rule,
        formulaConfig: {
          ...rule.formulaConfig,
          weekendMode: value === 'exclude' ? 'exclude' : 'include'
        },
        formula: ''
      };
      return { ...header, conditionalFormulaRules: rules };
    }));
  };

  const handleConditionalPercentageNumeratorTypeChange = (headerId, ruleIndex, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(header.conditionalFormulaRules || [])];
      const rule = rules[ruleIndex];
      if (rule?.formulaConfig?.type !== 'percentage') return header;
      const existingOperands = rule.formulaConfig.numeratorOperands || [];
      const limit = getPercentageOperandLimit(value);
      const denominatorId = `${rule.formulaConfig.denominator || ''}`.startsWith('base:')
        ? `${rule.formulaConfig.denominator}`.slice(5)
        : rule.formulaConfig.denominator;
      const filteredOperands = denominatorId
        ? existingOperands.filter((operandId) => {
            const resolvedOperandId = `${operandId || ''}`.startsWith('base:') ? `${operandId}`.slice(5) : operandId;
            return resolvedOperandId !== denominatorId;
          })
        : existingOperands;
      rules[ruleIndex] = {
        ...rule,
        formulaConfig: {
          ...rule.formulaConfig,
          numeratorType: value,
          numeratorOperands: limit ? filteredOperands.slice(0, limit) : filteredOperands
        },
        formula: ''
      };
      return { ...header, conditionalFormulaRules: rules };
    }));
  };

  const handleConditionalPercentageDenominatorChange = (headerId, ruleIndex, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(header.conditionalFormulaRules || [])];
      const rule = rules[ruleIndex];
      if (rule?.formulaConfig?.type !== 'percentage') return header;
      rules[ruleIndex] = {
        ...rule,
        formulaConfig: {
          ...rule.formulaConfig,
          denominator: value,
          numeratorOperands: (rule.formulaConfig.numeratorOperands || []).filter((operandId) => {
            const resolvedOperandId = `${operandId || ''}`.startsWith('base:') ? `${operandId}`.slice(5) : operandId;
            const resolvedDenominatorId = `${value || ''}`.startsWith('base:') ? `${value}`.slice(5) : value;
            return resolvedOperandId !== resolvedDenominatorId;
          })
        },
        formula: ''
      };
      return { ...header, conditionalFormulaRules: rules };
    }));
  };

  const handleHeaderPendingOptionChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId ? { ...header, pendingOption: value } : header
    )));
  };

  const handleHeaderOptionSourceChange = (headerId, field, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;

      const currentSource = getHeaderOptionSourceDraft(header.optionSource);
      const nextSource = {
        ...currentSource,
        [field]: value
      };

      if (field === 'sheetId') {
        nextSource.headerId = '';
        nextSource.sourceFilters = [];
      }

      return {
        ...header,
        optionSource: nextSource
      };
    }));
  };

  const handleHeaderOptionSourceFilterLogicChange = (headerId, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;

      const nextSource = getHeaderOptionSourceDraft(header.optionSource);
      nextSource.filterLogic = value === 'or' ? 'or' : 'and';

      return {
        ...header,
        optionSource: nextSource
      };
    }));
  };

  const handleAddHeaderOptionSourceFilter = (headerId) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const nextSource = getHeaderOptionSourceDraft(header.optionSource);
      nextSource.sourceFilters = [...(nextSource.sourceFilters || []), createOptionSourceFilterDraft()];
      return {
        ...header,
        optionSource: nextSource
      };
    }));
  };

  const handleRemoveHeaderOptionSourceFilter = (headerId, filterIndex) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const nextSource = getHeaderOptionSourceDraft(header.optionSource);
      nextSource.sourceFilters = (nextSource.sourceFilters || []).filter((_, index) => index !== filterIndex);
      return {
        ...header,
        optionSource: nextSource
      };
    }));
  };

  const handleHeaderOptionSourceFilterChange = (headerId, filterIndex, field, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;

      const nextSource = getHeaderOptionSourceDraft(header.optionSource);
      const filters = [...(nextSource.sourceFilters || [])];
      const nextFilter = { ...(filters[filterIndex] || createOptionSourceFilterDraft()), [field]: value };
      if (field === 'valueMode') {
        if (value === 'static') {
          nextFilter.headerId = '';
        } else {
          nextFilter.value = '';
        }
      }
      filters[filterIndex] = nextFilter;
      nextSource.sourceFilters = filters;

      return {
        ...header,
        optionSource: nextSource
      };
    }));
  };

  const handleHeaderProfileConfigChange = (headerId, field, value) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? { ...header, profileConfig: { ...getProfileConfigDraft(header.profileConfig), [field]: value } }
        : header
    )));
  };

  const handleConditionalOptionRuleChange = (headerId, ruleIndex, field, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalOptions) ? header.conditionalOptions : [])];
      const currentRule = rules[ruleIndex] || createConditionalOptionRuleDraft();
      rules[ruleIndex] = { ...currentRule, [field]: value };
      return { ...header, conditionalOptions: rules };
    }));
  };

  const handleConditionalOptionRuleSourceChange = (headerId, ruleIndex, field, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalOptions) ? header.conditionalOptions : [])];
      const currentRule = rules[ruleIndex] || createConditionalOptionRuleDraft();
      const nextSource = getHeaderOptionSourceDraft(currentRule.optionSource);
      nextSource[field] = value;
      if (field === 'sheetId') {
        nextSource.headerId = '';
        nextSource.sourceFilters = [];
      }
      rules[ruleIndex] = { ...currentRule, optionSource: nextSource };
      return { ...header, conditionalOptions: rules };
    }));
  };

  const handleConditionalOptionRuleSourceFilterLogicChange = (headerId, ruleIndex, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalOptions) ? header.conditionalOptions : [])];
      const currentRule = rules[ruleIndex] || createConditionalOptionRuleDraft();
      const nextSource = getHeaderOptionSourceDraft(currentRule.optionSource);
      nextSource.filterLogic = value === 'or' ? 'or' : 'and';
      rules[ruleIndex] = { ...currentRule, optionSource: nextSource };
      return { ...header, conditionalOptions: rules };
    }));
  };

  const handleAddConditionalOptionRuleSourceFilter = (headerId, ruleIndex) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalOptions) ? header.conditionalOptions : [])];
      const currentRule = rules[ruleIndex] || createConditionalOptionRuleDraft();
      const nextSource = getHeaderOptionSourceDraft(currentRule.optionSource);
      nextSource.sourceFilters = [...(nextSource.sourceFilters || []), createOptionSourceFilterDraft()];
      rules[ruleIndex] = { ...currentRule, optionSource: nextSource };
      return { ...header, conditionalOptions: rules };
    }));
  };

  const handleRemoveConditionalOptionRuleSourceFilter = (headerId, ruleIndex, filterIndex) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalOptions) ? header.conditionalOptions : [])];
      const currentRule = rules[ruleIndex] || createConditionalOptionRuleDraft();
      const nextSource = getHeaderOptionSourceDraft(currentRule.optionSource);
      nextSource.sourceFilters = (nextSource.sourceFilters || []).filter((_, index) => index !== filterIndex);
      rules[ruleIndex] = { ...currentRule, optionSource: nextSource };
      return { ...header, conditionalOptions: rules };
    }));
  };

  const handleConditionalOptionRuleSourceFilterChange = (headerId, ruleIndex, filterIndex, field, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalOptions) ? header.conditionalOptions : [])];
      const currentRule = rules[ruleIndex] || createConditionalOptionRuleDraft();
      const nextSource = getHeaderOptionSourceDraft(currentRule.optionSource);
      const filters = [...(nextSource.sourceFilters || [])];
      const nextFilter = { ...(filters[filterIndex] || createOptionSourceFilterDraft()), [field]: value };
      if (field === 'valueMode') {
        if (value === 'static') {
          nextFilter.headerId = '';
        } else {
          nextFilter.value = '';
        }
      }
      filters[filterIndex] = nextFilter;
      nextSource.sourceFilters = filters;
      rules[ruleIndex] = { ...currentRule, optionSource: nextSource };
      return { ...header, conditionalOptions: rules };
    }));
  };

  const handleConditionalOptionRulePendingOptionChange = (headerId, ruleIndex, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalOptions) ? header.conditionalOptions : [])];
      const currentRule = rules[ruleIndex] || createConditionalOptionRuleDraft();
      rules[ruleIndex] = {
        ...currentRule,
        pendingOption: value
      };
      return { ...header, conditionalOptions: rules };
    }));
  };

  const handleAddConditionalOptionRuleOption = (headerId, ruleIndex) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalOptions) ? header.conditionalOptions : [])];
      const currentRule = rules[ruleIndex] || createConditionalOptionRuleDraft();
      const existingOptions = Array.isArray(currentRule.options) ? currentRule.options : [];
      const optionsToAdd = parsePendingOptionsInput(currentRule.pendingOption)
        .filter((option) => !existingOptions.includes(option));
      if (!optionsToAdd.length) {
        rules[ruleIndex] = {
          ...currentRule,
          pendingOption: ''
        };
        return { ...header, conditionalOptions: rules };
      }
      rules[ruleIndex] = {
        ...currentRule,
        options: [...existingOptions, ...optionsToAdd],
        pendingOption: ''
      };
      return { ...header, conditionalOptions: rules };
    }));
  };

  const handleRemoveConditionalOptionRuleOption = (headerId, ruleIndex, optionToRemove) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalOptions) ? header.conditionalOptions : [])];
      const currentRule = rules[ruleIndex] || createConditionalOptionRuleDraft();
      rules[ruleIndex] = {
        ...currentRule,
        options: (currentRule.options || []).filter((option) => option !== optionToRemove)
      };
      return { ...header, conditionalOptions: rules };
    }));
  };

  const handleAddConditionalOptionRule = (headerId) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? { ...header, conditionalOptions: [...(Array.isArray(header.conditionalOptions) ? header.conditionalOptions : []), createConditionalOptionRuleDraft()] }
        : header
    )));
  };

  const handleRemoveConditionalOptionRule = (headerId, ruleIndex) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? { ...header, conditionalOptions: (Array.isArray(header.conditionalOptions) ? header.conditionalOptions : []).filter((_, index) => index !== ruleIndex) }
        : header
    )));
  };

  const handleConditionalLogicChange = (headerId, field, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const nextLogic = {
        ...(header.conditionalLogic || { action: 'show', condition: 'all', rules: [] }),
        [field]: value
      };
      return {
        ...header,
        conditionalLogic: nextLogic
      };
    }));
  };

  const handleConditionalRuleChange = (headerId, ruleIndex, field, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(header.conditionalLogic?.rules || [])];
      const currentRule = rules[ruleIndex] || { headerId: '', operator: 'equals', value: '' };
      rules[ruleIndex] = { ...currentRule, [field]: value };
      return {
        ...header,
        conditionalLogic: {
          ...(header.conditionalLogic || { action: 'show', condition: 'all' }),
          rules
        }
      };
    }));
  };

  const handleAddConditionalRule = (headerId) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? {
            ...header,
            conditionalLogic: {
              ...(header.conditionalLogic || { action: 'show', condition: 'all' }),
              rules: [...(header.conditionalLogic?.rules || []), { headerId: '', operator: 'equals', value: '' }]
            }
          }
        : header
    )));
  };

  const handleRemoveConditionalRule = (headerId, ruleIndex) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = (header.conditionalLogic?.rules || []).filter((_, index) => index !== ruleIndex);
      return {
        ...header,
        conditionalLogic: rules.length
          ? { ...(header.conditionalLogic || { action: 'show', condition: 'all' }), rules }
          : null
      };
    }));
  };

  const handleClearConditionalLogic = (headerId) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? { ...header, conditionalLogic: null }
        : header
    )));
  };

  const handleAddConditionalDisplayNameRule = (headerId) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? {
            ...header,
            conditionalDisplayNames: [
              ...(Array.isArray(header.conditionalDisplayNames) ? header.conditionalDisplayNames : []),
              createConditionalDisplayNameRuleDraft()
            ]
          }
        : header
    )));
  };

  const handleConditionalDisplayNameRuleChange = (headerId, ruleIndex, field, value) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const rules = [...(Array.isArray(header.conditionalDisplayNames) ? header.conditionalDisplayNames : [])];
      const currentRule = rules[ruleIndex] || createConditionalDisplayNameRuleDraft();
      rules[ruleIndex] = {
        ...currentRule,
        [field]: value
      };
      return { ...header, conditionalDisplayNames: rules };
    }));
  };

  const handleRemoveConditionalDisplayNameRule = (headerId, ruleIndex) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? {
            ...header,
            conditionalDisplayNames: (Array.isArray(header.conditionalDisplayNames) ? header.conditionalDisplayNames : [])
              .filter((_, index) => index !== ruleIndex)
          }
        : header
    )));
  };

  const handleAddHeaderOption = (headerId) => {
    setHeaders((prev) => prev.map((header) => {
      if (header.id !== headerId) return header;
      const existingOptions = Array.isArray(header.options) ? header.options : [];
      const optionsToAdd = parsePendingOptionsInput(header.pendingOption)
        .filter((option) => !existingOptions.includes(option));
      if (!optionsToAdd.length) {
        return { ...header, pendingOption: '' };
      }
      return {
        ...header,
        options: [...existingOptions, ...optionsToAdd],
        pendingOption: ''
      };
    }));
  };

  const handleRemoveHeaderOption = (headerId, optionToRemove) => {
    setHeaders((prev) => prev.map((header) => (
      header.id === headerId
        ? { ...header, options: (header.options || []).filter((option) => option !== optionToRemove) }
        : header
    )));
  };

  const handleResizeStart = (event, headerId) => {
    event.preventDefault();
    const currentHeader = headers.find((header) => header.id === headerId);
    setActiveResize({
      headerId,
      startX: event.clientX,
      startWidth: Math.max(120, Number(currentHeader?.width) || 180)
    });
  };

  useEffect(() => {
    if (!activeResize) return undefined;

    const handleMouseMove = (event) => {
      const nextWidth = Math.max(120, activeResize.startWidth + (event.clientX - activeResize.startX));
      setHeaders((prev) => prev.map((header) => (
        header.id === activeResize.headerId ? { ...header, width: nextWidth } : header
      )));
    };

    const handleMouseUp = () => {
      setActiveResize(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [activeResize]);

  const handleRemoveHeader = (headerId) => {
    if (headers.length === 1) {
      toast.error('At least one header is required.');
      return;
    }

    setHeaders((prev) => prev
      .filter((header) => header.id !== headerId)
      .map((header) => (
        header.formulaConfig
          ? {
              ...header,
              optionSource: (() => {
                return clearOptionSourceHeaderReferences(header.optionSource, id, headerId);
              })(),
              formulaConfig: {
                ...header.formulaConfig,
                ...(header.formulaConfig.type === 'percentage'
                  ? {
                      numeratorOperands: (header.formulaConfig.numeratorOperands || []).filter((operandId) => operandId !== headerId),
                      denominator: (`${header.formulaConfig.denominator || ''}`.startsWith('base:')
                        ? `${header.formulaConfig.denominator}`.slice(5)
                        : header.formulaConfig.denominator) === headerId
                        ? ''
                        : header.formulaConfig.denominator
                    }
                  : {
                      operands: (header.formulaConfig.operands || []).filter((operandId) => operandId !== headerId)
                    })
              },
              conditionalFormulaRules: (header.conditionalFormulaRules || [])
                .filter((rule) => rule.headerId !== headerId)
                .map((rule) => ({
                  ...rule,
                  formulaConfig: rule.formulaConfig
                    ? {
                        ...rule.formulaConfig,
                        ...(rule.formulaConfig.type === 'percentage'
                          ? {
                              numeratorOperands: (rule.formulaConfig.numeratorOperands || []).filter((operandId) => operandId !== headerId),
                              denominator: (`${rule.formulaConfig.denominator || ''}`.startsWith('base:')
                                ? `${rule.formulaConfig.denominator}`.slice(5)
                                : rule.formulaConfig.denominator) === headerId
                                ? ''
                                : rule.formulaConfig.denominator
                            }
                          : {
                              operands: (rule.formulaConfig.operands || []).filter((operandId) => operandId !== headerId)
                            })
                      }
                    : rule.formulaConfig
                })),
              conditionalLogic: sanitizeHeaderConditionalLogic(header.conditionalLogic, prev.filter((item) => item.id !== headerId), header.id),
              profileConfig: sanitizeProfileConfig(header.profileConfig, prev.filter((item) => item.id !== headerId), header.id),
              conditionalOptions: sanitizeHeaderConditionalOptions(
                (header.conditionalOptions || []).map((rule) => ({
                  ...rule,
                  optionSource: (() => {
                    return clearOptionSourceHeaderReferences(rule?.optionSource, id, headerId);
                  })()
                })),
                prev.filter((item) => item.id !== headerId),
                header.id
              ).map((rule) => normalizeConditionalOptionRuleForEditor(rule))
            }
          : {
              ...header,
              optionSource: (() => {
                return clearOptionSourceHeaderReferences(header.optionSource, id, headerId);
              })(),
              conditionalFormulaRules: (header.conditionalFormulaRules || []).filter((rule) => rule.headerId !== headerId),
              conditionalLogic: sanitizeHeaderConditionalLogic(header.conditionalLogic, prev.filter((item) => item.id !== headerId), header.id),
              profileConfig: sanitizeProfileConfig(header.profileConfig, prev.filter((item) => item.id !== headerId), header.id),
              conditionalOptions: sanitizeHeaderConditionalOptions(
                (header.conditionalOptions || []).map((rule) => ({
                  ...rule,
                  optionSource: (() => {
                    return clearOptionSourceHeaderReferences(rule?.optionSource, id, headerId);
                  })()
                })),
                prev.filter((item) => item.id !== headerId),
                header.id
              ).map((rule) => normalizeConditionalOptionRuleForEditor(rule))
            }
      )));
    setFirstRowValues((prev) => {
      const next = { ...prev };
      delete next[headerId];
      return next;
    });
  };

  const handleMoveHeader = (headerId, direction) => {
    setHeaders((prev) => {
      const currentIndex = prev.findIndex((header) => header.id === headerId);
      if (currentIndex === -1) return prev;

      const targetIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;

      const next = [...prev];
      const [movedHeader] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, movedHeader);
      return next;
    });
  };

  const handleFirstRowChange = (headerId, value) => {
    setFirstRowValues((prev) => ({ ...prev, [headerId]: value }));
  };

  const handlePreviewListDraftChange = (headerId, value) => {
    setPreviewListDrafts((prev) => ({ ...prev, [headerId]: value }));
  };

  const handleAddPreviewListItem = (headerId) => {
    const nextItem = `${previewListDrafts[headerId] || ''}`.trim();
    if (!nextItem) return;

    setFirstRowValues((prev) => ({
      ...prev,
      [headerId]: [...normalizeListItems(prev[headerId]), nextItem]
    }));
    setPreviewListDrafts((prev) => ({ ...prev, [headerId]: '' }));
  };

  const handleRemovePreviewListItem = (headerId, itemIndex) => {
    setFirstRowValues((prev) => ({
      ...prev,
      [headerId]: normalizeListItems(prev[headerId]).filter((_, index) => index !== itemIndex)
    }));
  };

  const runSaveStep = async (label, operation) => {
    try {
      return await operation();
    } catch (error) {
      const nextError = error instanceof Error ? error : new Error(`${error}`);
      nextError.message = `${label} failed: ${nextError.message || 'Unknown error'}`;
      nextError.saveStep = nextError.saveStep || label;
      throw nextError;
    }
  };

  const handleToggleFormHeader = (headerId) => {
    setSheetFormConfig((prev) => {
      const currentLayout = Array.isArray(prev.formLayout) ? prev.formLayout : [];
      const hasField = currentLayout.some((item) => item.type === 'field' && item.headerId === headerId);
      const nextLayout = hasField
        ? currentLayout.filter((item) => !(item.type === 'field' && item.headerId === headerId))
        : [...currentLayout, createSheetFormFieldLayoutItem(headerId)];
      const nextEnabled = nextLayout.filter((item) => item.type === 'field').map((item) => item.headerId);
      const nextRepeatable = (Array.isArray(prev.repeatableHeaderIds) ? prev.repeatableHeaderIds : []).filter((item) => item !== headerId);

      return {
        ...prev,
        formLayout: nextLayout,
        enabledHeaderIds: nextEnabled,
        requiredHeaderIds: (Array.isArray(prev.requiredHeaderIds) ? prev.requiredHeaderIds : []).filter((item) => nextEnabled.includes(item)),
        repeatableHeaderIds: nextRepeatable,
        repeatableLayout: (Array.isArray(prev.repeatableLayout) ? prev.repeatableLayout : []).filter((item) => !(item.type === 'field' && item.headerId === headerId)),
        repeatableRequiredHeaderIds: (Array.isArray(prev.repeatableRequiredHeaderIds) ? prev.repeatableRequiredHeaderIds : []).filter((item) => nextRepeatable.includes(item)),
        repeatableFieldWidths: normalizeSheetFormRepeatableFieldWidths(prev.repeatableFieldWidths, nextRepeatable)
      };
    });
  };

  const handleToggleRequiredFormHeader = (headerId) => {
    setSheetFormConfig((prev) => {
      const enabledHeaderIds = Array.isArray(prev.enabledHeaderIds) ? prev.enabledHeaderIds : [];
      if (!enabledHeaderIds.includes(headerId)) {
        return prev;
      }

      const requiredHeaderIds = Array.isArray(prev.requiredHeaderIds) ? prev.requiredHeaderIds : [];
      return {
        ...prev,
        formLayout: (Array.isArray(prev.formLayout) ? prev.formLayout : []).map((item) => (
          item.type === 'field' && item.headerId === headerId
            ? { ...item, required: !requiredHeaderIds.includes(headerId) }
            : item
        )),
        requiredHeaderIds: requiredHeaderIds.includes(headerId)
          ? requiredHeaderIds.filter((item) => item !== headerId)
          : [...requiredHeaderIds, headerId]
      };
    });
  };

  const handleUpdateFormLayoutItem = (itemId, updates) => {
    setSheetFormConfig((prev) => {
      const nextLayout = (Array.isArray(prev.formLayout) ? prev.formLayout : []).map((item) => {
        if (item.id !== itemId) return item;
        if (item.type === 'field') {
          return {
            ...item,
            ...updates,
            width: normalizeSheetFormFieldWidth(updates.width ?? item.width)
          };
        }
        if (item.type === 'textBlock') {
          return {
            ...item,
            ...updates,
            contentHtml: updates.contentHtml !== undefined ? sanitizeSheetFormRichTextHtml(updates.contentHtml) : item.contentHtml,
            alignment: normalizeSheetFormTextBlockAlignment(updates.alignment ?? item.alignment)
          };
        }
        if (item.type === 'imageSlide') {
          return {
            ...item,
            ...updates,
            slides: updates.slides ? sanitizeSheetFormSlideItems(updates.slides) : sanitizeSheetFormSlideItems(item.slides),
            slideMode: normalizeSheetFormSlideMode(updates.slideMode ?? item.slideMode),
            autoAdvanceSeconds: normalizeSheetFormSlideIntervalSeconds(updates.autoAdvanceSeconds ?? item.autoAdvanceSeconds)
          };
        }
        return {
          ...item,
          ...updates
        };
      });
      return {
        ...prev,
        formLayout: nextLayout
      };
    });
  };

  const handleMoveFormLayoutItem = (itemId, direction) => {
    setSheetFormConfig((prev) => {
      const currentLayout = Array.isArray(prev.formLayout) ? [...prev.formLayout] : [];
      const currentIndex = currentLayout.findIndex((item) => item.id === itemId);
      if (currentIndex < 0) return prev;
      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= currentLayout.length) return prev;
      const [movedItem] = currentLayout.splice(currentIndex, 1);
      currentLayout.splice(nextIndex, 0, movedItem);
      return {
        ...prev,
        formLayout: currentLayout
      };
    });
  };

  const handleRemoveFormLayoutItem = (itemId) => {
    setSheetFormConfig((prev) => {
      const currentLayout = Array.isArray(prev.formLayout) ? prev.formLayout : [];
      const removedItem = currentLayout.find((item) => item.id === itemId);
      if (!removedItem) return prev;
      const nextLayout = currentLayout.filter((item) => item.id !== itemId);
      const nextEnabled = nextLayout.filter((item) => item.type === 'field').map((item) => item.headerId);
      return {
        ...prev,
        formLayout: nextLayout,
        enabledHeaderIds: nextEnabled,
        requiredHeaderIds: (Array.isArray(prev.requiredHeaderIds) ? prev.requiredHeaderIds : []).filter((headerId) => nextEnabled.includes(headerId))
      };
    });
  };

  const handleUpdateRepeatableLayoutItem = (itemId, updates) => {
    setSheetFormConfig((prev) => {
      const nextLayout = (Array.isArray(prev.repeatableLayout) ? prev.repeatableLayout : []).map((item) => {
        if (item.id !== itemId) return item;
        if (item.type === 'field') {
          return {
            ...item,
            ...updates,
            width: normalizeSheetFormFieldWidth(updates.width ?? item.width)
          };
        }
        if (item.type === 'textBlock') {
          return {
            ...item,
            ...updates,
            contentHtml: updates.contentHtml !== undefined ? sanitizeSheetFormRichTextHtml(updates.contentHtml) : item.contentHtml,
            alignment: normalizeSheetFormTextBlockAlignment(updates.alignment ?? item.alignment)
          };
        }
        if (item.type === 'imageSlide') {
          return {
            ...item,
            ...updates,
            slides: updates.slides ? sanitizeSheetFormSlideItems(updates.slides) : sanitizeSheetFormSlideItems(item.slides),
            slideMode: normalizeSheetFormSlideMode(updates.slideMode ?? item.slideMode),
            autoAdvanceSeconds: normalizeSheetFormSlideIntervalSeconds(updates.autoAdvanceSeconds ?? item.autoAdvanceSeconds)
          };
        }
        return {
          ...item,
          ...updates
        };
      });
      const nextRepeatable = nextLayout.filter((item) => item.type === 'field').map((item) => item.headerId);
      return {
        ...prev,
        repeatableLayout: nextLayout,
        repeatableHeaderIds: nextRepeatable,
        repeatableRequiredHeaderIds: (Array.isArray(prev.repeatableRequiredHeaderIds) ? prev.repeatableRequiredHeaderIds : []).filter((headerId) => nextRepeatable.includes(headerId)),
        repeatableFieldWidths: normalizeSheetFormRepeatableFieldWidths(prev.repeatableFieldWidths, nextRepeatable)
      };
    });
  };

  const handleMoveRepeatableLayoutItem = (itemId, direction) => {
    setSheetFormConfig((prev) => {
      const currentLayout = Array.isArray(prev.repeatableLayout) ? [...prev.repeatableLayout] : [];
      const currentIndex = currentLayout.findIndex((item) => item.id === itemId);
      if (currentIndex < 0) return prev;
      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= currentLayout.length) return prev;
      const [movedItem] = currentLayout.splice(currentIndex, 1);
      currentLayout.splice(nextIndex, 0, movedItem);
      return {
        ...prev,
        repeatableLayout: currentLayout,
        repeatableHeaderIds: currentLayout.filter((item) => item.type === 'field').map((item) => item.headerId)
      };
    });
  };

  const handleRemoveRepeatableLayoutItem = (itemId) => {
    setSheetFormConfig((prev) => {
      const currentLayout = Array.isArray(prev.repeatableLayout) ? prev.repeatableLayout : [];
      const removedItem = currentLayout.find((item) => item.id === itemId);
      if (!removedItem) return prev;
      const nextLayout = currentLayout.filter((item) => item.id !== itemId);
      const nextRepeatable = nextLayout.filter((item) => item.type === 'field').map((item) => item.headerId);
      return {
        ...prev,
        repeatableLayout: nextLayout,
        repeatableHeaderIds: nextRepeatable,
        repeatableRequiredHeaderIds: (Array.isArray(prev.repeatableRequiredHeaderIds) ? prev.repeatableRequiredHeaderIds : []).filter((headerId) => nextRepeatable.includes(headerId)),
        repeatableFieldWidths: normalizeSheetFormRepeatableFieldWidths(prev.repeatableFieldWidths, nextRepeatable)
      };
    });
  };

  const handleAddFormField = () => {
    if (!pendingFormHeaderId) return;
    setSheetFormConfig((prev) => {
      const currentLayout = Array.isArray(prev.formLayout) ? prev.formLayout : [];
      if (currentLayout.some((item) => item.type === 'field' && item.headerId === pendingFormHeaderId)) {
        return prev;
      }
      return {
        ...prev,
        formLayout: [...currentLayout, createSheetFormFieldLayoutItem(pendingFormHeaderId)],
        enabledHeaderIds: [...(Array.isArray(prev.enabledHeaderIds) ? prev.enabledHeaderIds : []), pendingFormHeaderId],
        repeatableHeaderIds: (Array.isArray(prev.repeatableHeaderIds) ? prev.repeatableHeaderIds : []).filter((headerId) => headerId !== pendingFormHeaderId),
        repeatableLayout: (Array.isArray(prev.repeatableLayout) ? prev.repeatableLayout : []).filter((item) => !(item.type === 'field' && item.headerId === pendingFormHeaderId)),
        repeatableRequiredHeaderIds: (Array.isArray(prev.repeatableRequiredHeaderIds) ? prev.repeatableRequiredHeaderIds : []).filter((headerId) => headerId !== pendingFormHeaderId),
        repeatableFieldWidths: normalizeSheetFormRepeatableFieldWidths(prev.repeatableFieldWidths, (Array.isArray(prev.repeatableHeaderIds) ? prev.repeatableHeaderIds : []).filter((headerId) => headerId !== pendingFormHeaderId))
      };
    });
  };

  const handleToggleRepeatableFormHeader = (headerId) => {
    setSheetFormConfig((prev) => {
      const repeatableHeaderIds = Array.isArray(prev.repeatableHeaderIds) ? prev.repeatableHeaderIds : [];
      const hasField = repeatableHeaderIds.includes(headerId);
      const nextRepeatable = hasField
        ? repeatableHeaderIds.filter((item) => item !== headerId)
        : [...repeatableHeaderIds, headerId];
      const currentRepeatableLayout = Array.isArray(prev.repeatableLayout) ? prev.repeatableLayout : [];
      const nextRepeatableLayout = hasField
        ? currentRepeatableLayout.filter((item) => !(item.type === 'field' && item.headerId === headerId))
        : [...currentRepeatableLayout, createSheetFormFieldLayoutItem(headerId)];

      return {
        ...prev,
        repeatableHeaderIds: nextRepeatable,
        repeatableLayout: nextRepeatableLayout,
        repeatableRequiredHeaderIds: (Array.isArray(prev.repeatableRequiredHeaderIds) ? prev.repeatableRequiredHeaderIds : []).filter((item) => nextRepeatable.includes(item)),
        repeatableFieldWidths: normalizeSheetFormRepeatableFieldWidths(prev.repeatableFieldWidths, nextRepeatable),
        formLayout: (Array.isArray(prev.formLayout) ? prev.formLayout : []).filter((item) => !(item.type === 'field' && item.headerId === headerId)),
        enabledHeaderIds: (Array.isArray(prev.enabledHeaderIds) ? prev.enabledHeaderIds : []).filter((item) => item !== headerId),
        requiredHeaderIds: (Array.isArray(prev.requiredHeaderIds) ? prev.requiredHeaderIds : []).filter((item) => item !== headerId)
      };
    });
  };

  const handleToggleRequiredRepeatableFormHeader = (headerId) => {
    setSheetFormConfig((prev) => {
      const repeatableHeaderIds = Array.isArray(prev.repeatableHeaderIds) ? prev.repeatableHeaderIds : [];
      if (!repeatableHeaderIds.includes(headerId)) {
        return prev;
      }

      const requiredHeaderIds = Array.isArray(prev.repeatableRequiredHeaderIds) ? prev.repeatableRequiredHeaderIds : [];
      return {
        ...prev,
        repeatableRequiredHeaderIds: requiredHeaderIds.includes(headerId)
          ? requiredHeaderIds.filter((item) => item !== headerId)
          : [...requiredHeaderIds, headerId]
      };
    });
  };

  const handleUpdateRepeatableFormFieldWidth = (headerId, width) => {
    setSheetFormConfig((prev) => {
      const repeatableHeaderIds = Array.isArray(prev.repeatableHeaderIds) ? prev.repeatableHeaderIds : [];
      if (!repeatableHeaderIds.includes(headerId)) return prev;
      return {
        ...prev,
        repeatableFieldWidths: normalizeSheetFormRepeatableFieldWidths(
          {
            ...(prev.repeatableFieldWidths || {}),
            [headerId]: width
          },
          repeatableHeaderIds
        ),
        repeatableLayout: (Array.isArray(prev.repeatableLayout) ? prev.repeatableLayout : []).map((item) => (
          item.type === 'field' && item.headerId === headerId
            ? { ...item, width: normalizeSheetFormFieldWidth(width) }
            : item
        ))
      };
    });
  };

  const handleAddRepeatableFormField = () => {
    if (!pendingRepeatableFormHeaderId) return;
    setSheetFormConfig((prev) => {
      const repeatableHeaderIds = Array.isArray(prev.repeatableHeaderIds) ? prev.repeatableHeaderIds : [];
      if (repeatableHeaderIds.includes(pendingRepeatableFormHeaderId)) {
        return prev;
      }

      return {
        ...prev,
        repeatableHeaderIds: [...repeatableHeaderIds, pendingRepeatableFormHeaderId],
        repeatableLayout: [
          ...(Array.isArray(prev.repeatableLayout) ? prev.repeatableLayout : []),
          createSheetFormFieldLayoutItem(pendingRepeatableFormHeaderId)
        ],
        repeatableFieldWidths: normalizeSheetFormRepeatableFieldWidths(prev.repeatableFieldWidths, [...repeatableHeaderIds, pendingRepeatableFormHeaderId]),
        formLayout: (Array.isArray(prev.formLayout) ? prev.formLayout : []).filter((item) => !(item.type === 'field' && item.headerId === pendingRepeatableFormHeaderId)),
        enabledHeaderIds: (Array.isArray(prev.enabledHeaderIds) ? prev.enabledHeaderIds : []).filter((item) => item !== pendingRepeatableFormHeaderId),
        requiredHeaderIds: (Array.isArray(prev.requiredHeaderIds) ? prev.requiredHeaderIds : []).filter((item) => item !== pendingRepeatableFormHeaderId)
      };
    });
  };

  const handleAddSectionDivider = () => {
    setSheetFormConfig((prev) => ({
      ...prev,
      formLayout: [...(Array.isArray(prev.formLayout) ? prev.formLayout : []), createSheetFormSectionDividerItem()]
    }));
  };

  const handleAddTextBlock = () => {
    setSheetFormConfig((prev) => ({
      ...prev,
      formLayout: [...(Array.isArray(prev.formLayout) ? prev.formLayout : []), createSheetFormTextBlockItem()]
    }));
  };

  const handleAddImageSlide = () => {
    setSheetFormConfig((prev) => ({
      ...prev,
      formLayout: [...(Array.isArray(prev.formLayout) ? prev.formLayout : []), createSheetFormImageSlideItem()]
    }));
  };

  const handleAddPageBreak = () => {
    setSheetFormConfig((prev) => ({
      ...prev,
      formLayout: [...(Array.isArray(prev.formLayout) ? prev.formLayout : []), createSheetFormPageBreakItem()]
    }));
  };

  const handleAddRepeatableSectionDivider = () => {
    setSheetFormConfig((prev) => ({
      ...prev,
      repeatableLayout: [...(Array.isArray(prev.repeatableLayout) ? prev.repeatableLayout : []), createSheetFormSectionDividerItem()]
    }));
  };

  const handleAddRepeatableTextBlock = () => {
    setSheetFormConfig((prev) => ({
      ...prev,
      repeatableLayout: [...(Array.isArray(prev.repeatableLayout) ? prev.repeatableLayout : []), createSheetFormTextBlockItem()]
    }));
  };

  const handleAddRepeatableImageSlide = () => {
    setSheetFormConfig((prev) => ({
      ...prev,
      repeatableLayout: [...(Array.isArray(prev.repeatableLayout) ? prev.repeatableLayout : []), createSheetFormImageSlideItem()]
    }));
  };

  const handleAddRepeatablePageBreak = () => {
    setSheetFormConfig((prev) => ({
      ...prev,
      repeatableLayout: [...(Array.isArray(prev.repeatableLayout) ? prev.repeatableLayout : []), createSheetFormPageBreakItem()]
    }));
  };

  const handleUploadFormSlideImages = useCallback(async (itemId, files) => {
    if (!user?.uid || !files?.length) {
      toast.error('Sign in before uploading slide images.');
      return;
    }

    const targetSheetId = id || formAssetScopeRef.current;

    try {
      setUploadingFormLayoutItemId(itemId);
      const uploadedSlides = await Promise.all(
        Array.from(files).map(async (file, index) => {
          const safeName = `${Date.now()}_${index}_${file.name || 'slide'}`.replace(/[^\w.-]+/g, '_');
          const slideRef = ref(storage, `sheets/${targetSheetId}/${user.uid}/form-slides/${safeName}`);
          const snapshot = await uploadBytes(slideRef, file);
          const url = await getDownloadURL(snapshot.ref);
          return {
            id: `slide_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            url,
            alt: file.name || `Slide ${index + 1}`
          };
        })
      );

      setSheetFormConfig((prev) => ({
        ...prev,
        formLayout: (Array.isArray(prev.formLayout) ? prev.formLayout : []).map((item) => (
          item.id === itemId && item.type === 'imageSlide'
            ? {
                ...item,
                slides: [...sanitizeSheetFormSlideItems(item.slides), ...uploadedSlides]
              }
            : item
        )),
        repeatableLayout: (Array.isArray(prev.repeatableLayout) ? prev.repeatableLayout : []).map((item) => (
          item.id === itemId && item.type === 'imageSlide'
            ? {
                ...item,
                slides: [...sanitizeSheetFormSlideItems(item.slides), ...uploadedSlides]
              }
            : item
        ))
      }));
      toast.success(`${uploadedSlides.length} slide image${uploadedSlides.length === 1 ? '' : 's'} uploaded.`);
    } catch (error) {
      toast.error(error?.message || 'Failed to upload slide images.');
    } finally {
      setUploadingFormLayoutItemId((current) => (current === itemId ? '' : current));
    }
  }, [id, user?.uid]);

  const handleSheetFormLogoUpload = useCallback(async (file) => {
    if (!file) return;
    if (!user?.uid) {
      toast.error('Sign in before uploading a form logo.');
      return;
    }

    if (!`${file.type || ''}`.startsWith('image/')) {
      toast.error('Choose an image file for the form logo.');
      return;
    }

    const targetSheetId = id || formAssetScopeRef.current;

    try {
      setUploadingFormLayoutItemId('form_logo');
      const safeName = `${Date.now()}_${file.name || 'form_logo'}`.replace(/[^\w.-]+/g, '_');
      const logoRef = ref(storage, `sheets/${targetSheetId}/${user.uid}/form-logo/${safeName}`);
      const snapshot = await uploadBytes(logoRef, file);
      const url = await getDownloadURL(snapshot.ref);
      setSheetFormConfig((prev) => ({ ...prev, logoUrl: url }));
      toast.success('Form logo uploaded.');
    } catch (error) {
      toast.error(error?.message || 'Failed to upload the form logo.');
    } finally {
      setUploadingFormLayoutItemId((current) => (current === 'form_logo' ? '' : current));
    }
  }, [id, user?.uid]);

  const handleCopySheetFormLink = async (formId) => {
    try {
      await navigator.clipboard.writeText(getSheetFormUrl(formId));
      toast.success('Form link copied.');
    } catch (error) {
      toast.error('Failed to copy the form link.');
    }
  };

  const handleSave = async () => {
    const cleanTitle = title.trim();
    if (!cleanTitle) {
      toast.error('Sheet title is required.');
      return;
    }
    if (!sanitizedHeaders.length) {
      toast.error('Add at least one header.');
      return;
    }
    const cleanTitleKey = getSheetTitleKey(cleanTitle);
    const hasDuplicateTitle = availableSheets.some((sheetItem) => (
      sheetItem?.id !== id && getSheetTitleKey(sheetItem?.title) === cleanTitleKey
    ));
    if (hasDuplicateTitle) {
      toast.error(`A sheet named "${cleanTitle}" already exists.`);
      return;
    }

    setSaving(true);
    try {
      let effectiveWorkspaceId = currentWorkspaceId;
      let effectiveWorkspaceTitle = workspaceTitle;
      const cleanWorkspaceTitle = newWorkspaceTitle.trim();
      if (!effectiveWorkspaceId && !isEditing && cleanWorkspaceTitle) {
        setCreatingWorkspace(true);
        const workspace = await sheetWorkspaceService.createWorkspace({
          title: cleanWorkspaceTitle,
          description: newWorkspaceDescription.trim(),
          organizationIds
        }, user?.uid);
        effectiveWorkspaceId = workspace.id;
        effectiveWorkspaceTitle = workspace.title || '';
        setAvailableWorkspaces((prev) => [workspace, ...prev]);
        setWorkspaceId(workspace.id);
        setWorkspaceTitle(workspace.title || '');
        setNewWorkspaceTitle('');
        setNewWorkspaceDescription('');
      }

      const selectedWorkspace = availableWorkspaces.find((workspace) => workspace.id === effectiveWorkspaceId);
      const workspaceOrganizationIds = Array.isArray(selectedWorkspace?.organizationIds)
        ? selectedWorkspace.organizationIds
        : (!isEditing && cleanWorkspaceTitle ? organizationIds : []);
      if (effectiveWorkspaceId && organizationIds.some((organizationId) => !workspaceOrganizationIds.includes(organizationId))) {
        throw new Error('Assign the selected organisations to this workspace before assigning them to the sheet.');
      }

      const effectiveVisibility = organizationIds.length ? 'restricted' : visibility;

      const allowedEmails = effectiveVisibility === 'restricted'
        ? parseEmailList(allowedEmailsInput)
        : [];
      const sanitizedActionForms = sanitizeActionFormsForSave(actionForms, workspaceScopedAvailableSheets, id, headers);

      if (effectiveVisibility === 'restricted' && !allowedEmails.length && !organizationIds.length) {
        throw new Error('Add at least one valid email for restricted access.');
      }

      const payload = {
        documentId: id,
        workspaceId: effectiveWorkspaceId,
        workspaceTitle: effectiveWorkspaceTitle,
        workspaceOnly: Boolean(effectiveWorkspaceId) || workspaceOnly,
        organizationIds,
        title: cleanTitle,
        description: description.trim(),
        adminBuilderAccessEnabled,
        customLogSheetId: isLogSheet ? '' : customLogSheetId,
        actionForms: sanitizedActionForms,
        renderPreset: sheetRenderPreset,
        primaryColor: sheetPrimaryColor,
        secondaryColor: sheetSecondaryColor,
        renderFont: sheetRenderFont,
        ogImageUrl: sheetOgImageUrl,
        renderPresetConfig: sanitizedSheetRenderPresetConfig,
        isLogSheet,
        visibility: effectiveVisibility,
        allowedEmails,
        allowedUserIds: effectiveVisibility === 'restricted' ? selectedSharedUserIds : [],
        collaboratorAccess: effectiveVisibility === 'restricted' ? collaboratorAccess : 'view',
        headers: effectiveSanitizedHeaders,
        filterHeaderIds: sanitizedFilterHeaders,
        analytics: sanitizeAnalytics(analytics, effectiveSanitizedHeaders),
        charts: [],
        aiSkills: {
          report: sanitizeReportSkillForSave(reportSkill, effectiveSanitizedHeaders)
        },
        aiAutomations: sanitizeAutomationsForSave(automations, effectiveSanitizedHeaders)
      };

      const savedSheet = await runSaveStep('Sheet document save', () => (
        sheetService.saveSheet(payload, user?.uid)
      ));
      await runSaveStep('Sheet publish snapshot write', () => (
        sheetService.publishSheet(savedSheet.id, payload, user?.uid)
      ));
      const sheetSnapshotForForm = {
        ...savedSheet,
        ...payload,
        id: savedSheet.id,
        title: payload.title,
        description: payload.description,
        headers: effectiveSanitizedHeaders
      };

      if (sheetFormConfig.enabled) {
        await runSaveStep('Sheet form publish', () => (
          sheetFormService.saveSheetForm(savedSheet.id, sheetSnapshotForForm, sheetFormConfig, user?.uid)
        ));
        setHadExistingSheetForm(true);
      } else if (isEditing && hadExistingSheetForm) {
        try {
          await runSaveStep('Sheet form disable', () => (
            sheetFormService.deleteSheetForm(savedSheet.id, user?.uid).catch((error) => {
              const message = `${error?.message || ''}`.toLowerCase();
              if (message.includes('not found')) return null;
              throw error;
            })
          ));
        } catch (error) {
          const message = `${error?.message || ''}`.toLowerCase();
          const isLegacyFormPermissionIssue = error?.code === 'permission-denied'
            || (
              message.includes('missing or insufficient permissions')
              && message.includes('sheet form disable failed')
            );

          if (isLegacyFormPermissionIssue) {
            console.warn('Sheet form disable was skipped because the existing form document could not be deleted under current rules.', {
              sheetId: savedSheet.id,
              userId: user?.uid,
              error
            });
            toast.error('Sheet saved, but an older linked form could not be removed automatically.');
          } else {
            throw error;
          }
        }
      }

      if (!isEditing) {
        await runSaveStep('Initial sheet row create', () => (
          sheetService.saveSheetRow(savedSheet.id, {
            values: buildInitialRowValues(sanitizedHeaders, firstRowValues)
          }, user?.uid)
        ));
      }

      toast.success(isEditing ? 'Sheet updated.' : 'Sheet created.');
      const resolvedWorkspaceSlug = effectiveWorkspaceId
        ? availableWorkspaces.find((workspace) => workspace.id === effectiveWorkspaceId)?.slug || workspaceSlug
        : '';
      navigate(getSheetPath(savedSheet.id, resolvedWorkspaceSlug));
    } catch (err) {
      console.error('Sheet save flow failed', {
        sheetId: id,
        isEditing,
        userId: user?.uid,
        workspaceId: currentWorkspaceId,
        visibility,
        collaboratorAccess,
        saveStep: err?.saveStep || 'Unknown save step',
        errorCode: err?.code || '',
        errorMessage: err?.message || 'Failed to save sheet.',
        error: err
      });
      toast.error(err?.message || 'Failed to save sheet.');
    } finally {
      setCreatingWorkspace(false);
      setSaving(false);
    }
  };

  const isPlatformAdminMaintainer = Boolean(
    isEditing &&
    sheetOwnerId &&
    sheetOwnerId !== user?.uid &&
    `${userProfile?.role || ''}`.trim() === 'admin'
  );

  const handleDownloadCsvTemplate = useCallback(() => {
    if (!csvColumns.length) {
      toast.error('Add at least one importable column before downloading a CSV template.');
      return;
    }

    const template = buildSheetCsvTemplate(sanitizedHeaders);
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeTitle = `${title || 'sheet'}`
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'sheet';

    link.href = url;
    link.download = `${safeTitle}-template.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success('CSV template downloaded.');
  }, [csvColumns.length, sanitizedHeaders, title]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
        Loading sheet builder...
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 gap-6">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-6">
        <header className="border-b border-slate-200 bg-white/80 px-6 py-0 backdrop-blur-sm lg:px-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 flex-1 gap-5">
              <div className="flex min-w-0 flex-1 flex-col gap-4">
                <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start">
                  <div className="flex min-w-0 items-start gap-3 lg:max-w-[48%]">
                    <button
                      type="button"
                      onClick={handleReturnNavigation}
                      aria-label="Back to sheets"
                      className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div className="min-w-0 space-y-3">
                      <h1 className="truncate text-3xl font-extrabold tracking-tight text-slate-900">
                        {isEditing ? 'Edit Smart Sheet' : 'Create Smart Sheet'}
                      </h1>
                      <div className="inline-flex items-center gap-2 rounded-md border border-[#008080]/20 bg-[#008080]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#0f4f4f]">
                        <TableProperties className="h-3.5 w-3.5" />
                        Smart Sheet Builder
                      </div>
                    </div>
                  </div>

                  <div className="hidden self-stretch border-l border-slate-200 lg:block" />

                  <div className="min-w-0 flex-1 space-y-3 pt-0">
                    <p className="text-sm font-medium text-slate-500">
                      Define the headers once. The sheet starts with one row and each saved row becomes a document in `sheet-items`.
                    </p>
                    <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-slate-400">
                      {sanitizedHeaders.length} configured columns <span className="mx-2">|</span> {sanitizedFilterHeaders.length} filter columns <span className="mx-2">|</span> {visibility} <span className="mx-2">|</span> {isEditing ? 'editing existing sheet' : 'new sheet draft'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 rounded-none bg-white/80 p-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl bg-[#008080] px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0f6d6d] disabled:cursor-not-allowed disabled:opacity-70"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save Sheet'}
              </button>
              {creatingWorkspace && (
                <span className="text-xs font-medium text-slate-500">Creating workspace...</span>
              )}
            </div>
          </div>
        </header>

        <section className="flex min-h-0 max-w-5xl mx-auto flex-1 flex-col px-6 pb-2 lg:px-10">
          <div className="mb-6">
            <div className="mb-4 inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
              {[
                { id: 'setup', label: 'Sheet Setup' },
                 { id: 'headers', label: 'Headers' },
                { id: 'actions', label: 'Actions' },
                { id: 'forms', label: 'Forms' },
                { id: 'ai', label: 'AI Skills' },
                { id: 'analytics', label: 'Analytics' }
              ].map((panel) => {
                const isActive = activeBuilderPanel === panel.id;
                return (
                  <button
                    key={panel.id}
                    type="button"
                    onClick={() => setActiveBuilderPanel(panel.id)}
                    className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                      isActive
                        ? 'bg-slate-900 text-white'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    {panel.label}
                  </button>
                );
              })}
            </div>

            {activeBuilderPanel === 'setup' && (
            <section className="rounded-3xl border-2 border-slate-300 bg-slate-50/55 p-6 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.32)]">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Sheet Setup</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Configure the title, access level, and sharing rules before you publish.
                </p>
              </div>

              <div className="mt-5 space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Title</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Client intake tracker"
                    className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    placeholder="Optional context for people opening the sheet."
                    className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                  />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Admin builder access</label>
                      <p className="mt-1 text-xs text-slate-500">
                        Let platform admins open this sheet from the admin access section for builder-only maintenance. This does not grant access to rows or rendered views.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAdminBuilderAccessEnabled((prev) => !prev)}
                      disabled={isPlatformAdminMaintainer && adminBuilderAccessEnabled}
                      className={`inline-flex h-7 w-12 items-center rounded-full border transition ${
                        adminBuilderAccessEnabled
                          ? 'border-[#008080] bg-[#008080]'
                          : 'border-slate-300 bg-slate-200'
                      } disabled:cursor-not-allowed disabled:opacity-70`}
                      aria-pressed={adminBuilderAccessEnabled}
                      title={isPlatformAdminMaintainer && adminBuilderAccessEnabled
                        ? 'Only the sheet owner can disable admin builder access.'
                        : undefined}
                    >
                      <span
                        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition ${
                          adminBuilderAccessEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Sheet render styling</label>
                      <p className="mt-1 text-xs text-slate-500">
                        Save reusable colors and a font choice on the sheet for rendered layouts, presets, and forms.
                      </p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      Optional
                    </span>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">Primary color</label>
                      <input
                        value={sheetPrimaryColor}
                        onChange={(e) => setSheetPrimaryColor(e.target.value)}
                        placeholder="008080 or #008080"
                        className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">Secondary color</label>
                      <input
                        value={sheetSecondaryColor}
                        onChange={(e) => setSheetSecondaryColor(e.target.value)}
                        placeholder="2563eb or #2563eb"
                        className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">Render font</label>
                      <select
                        value={normalizeSheetRenderFont(sheetRenderFont)}
                        onChange={(e) => setSheetRenderFont(normalizeSheetRenderFont(e.target.value))}
                        className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                      >
                        {SHEET_RENDER_FONT_OPTIONS.map((option) => (
                          <option key={`sheet_render_font_${option.value}`} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">
                    Accepts 3 or 6 digit hex with or without #. Invalid values are ignored when the sheet is saved.
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Share image URL</label>
                      <p className="mt-1 text-xs text-slate-500">
                        Optional OG image used when the sheet link is shared. Leave blank to use the default ParrotSheets image.
                      </p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      Optional
                    </span>
                  </div>

                  <div className="mt-4">
                    <label className="mb-2 block text-sm font-medium text-slate-700">OG image URL</label>
                    <input
                      value={sheetOgImageUrl}
                      onChange={(e) => setSheetOgImageUrl(e.target.value)}
                      placeholder="https://storage.googleapis.com/.../sheet-og.png"
                      className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                    />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <label className="inline-flex cursor-pointer items-center rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-[#008080]/35 hover:text-[#008080]">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const [file] = Array.from(e.target.files || []);
                          handleSheetOgImageUpload(file);
                          e.target.value = '';
                        }}
                      />
                      {uploadingOgImage ? 'Uploading image...' : 'Upload image'}
                    </label>
                    {sheetOgImageUrl ? (
                      <>
                        <a
                          href={sheetOgImageUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium text-[#008080] hover:text-[#006d6d]"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Preview image
                        </a>
                        <button
                          type="button"
                          onClick={() => setSheetOgImageUrl('')}
                          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                        >
                          <X className="h-4 w-4" />
                          Clear
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Viewer preset render mode</label>
                      <p className="mt-1 text-xs text-slate-500">
                        Leave this on default to keep the standard sheet rows UI. Presets only apply in rendered view and fall back to forms in edit mode.
                      </p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      Optional
                    </span>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {SHEET_RENDER_PRESET_OPTIONS.map((option) => (
                      <label
                        key={option.value}
                        className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 text-sm transition ${
                          sheetRenderPreset === option.value
                            ? 'border-[#008080]/25 bg-[#008080]/10 text-[#0f4f4f]'
                            : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-[#008080]/20 hover:bg-[#008080]/5'
                        }`}
                      >
                        <input
                          type="radio"
                          name="sheet-render-preset"
                          value={option.value}
                          checked={sheetRenderPreset === option.value}
                          onChange={(e) => setSheetRenderPreset(normalizeSheetRenderPreset(e.target.value))}
                          className="mt-0.5 h-4 w-4 border-slate-300 text-[#008080] focus:ring-[#008080]"
                        />
                        <span>
                          <span className="block font-semibold text-slate-900">{option.label}</span>
                          <span className="mt-1 block text-xs text-slate-500">
                            {option.value === 'default'
                              ? 'Spreadsheet rows stay visible in rendered mode.'
                              : option.value === 'profiles'
                                ? 'Rows become profile cards and the row sidebar uses a profile detail layout.'
                                : option.value === 'list-card'
                                  ? 'Rows become list cards with an image, title, body text, and tag pills.'
                                  : option.value === 'kanban'
                                    ? 'Rows group into status columns for task and workflow tracking.'
                                    : 'Rows appear on a month calendar for bookings, events, and schedules.'}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>

                  {sheetRenderPreset === 'profiles' && (
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Primary profile column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.profileHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, profileHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a profile column</option>
                          {headers
                            .filter((header) => header.type === 'profile' && `${header.label || ''}`.trim())
                            .map((header) => (
                              <option key={`render_preset_${header.id}`} value={header.id}>
                                {header.label}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Body column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.bodyHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, bodyHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a body column</option>
                          {headers.map((header) => (
                            <option key={`render_preset_profile_body_${header.id}`} value={header.id}>
                              {header.label}
                            </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">UI columns</label>
                        <select
                          value={normalizePresetUiColumns(sanitizedSheetRenderPresetConfig.uiColumns, 3)}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, uiColumns: normalizePresetUiColumns(e.target.value, 3) }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value={1}>1 column</option>
                          <option value={2}>2 columns</option>
                          <option value={3}>3 columns</option>
                          <option value={4}>4 columns</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Action column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.actionHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, actionHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose an action column</option>
                          {headers.map((header) => (
                            <option key={`render_preset_profile_action_${header.id}`} value={header.id}>
                              {header.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-xs text-slate-500">
                        Add a `profile` column in Headers first if this list is empty.
                      </div>
                    </div>
                  )}

                  {sheetRenderPreset === 'list-card' && (
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Image column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.imageHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, imageHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a column</option>
                          {headers.map((header) => (
                            <option key={`render_preset_list_image_${header.id}`} value={header.id}>
                              {header.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Category or type column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.categoryHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, categoryHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a column</option>
                          {headers
                            .filter((header) => !['multiselect', 'list', 'image', 'textarea'].includes(header.type))
                            .map((header) => (
                              <option key={`render_preset_list_category_${header.id}`} value={header.id}>
                                {header.label}
                              </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Image layout</label>
                        <select
                          value={normalizeListCardImageLayout(sanitizedSheetRenderPresetConfig.imageLayout)}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, imageLayout: normalizeListCardImageLayout(e.target.value) }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="inline">Inline with title</option>
                          <option value="full-width">Full width image, title above</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">UI columns</label>
                        <select
                          value={normalizePresetUiColumns(sanitizedSheetRenderPresetConfig.uiColumns, 3)}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, uiColumns: normalizePresetUiColumns(e.target.value, 3) }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value={1}>1 column</option>
                          <option value={2}>2 columns</option>
                          <option value={3}>3 columns</option>
                          <option value={4}>4 columns</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Title column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.titleHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, titleHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a column</option>
                          {headers.map((header) => (
                            <option key={`render_preset_list_title_${header.id}`} value={header.id}>
                              {header.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Body text column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.bodyHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, bodyHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a column</option>
                          {headers.map((header) => (
                            <option key={`render_preset_list_body_${header.id}`} value={header.id}>
                              {header.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Tag items column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.tagHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, tagHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a `list` or `multiselect` column</option>
                          {headers
                            .filter((header) => header.type === 'list' || header.type === 'multiselect')
                            .map((header) => (
                              <option key={`render_preset_list_tag_${header.id}`} value={header.id}>
                                {header.label}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {sheetRenderPreset === 'kanban' && (
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Status column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.statusHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, statusHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a status column</option>
                          {headers
                            .filter((header) => ['select', 'buttons', 'yesno', 'rog', 'text'].includes(header.type))
                            .map((header) => (
                              <option key={`render_preset_kanban_status_${header.id}`} value={header.id}>
                                {header.label}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Title column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.titleHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, titleHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a title column</option>
                          {headers.map((header) => (
                            <option key={`render_preset_kanban_title_${header.id}`} value={header.id}>
                              {header.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Body column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.bodyHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, bodyHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a body column</option>
                          {headers.map((header) => (
                            <option key={`render_preset_kanban_body_${header.id}`} value={header.id}>
                              {header.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">UI columns</label>
                        <select
                          value={normalizePresetUiColumns(sanitizedSheetRenderPresetConfig.uiColumns, 3)}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, uiColumns: normalizePresetUiColumns(e.target.value, 3) }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value={1}>1 column</option>
                          <option value={2}>2 columns</option>
                          <option value={3}>3 columns</option>
                          <option value={4}>4 columns</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Meta column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.metaHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, metaHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a meta column</option>
                          {headers.map((header) => (
                            <option key={`render_preset_kanban_meta_${header.id}`} value={header.id}>
                              {header.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {sheetRenderPreset === 'calendar' && (
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Image column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.imageHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, imageHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose an image column</option>
                          {headers.map((header) => (
                            <option key={`render_preset_calendar_image_${header.id}`} value={header.id}>
                              {header.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Date column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.dateHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, dateHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a date column</option>
                          {headers
                            .filter((header) => header.type === 'date' || header.type === 'datetime')
                            .map((header) => (
                              <option key={`render_preset_calendar_date_${header.id}`} value={header.id}>
                                {header.label}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Title column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.titleHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, titleHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a title column</option>
                          {headers.map((header) => (
                            <option key={`render_preset_calendar_title_${header.id}`} value={header.id}>
                              {header.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Meta column</label>
                        <select
                          value={sanitizedSheetRenderPresetConfig.metaHeaderId || ''}
                          onChange={(e) => setSheetRenderPresetConfig((prev) => ({ ...prev, metaHeaderId: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Choose a meta column</option>
                          {headers.map((header) => (
                            <option key={`render_preset_calendar_meta_${header.id}`} value={header.id}>
                              {header.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-[#008080]/20 bg-[#008080]/5 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-900">AI-assisted sheet creation</label>
                      <p className="mt-1 text-xs text-slate-600">
                        Turn this on only when you want the AI to scaffold a draft from an operational prompt and reference sheets.
                      </p>
                    </div>
                    <label className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm font-semibold text-slate-700">
                      <input
                        type="checkbox"
                        checked={showAiSheetAssistant}
                        onChange={(e) => setShowAiSheetAssistant(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                      />
                      Enable
                    </label>
                  </div>

                  {showAiSheetAssistant && (
                    <>
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={handleGenerateBlueprint}
                          disabled={generatingBlueprint}
                          className="inline-flex items-center gap-2 rounded-xl bg-[#008080] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#006d6d] disabled:cursor-not-allowed disabled:opacity-70"
                        >
                          <Sparkles className={`h-4 w-4 ${generatingBlueprint ? 'animate-pulse' : ''}`} />
                          {generatingBlueprint ? 'Generating...' : 'Generate Draft'}
                        </button>
                      </div>

                      <div className="mt-4">
                        <textarea
                          value={sheetIdeaPrompt}
                          onChange={(e) => setSheetIdeaPrompt(e.target.value)}
                          rows={5}
                          placeholder="Example: Build a client follow-up sheet for admissions. Track contact details, stage, next contact date, call outcomes, and weekly manager summaries."
                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        />
                      </div>

                      <div className="mt-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Reference sheets</p>
                        <div className="mt-2 grid gap-2 md:grid-cols-2">
                          {workspaceScopedAvailableSheets.slice(0, 8).length ? workspaceScopedAvailableSheets.slice(0, 8).map((sheetItem) => (
                            <label key={`blueprint_ref_${sheetItem.id}`} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700">
                              <input
                                type="checkbox"
                                checked={selectedReferenceSheetIds.includes(sheetItem.id)}
                                onChange={() => toggleReferenceSheet(sheetItem.id)}
                                className="mt-1 h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                              />
                              <span>
                                <span className="block font-semibold text-slate-900">{sheetItem.title || 'Untitled sheet'}</span>
                                <span className="mt-1 block text-xs text-slate-500">
                                  {(sheetItem.headers?.length || 0)} columns{sheetItem.isLogSheet ? ' â€¢ log sheet' : ''}
                                </span>
                              </span>
                            </label>
                          )) : (
                            <div className="rounded-xl border border-dashed border-slate-300 px-4 py-5 text-sm text-slate-500">
                              No accessible sheets available yet for reference.
                            </div>
                          )}
                        </div>
                      </div>

                      {blueprintSummary ? (
                        <div className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">AI Reference Notes</p>
                          <p className="mt-2">{blueprintSummary}</p>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Workspace</label>
                      <p className="mt-1 text-xs text-slate-500">
                        The workspace slug becomes the unique URL path and defines the boundary for sheet references and shared logs.
                      </p>
                    </div>
                    {workspaceId && (
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                        {availableWorkspaces.find((workspace) => workspace.id === workspaceId)?.slug || 'workspace'}
                      </span>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                      {workspaceTitle || 'Legacy sheet without an assigned workspace'}
                    </div>
                  ) : (
                    <>
                      <select
                        value={workspaceId}
                        onChange={(e) => {
                          const nextWorkspace = availableWorkspaces.find((workspace) => workspace.id === e.target.value) || null;
                          setWorkspaceId(e.target.value);
                          setWorkspaceTitle(nextWorkspace?.title || '');
                          const nextWorkspaceOrganizationIds = Array.isArray(nextWorkspace?.organizationIds)
                            ? nextWorkspace.organizationIds
                            : [];
                          setOrganizationIds((current) => current.filter((organizationId) => (
                            !nextWorkspace || nextWorkspaceOrganizationIds.includes(organizationId)
                          )));
                        }}
                        className="mt-3 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                      >
                        <option value="">Create a new workspace below</option>
                        {availableWorkspaces.map((workspace) => (
                          <option key={`workspace_${workspace.id}`} value={workspace.id}>
                            {workspace.title} ({workspace.slug})
                          </option>
                        ))}
                      </select>

                      {!workspaceId && (
                        <div className="mt-4 grid gap-4 md:grid-cols-2">
                          <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700">New Workspace Name</label>
                            <input
                              value={newWorkspaceTitle}
                              onChange={(e) => setNewWorkspaceTitle(e.target.value)}
                              placeholder="Client Ops"
                              className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700">New Workspace Description</label>
                            <input
                              value={newWorkspaceDescription}
                              onChange={(e) => setNewWorkspaceDescription(e.target.value)}
                              placeholder="Optional workspace summary"
                              className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Organisation access</label>
                      <p className="mt-1 text-xs text-slate-500">
                        Opt in to a hard organisation boundary. Approved members use the collaborator access mode below, while ownership stays unchanged.
                      </p>
                    </div>
                    {userProfile?.activeOrganizationId && !organizationIds.length && (
                      <span className="rounded-full bg-[#008080]/10 px-3 py-1 text-xs font-semibold text-[#008080]">Active organisation available</span>
                    )}
                  </div>
                  {organizationOptions.length ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {organizationOptions.map((organization) => {
                        const checked = organizationIds.includes(organization.id);
                        const isActive = userProfile?.activeOrganizationId === organization.id;
                        return (
                          <label key={`sheet_org_${organization.id}`} className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-sm ${checked ? 'border-[#008080] bg-[#008080]/10 text-[#0f4f4f]' : 'border-slate-200 bg-slate-50 text-slate-700'}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                setOrganizationIds((current) => checked
                                  ? current.filter((idValue) => idValue !== organization.id)
                                  : [...current, organization.id]);
                                if (!checked) setVisibility('restricted');
                              }}
                              className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                            />
                            <span className="font-semibold">{organization.name}</span>
                            {isActive && <span className="text-[10px] font-bold uppercase text-[#008080]">Active</span>}
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="mt-3 rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-500">
                      {currentWorkspaceId ? 'Assign your organisations to this workspace first.' : 'No approved organisation memberships are available.'}
                    </p>
                  )}
                  {organizationIds.length > 0 && (
                    <p className="mt-3 text-xs font-semibold text-amber-700">Public links and shares outside the selected organisations will not grant sheet access.</p>
                  )}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Visibility</label>
                    <select
                      value={organizationIds.length ? 'restricted' : visibility}
                      onChange={(e) => setVisibility(e.target.value)}
                      disabled={organizationIds.length > 0}
                      className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100"
                    >
                      <option value="private">Private</option>
                      <option value="public">Public</option>
                      <option value="restricted">Restricted</option>
                    </select>
                  </div>

                  {visibility === 'restricted' && (
                    <>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Allowed Emails</label>
                        <input
                          value={allowedEmailsInput}
                          onChange={(e) => setAllowedEmailsInput(e.target.value)}
                          placeholder="name@example.com, second@example.com"
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        />
                        {resolvedSharedUsers.length > 0 && (
                          <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3">
                            <p className="text-[11px] font-semibold tracking-wide text-slate-500">Matched Signed-In Users</p>
                            <div className="mt-2 space-y-2">
                              {resolvedSharedUsers.map((sharedUser) => (
                                <label
                                  key={sharedUser.uid}
                                  className="flex items-start gap-3 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedSharedUserIds.includes(sharedUser.uid)}
                                    onChange={() => handleToggleSharedUser(sharedUser.uid)}
                                    className="mt-1 h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                                  />
                                  <span>
                                    <span className="block font-semibold text-slate-800">
                                      {sharedUser.displayName || sharedUser.email}
                                    </span>
                                    <span className="block text-xs text-slate-500">
                                      {sharedUser.email} â€¢ {sharedUser.uid}
                                    </span>
                                  </span>
                                </label>
                              ))}
                            </div>
                            <p className="mt-2 text-xs text-slate-500">
                              {organizationIds.length
                                ? 'Checked users are accepted only when they belong to one of the selected organisations. External emails cannot bypass organisation access.'
                                : 'Checked users are linked by UID as explicit share targets. Unmatched emails remain as email-based invite access.'}
                            </p>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Restricted Access</label>
                        <select
                          value={collaboratorAccess}
                          onChange={(e) => setCollaboratorAccess(e.target.value)}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="view">View only</option>
                          <option value="edit_own_rows">Can edit own rows only</option>
                          <option value="view_all_edit_own">View all rows, edit own</option>
                          <option value="edit">View, edit sheet (no delete)</option>
                        </select>
                        <p className="mt-2 text-xs text-slate-500">
                          `Can edit own rows only` keeps collaborator rows isolated. `View all rows, edit own` uses the main sheet rows, but collaborators can only change rows they created. `View, edit sheet (no delete)` allows collaborators to work across the sheet without deleting the sheet itself.
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <label className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isLogSheet}
                        onChange={(e) => {
                          setIsLogSheet(e.target.checked);
                          if (e.target.checked) {
                            setCustomLogSheetId('');
                          }
                        }}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-800">This is a log sheet</span>
                        <span className="mt-1 block text-xs text-slate-500">
                          Required system columns are created and hidden automatically. The log form will control scope and target selection.
                        </span>
                      </span>
                    </label>
                  </div>
                  <label className="block text-sm font-medium text-slate-700">Custom Log Sheet</label>
                  <p className="mt-1 text-xs text-slate-500">
                    Pick one sheet to store sheet, column, and row logs. Leave empty to keep using the default built-in log form.
                  </p>
                  <select
                    value={customLogSheetId}
                    onChange={(e) => setCustomLogSheetId(e.target.value)}
                    disabled={isLogSheet}
                    className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    <option value="">Use default log form</option>
                    {workspaceScopedAvailableSheets
                      .filter((sheetItem) => sheetItem.id !== id && sheetItem.userId === user?.uid && Boolean(sheetItem.isLogSheet))
                      .map((sheetItem) => (
                        <option key={`log_sheet_${sheetItem.id}`} value={sheetItem.id}>
                          {sheetItem.title || 'Untitled Sheet'}
                        </option>
                      ))}
                  </select>
                  {isLogSheet && (
                    <p className="mt-2 text-xs text-slate-500">
                      Log sheets cannot themselves attach another custom log sheet.
                    </p>
                  )}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Filter Columns</label>
                      <p className="mt-1 text-xs text-slate-500">
                        Choose which headers should appear as row filters in the sheet viewer. Every selected column renders as a dropdown populated from its distinct row values.
                      </p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      {sanitizedFilterHeaders.length} selected
                    </span>
                  </div>

                  {sanitizedHeaders.length === 0 ? (
                    <p className="mt-3 text-sm text-slate-400">
                      Add and name at least one header before choosing filter columns.
                    </p>
                  ) : (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {sanitizedHeaders.map((header, index) => (
                        <label
                          key={`${header.id}_filter`}
                          className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-sm transition ${
                            sanitizedFilterHeaders.includes(header.id)
                              ? 'border-[#008080]/25 bg-[#008080]/10 text-[#0f4f4f]'
                              : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-[#008080]/25 hover:bg-[#008080]/10'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={sanitizedFilterHeaders.includes(header.id)}
                            onChange={() => handleToggleFilterHeader(header.id)}
                            className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                          />
                          <span className="font-semibold">{header.label}</span>
                          <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                            {getColumnLetter(index)}
                          </span>
                          <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#008080]">
                            Dropdown
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>
            )}
            {activeBuilderPanel === 'actions' && (
            <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.32)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Actions</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Add linked-form actions or direct row-update actions for the row smart-chip popup.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setActionForms((prev) => [...prev, createActionFormDraft()])}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  <Plus className="h-4 w-4" />
                  Add action
                </button>
              </div>

              <div className="mt-5 space-y-4">
                {actionForms.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    No actions configured yet.
                  </div>
                ) : actionForms.map((actionForm, index) => {
                  const actionMode = normalizeActionConfigMode(actionForm?.mode);
                  const targetHeaders = getActionTargetHeaders(actionForm.targetSheetId);

                  return (
                    <div key={actionForm.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Action {index + 1}</p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">
                            {actionForm.name || actionForm.buttonLabel || (actionMode === 'update_rows' ? 'Update rows action' : 'Linked form action')}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => moveActionFormDraft(actionForm.id, 'up')}
                            disabled={index === 0}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-white hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
                            aria-label="Move action up"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveActionFormDraft(actionForm.id, 'down')}
                            disabled={index === actionForms.length - 1}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-white hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
                            aria-label="Move action down"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => duplicateActionFormDraft(actionForm.id)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-white hover:text-slate-900"
                            aria-label="Duplicate action"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeActionFormDraft(actionForm.id)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-white hover:text-rose-600"
                            aria-label="Remove action"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Action type</label>
                          <select
                            value={actionMode}
                            onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({
                              ...item,
                              mode: normalizeActionConfigMode(e.target.value),
                              targetSheetId: '',
                              fieldMappings: [],
                              conditions: [],
                              updates: []
                            }))}
                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            {ACTION_CONFIG_MODE_OPTIONS.map((option) => (
                                  <option key={`${actionForm.id}_mode_${option.value}`} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Button label</label>
                          <input
                            type="text"
                            value={actionForm.buttonLabel}
                            onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, buttonLabel: e.target.value }))}
                            placeholder={actionMode === 'update_rows' ? 'Mark complete' : 'Booking'}
                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{actionMode === 'update_rows' ? 'Action title' : 'Sidebar title'}</label>
                          <input
                            type="text"
                            value={actionForm.name}
                            onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, name: e.target.value }))}
                            placeholder={actionMode === 'update_rows' ? 'Approve matching rows' : 'Booking form'}
                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Description</label>
                          <textarea
                            rows={3}
                            value={actionForm.description}
                            onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, description: e.target.value }))}
                            placeholder="Explain what this action does."
                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          />
                        </div>
                      </div>

                      {actionMode === 'linked_form' ? (
                        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Add row style</label>
                              <select
                                value={actionForm.type}
                                onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, type: normalizeActionFormType(e.target.value) }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                              >
                                {ACTION_FORM_TYPE_OPTIONS.map((option) => (
                                  <option key={`${actionForm.id}_type_${option.value}`} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Action behavior</label>
                              <select
                                value={normalizeActionLinkedFormBehavior(actionForm.linkedFormBehavior)}
                                onChange={(e) => updateActionFormDraft(actionForm.id, (item) => {
                                  const nextBehavior = normalizeActionLinkedFormBehavior(e.target.value);
                                  return {
                                    ...item,
                                    linkedFormBehavior: nextBehavior,
                                    allowTargetRowSelection: nextBehavior === 'open_form' ? item.allowTargetRowSelection === true : false
                                  };
                                })}
                                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                              >
                                {ACTION_LINKED_FORM_BEHAVIOR_OPTIONS.map((option) => (
                                  <option key={`${actionForm.id}_behavior_${option.value}`} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Target sheet</label>
                              <select
                                value={actionForm.targetSheetId}
                                onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, targetSheetId: e.target.value, fieldMappings: [] }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                              >
                                <option value="">Select target sheet</option>
                                {workspaceScopedAvailableSheets.filter((sheetItem) => sheetItem.id !== id && !sheetItem.isLogSheet).map((sheetItem) => (
                                  <option key={`${actionForm.id}_sheet_${sheetItem.id}`} value={sheetItem.id}>{sheetItem.title || 'Untitled Sheet'}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Submit label</label>
                              <input
                                type="text"
                                value={actionForm.submitLabel}
                                onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, submitLabel: e.target.value }))}
                                placeholder="Save booking"
                                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                              />
                            </div>
                          </div>
                          {normalizeActionLinkedFormBehavior(actionForm.linkedFormBehavior) === 'open_form' ? (
                            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                              <label className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  checked={actionForm.allowTargetRowSelection === true}
                                  onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({
                                    ...item,
                                    allowTargetRowSelection: e.target.checked
                                  }))}
                                  className="mt-1 h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                                />
                                <span>
                                  <span className="block text-sm font-semibold text-slate-800">Allow selecting a target row to prefill from</span>
                                  <span className="mt-1 block text-xs text-slate-500">
                                    Opens the published sheet form page with a row selector at the top. The selector uses the target sheet&apos;s label column and copies values from the selected row into the new row being created.
                                  </span>
                                </span>
                              </label>
                            </div>
                          ) : (
                            <div className="mt-4 rounded-2xl border border-slate-200 bg-emerald-50 px-4 py-3 text-sm text-slate-700">
                              Clicking this action button will create the target-sheet row immediately and show a confirmation toast.
                            </div>
                          )}
                          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <label className="block text-sm font-medium text-slate-700">Prefill columns from this row</label>
                                <p className="mt-1 text-xs text-slate-500">
                                  {normalizeActionLinkedFormBehavior(actionForm.linkedFormBehavior) === 'instant_save'
                                    ? 'Copy current row values into the new target row before it is saved.'
                                    : 'Copy current row values into the linked sheet draft before the form opens.'}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => updateActionFormDraft(actionForm.id, (item) => ({ ...item, fieldMappings: [...sanitizeActionFormFieldMappingsForEditor(item.fieldMappings), createActionFormFieldMappingDraft()] }))}
                                disabled={!actionForm.targetSheetId}
                                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <Plus className="h-4 w-4" />
                                Add mapping
                              </button>
                            </div>
                            <div className="mt-4 space-y-3">
                              {sanitizeActionFormFieldMappingsForEditor(actionForm.fieldMappings).map((fieldMapping) => (
                                <div key={fieldMapping.id} className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto]">
                                  <div>
                                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Target sheet column</label>
                                    <select
                                      value={fieldMapping.targetHeaderId}
                                      onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, fieldMappings: sanitizeActionFormFieldMappingsForEditor(item.fieldMappings).map((candidate) => candidate.id === fieldMapping.id ? { ...candidate, targetHeaderId: e.target.value } : candidate) }))}
                                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                    >
                                      <option value="">Target sheet column</option>
                                      {targetHeaders.map((header) => (
                                        <option key={`${fieldMapping.id}_target_${header.id}`} value={header.id}>{header.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Source value</label>
                                    <div className="space-y-2">
                                      <select
                                        value={normalizeActionUpdateValueMode(fieldMapping.valueMode)}
                                        onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, fieldMappings: sanitizeActionFormFieldMappingsForEditor(item.fieldMappings).map((candidate) => candidate.id === fieldMapping.id ? {
                                          ...candidate,
                                          valueMode: normalizeActionUpdateValueMode(e.target.value),
                                          sourceHeaderId: normalizeActionUpdateValueMode(e.target.value) === 'sourceHeader' ? candidate.sourceHeaderId : '',
                                          value: normalizeActionUpdateValueMode(e.target.value) === 'static' ? candidate.value : ''
                                        } : candidate) }))}
                                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                      >
                                        <option value="sourceHeader">Source sheet column</option>
                                        <option value="static">Static value</option>
                                      </select>
                                      {normalizeActionUpdateValueMode(fieldMapping.valueMode) === 'static' ? (
                                        <input
                                          type="text"
                                          value={fieldMapping.value}
                                          onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, fieldMappings: sanitizeActionFormFieldMappingsForEditor(item.fieldMappings).map((candidate) => candidate.id === fieldMapping.id ? { ...candidate, value: e.target.value } : candidate) }))}
                                          placeholder="Enter static value"
                                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                        />
                                      ) : (
                                        <select
                                          value={fieldMapping.sourceHeaderId}
                                          onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, fieldMappings: sanitizeActionFormFieldMappingsForEditor(item.fieldMappings).map((candidate) => candidate.id === fieldMapping.id ? { ...candidate, sourceHeaderId: e.target.value } : candidate) }))}
                                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                        >
                                          <option value="">Source sheet column</option>
                                          {sanitizedHeaders.filter((header) => header.id !== ACTIVE_ACTIONS_HEADER_ID).map((header) => (
                                            <option key={`${fieldMapping.id}_source_${header.id}`} value={header.id}>{header.label}</option>
                                          ))}
                                        </select>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => updateActionFormDraft(actionForm.id, (item) => ({ ...item, fieldMappings: sanitizeActionFormFieldMappingsForEditor(item.fieldMappings).filter((candidate) => candidate.id !== fieldMapping.id) }))}
                                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-500 transition hover:bg-rose-50"
                                    aria-label="Remove mapping"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                              {!sanitizeActionFormFieldMappingsForEditor(actionForm.fieldMappings).length ? (
                                <div className="rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
                                  No mapped columns yet.
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Target sheet</label>
                              <select
                                value={actionForm.targetSheetId}
                                onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, targetSheetId: e.target.value, conditions: [], updates: [] }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                              >
                                <option value="">Select target sheet</option>
                                <option value={ACTION_UPDATE_TARGET_CURRENT_SHEET}>Current sheet</option>
                                {workspaceScopedAvailableSheets.filter((sheetItem) => sheetItem.id !== id && !sheetItem.isLogSheet).map((sheetItem) => (
                                  <option key={`${actionForm.id}_update_sheet_${sheetItem.id}`} value={sheetItem.id}>{sheetItem.title || 'Untitled Sheet'}</option>
                                ))}
                              </select>
                            </div>
                            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                              Choose `Current sheet` to update the clicked row by default. Add conditions only when you want to update multiple matching rows.
                            </div>
                          </div>
                          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <label className="block text-sm font-medium text-slate-700">Match rows when</label>
                                <p className="mt-1 text-xs text-slate-500">Rows in the target sheet must satisfy these conditions.</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => updateActionFormDraft(actionForm.id, (item) => ({ ...item, conditions: [...sanitizeActionUpdateConditionsForEditor(item.conditions), createActionUpdateConditionDraft()] }))}
                                disabled={!actionForm.targetSheetId}
                                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <Plus className="h-4 w-4" />
                                Add condition
                              </button>
                            </div>
                            <div className="mt-4 space-y-3">
                              {sanitizeActionUpdateConditionsForEditor(actionForm.conditions).map((condition) => (
                                <div key={condition.id} className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-3 md:grid-cols-[minmax(0,1fr)_180px_180px_minmax(0,1fr)_auto]">
                                  <select
                                    value={condition.targetHeaderId}
                                    onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, conditions: sanitizeActionUpdateConditionsForEditor(item.conditions).map((candidate) => candidate.id === condition.id ? { ...candidate, targetHeaderId: e.target.value } : candidate) }))}
                                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                  >
                                    <option value="">Target column</option>
                                    {targetHeaders.map((header) => (
                                      <option key={`${condition.id}_target_${header.id}`} value={header.id}>{header.label}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={condition.operator}
                                    onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, conditions: sanitizeActionUpdateConditionsForEditor(item.conditions).map((candidate) => candidate.id === condition.id ? { ...candidate, operator: normalizeActionUpdateConditionOperator(e.target.value) } : candidate) }))}
                                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                  >
                                    {ACTION_UPDATE_CONDITION_OPERATOR_OPTIONS.map((option) => (
                                      <option key={`${condition.id}_operator_${option.value}`} value={option.value}>{option.label}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={condition.valueMode}
                                    onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, conditions: sanitizeActionUpdateConditionsForEditor(item.conditions).map((candidate) => candidate.id === condition.id ? { ...candidate, valueMode: normalizeActionUpdateValueMode(e.target.value) } : candidate) }))}
                                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                  >
                                    {ACTION_UPDATE_VALUE_MODE_OPTIONS.map((option) => (
                                      <option key={`${condition.id}_value_mode_${option.value}`} value={option.value}>{option.label}</option>
                                    ))}
                                  </select>
                                  {condition.valueMode === 'sourceHeader' ? (
                                    <select
                                      value={condition.sourceHeaderId}
                                      onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, conditions: sanitizeActionUpdateConditionsForEditor(item.conditions).map((candidate) => candidate.id === condition.id ? { ...candidate, sourceHeaderId: e.target.value } : candidate) }))}
                                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                    >
                                      <option value="">Current row column</option>
                                      {sanitizedHeaders.filter((header) => header.id !== ACTIVE_ACTIONS_HEADER_ID).map((header) => (
                                        <option key={`${condition.id}_source_${header.id}`} value={header.id}>{header.label}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input
                                      value={condition.value}
                                      onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, conditions: sanitizeActionUpdateConditionsForEditor(item.conditions).map((candidate) => candidate.id === condition.id ? { ...candidate, value: e.target.value } : candidate) }))}
                                      disabled={condition.operator === 'isEmpty' || condition.operator === 'isNotEmpty'}
                                      placeholder={condition.operator === 'isEmpty' || condition.operator === 'isNotEmpty' ? 'No value needed' : 'Approved'}
                                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                    />
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => updateActionFormDraft(actionForm.id, (item) => ({ ...item, conditions: sanitizeActionUpdateConditionsForEditor(item.conditions).filter((candidate) => candidate.id !== condition.id) }))}
                                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-500 transition hover:bg-rose-50"
                                    aria-label="Remove condition"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                              {!sanitizeActionUpdateConditionsForEditor(actionForm.conditions).length ? (
                                <div className="rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
                                  No conditions yet.
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <label className="block text-sm font-medium text-slate-700">Set these values</label>
                                <p className="mt-1 text-xs text-slate-500">Every matching row gets these updates.</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => updateActionFormDraft(actionForm.id, (item) => ({ ...item, updates: [...sanitizeActionUpdateValuesForEditor(item.updates), createActionUpdateValueDraft()] }))}
                                disabled={!actionForm.targetSheetId}
                                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <Plus className="h-4 w-4" />
                                Add update
                              </button>
                            </div>
                            <div className="mt-4 space-y-3">
                              {sanitizeActionUpdateValuesForEditor(actionForm.updates).map((update) => (
                                <div key={update.id} className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-3 md:grid-cols-[minmax(0,1fr)_180px_minmax(0,1fr)_auto]">
                                  <select
                                    value={update.targetHeaderId}
                                    onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, updates: sanitizeActionUpdateValuesForEditor(item.updates).map((candidate) => candidate.id === update.id ? { ...candidate, targetHeaderId: e.target.value } : candidate) }))}
                                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                  >
                                    <option value="">Target column</option>
                                    {targetHeaders.map((header) => (
                                      <option key={`${update.id}_target_${header.id}`} value={header.id}>{header.label}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={update.valueMode}
                                    onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, updates: sanitizeActionUpdateValuesForEditor(item.updates).map((candidate) => candidate.id === update.id ? { ...candidate, valueMode: normalizeActionUpdateValueMode(e.target.value) } : candidate) }))}
                                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                  >
                                    {ACTION_UPDATE_VALUE_MODE_OPTIONS.map((option) => (
                                      <option key={`${update.id}_mode_${option.value}`} value={option.value}>{option.label}</option>
                                    ))}
                                  </select>
                                  {update.valueMode === 'sourceHeader' ? (
                                    <select
                                      value={update.sourceHeaderId}
                                      onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, updates: sanitizeActionUpdateValuesForEditor(item.updates).map((candidate) => candidate.id === update.id ? { ...candidate, sourceHeaderId: e.target.value } : candidate) }))}
                                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                    >
                                      <option value="">Current row column</option>
                                      {sanitizedHeaders.filter((header) => header.id !== ACTIVE_ACTIONS_HEADER_ID).map((header) => (
                                        <option key={`${update.id}_source_${header.id}`} value={header.id}>{header.label}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input
                                      value={update.value}
                                      onChange={(e) => updateActionFormDraft(actionForm.id, (item) => ({ ...item, updates: sanitizeActionUpdateValuesForEditor(item.updates).map((candidate) => candidate.id === update.id ? { ...candidate, value: e.target.value } : candidate) }))}
                                      placeholder="Completed"
                                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                    />
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => updateActionFormDraft(actionForm.id, (item) => ({ ...item, updates: sanitizeActionUpdateValuesForEditor(item.updates).filter((candidate) => candidate.id !== update.id) }))}
                                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-500 transition hover:bg-rose-50"
                                    aria-label="Remove update"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                              {!sanitizeActionUpdateValuesForEditor(actionForm.updates).length ? (
                                <div className="rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
                                  No updates yet.
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
            )}
            {activeBuilderPanel === 'headers' && (
            <section className="flex max-h-[calc(100vh-6.4rem)] min-h-0 flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.32)]">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Headers</h2>
                <p className="mt-1 text-sm text-slate-500">Each header becomes a spreadsheet column.</p>
              </div>

              <div className="mt-5 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
                {headers.map((header, index) => (
                  <div
                    key={header.id}
                    className="relative mb-5 space-y-3 rounded-2xl border-2 border-slate-300 bg-slate-50/55 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                          {getColumnLetter(index)}
                        </div>
                        {header.id === ACTIVE_ACTIONS_HEADER_ID && (
                          <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-amber-700">
                            Action system column
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleMoveHeader(header.id, 'left')}
                          disabled={index === 0}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 hover:border-[#008080]/35 hover:text-[#008080] disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label="Move header up"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveHeader(header.id, 'right')}
                          disabled={index === headers.length - 1}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 hover:border-[#008080]/35 hover:text-[#008080] disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label="Move header down"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveHeader(header.id)}
                          disabled={header.id === ACTIVE_ACTIONS_HEADER_ID}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-rose-200 bg-white text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label={`Remove ${header.label || `column ${index + 1}`}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="grid gap-3 lg:grid-cols-[minmax(220px,1.6fr)_120px_140px_120px]">
                      <div>
                        <label className="mb-2 block text-[11px] font-semibold tracking-wide text-slate-500">
                          column header
                        </label>
                        <input
                          value={header.label}
                          onChange={(e) => handleHeaderChange(header.id, e.target.value)}
                          placeholder={`Column ${index + 1}`}
                          readOnly={header.id === ACTIVE_ACTIONS_HEADER_ID}
                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] read-only:bg-slate-100 read-only:text-slate-500"
                        />
                        {header.id === ACTIVE_ACTIONS_HEADER_ID && (
                          <p className="mt-2 text-xs text-slate-500">
                            Auto-added when actions are enabled. Configure linked-form or row-update behavior in the `Actions` tab.
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="mb-2 block text-[11px] font-semibold tracking-wide text-slate-500">
                          column width
                        </label>
                        <input
                          type="number"
                          min="120"
                          step="20"
                          value={header.width || 180}
                          onChange={(e) => handleHeaderWidthChange(header.id, e.target.value)}
                          placeholder="Width"
                          disabled={header.id === ACTIVE_ACTIONS_HEADER_ID}
                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-[11px] font-semibold tracking-wide text-slate-500">
                          column type
                        </label>
                        <select
                          value={header.type || 'text'}
                          onChange={(e) => handleHeaderTypeChange(header.id, e.target.value)}
                          disabled={header.id === ACTIVE_ACTIONS_HEADER_ID}
                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                        >
                          <option value="text">Text</option>
                          <option value="textarea">Textarea</option>
                          <option value="number">Number</option>
                          <option value="checkbox">Checkbox</option>
                          <option value="select">Select</option>
                          <option value="buttons">Buttons</option>
                          <option value="yesno">Yes / No</option>
                          <option value="rog">ROG</option>
                          <option value="multiselect">MultiSelect</option>
                          <option value="rating">Rating (Five Stars)</option>
                          <option value="percent">Percent</option>
                          <option value="email">Email</option>
                          <option value="telephone">Telephone</option>
                          <option value="address">Address</option>
                          <option value="hyperlink">Hyperlink</option>
                          <option value="youtube">YouTube</option>
                          <option value="document">Document</option>
                          <option value="image">Image</option>
                          <option value="profile">Profile</option>
                          <option value="list">List</option>
                          <option value="date">Date</option>
                          <option value="time">Time</option>
                          <option value="datetime">DateTime</option>
                          <option value="currency">Currency</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-[11px] font-semibold tracking-wide text-slate-500">
                          decimals
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="6"
                          step="1"
                          value={DECIMAL_FIELD_TYPES.has(header.type) ? normalizeDecimalDigits(header.decimalDigits, 2) : 0}
                          onChange={(e) => handleHeaderDecimalDigitsChange(header.id, e.target.value)}
                          disabled={header.id === ACTIVE_ACTIONS_HEADER_ID || !DECIMAL_FIELD_TYPES.has(header.type)}
                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                        />
                      </div>
                    </div>

                    <div className="grid gap-3 lg:grid-cols-[minmax(220px,1fr)_minmax(0,1fr)]">
                      <div>
                        <label className="mb-2 block text-[11px] font-semibold tracking-wide text-slate-500">
                          form input
                        </label>
                        <select
                          value={canUseSensorInputMode(header.type) ? normalizeSheetFormInputMode(header.formInputMode) : 'default'}
                          onChange={(e) => setHeaders((prev) => prev.map((item) => (
                            item.id === header.id
                              ? { ...item, formInputMode: normalizeSheetFormInputMode(e.target.value) }
                              : item
                          )))}
                          disabled={header.id === ACTIVE_ACTIONS_HEADER_ID || !canUseSensorInputMode(header.type)}
                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                        >
                          {SHEET_FORM_INPUT_MODE_OPTIONS.map((option) => (
                            <option key={`${header.id}_form_input_${option.value}`} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-end">
                        <p className="text-xs text-slate-500">
                          {canUseSensorInputMode(header.type)
                            ? 'Controls how this column is captured on the published form. Stored values remain standard text.'
                            : 'Scanner input is available on text, email, telephone, address, and hyperlink columns.'}
                        </p>
                      </div>
                    </div>

                    {header.id === ACTIVE_ACTIONS_HEADER_ID && (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-slate-600">
                        This column is read-only in the builder. The configuration in the `Actions` tab controls what happens when someone opens a row action.
                      </div>
                    )}

                    {header.type === 'profile' && (
                      <div className="grid gap-3 lg:grid-cols-2">
                        <div>
                          <label className="mb-2 block text-[11px] font-semibold tracking-wide text-slate-500">
                            Profile Image Column
                          </label>
                          <select
                            value={getProfileConfigDraft(header.profileConfig).imageHeaderId}
                            onChange={(e) => handleHeaderProfileConfigChange(header.id, 'imageHeaderId', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            <option value="">Choose image column</option>
                            {headers
                              .filter((candidate) => candidate.id !== header.id && `${candidate.label || ''}`.trim())
                              .map((candidate) => (
                                <option key={`${header.id}_profile_image_${candidate.id}`} value={candidate.id}>
                                  {candidate.label}
                                </option>
                              ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-2 block text-[11px] font-semibold tracking-wide text-slate-500">
                            Profile Name Column
                          </label>
                          <select
                            value={getProfileConfigDraft(header.profileConfig).labelHeaderId}
                            onChange={(e) => handleHeaderProfileConfigChange(header.id, 'labelHeaderId', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            <option value="">Choose name column</option>
                            {headers
                              .filter((candidate) => candidate.id !== header.id && `${candidate.label || ''}`.trim())
                              .map((candidate) => (
                                <option key={`${header.id}_profile_label_${candidate.id}`} value={candidate.id}>
                                  {candidate.label}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {header.type === 'rog' && (
                      <HeaderSection
                        title="ROG Display"
                        description="Choose whether selected ROG values should softly tint the cell background."
                      >
                        <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={Boolean(header.rogOverlay)}
                            onChange={(e) => handleHeaderRogOverlayChange(header.id, e.target.checked)}
                            className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                          />
                          <span>Apply light overlay based on selected ROG value</span>
                        </label>
                      </HeaderSection>
                    )}

                    <HeaderSection
                      title="Display & Access"
                      description="Access level, row label, and text presentation."
                    >
                      <div className="space-y-3">
                        <div>
                          <label className="mb-2 block text-[11px] font-semibold tracking-wide text-slate-500">
                            display name
                          </label>
                          <input
                            value={header.displayName || ''}
                            onChange={(e) => handleHeaderDisplayNameChange(header.id, e.target.value)}
                            placeholder={header.label ? `Shown as ${header.label}` : 'Optional visible name or question'}
                            readOnly={header.id === ACTIVE_ACTIONS_HEADER_ID}
                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] read-only:bg-slate-100 read-only:text-slate-500"
                          />
                          <p className="mt-2 text-xs text-slate-500">
                            Keeps the saved column name unchanged while showing different text in forms and views.
                          </p>
                        </div>

                        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Conditional Display Names</p>
                              <p className="mt-1 text-xs text-slate-500">
                                Show a different display name when another column matches. This only affects what users see.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleAddConditionalDisplayNameRule(header.id)}
                              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-[#008080]/35 hover:text-[#008080]"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Add Rule
                            </button>
                          </div>

                          {(header.conditionalDisplayNames || []).length > 0 ? (
                            <div className="space-y-3">
                              {(header.conditionalDisplayNames || []).map((rule, ruleIndex) => (
                                <div key={`${header.id}_display_name_rule_${ruleIndex}`} className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-3 lg:grid-cols-[minmax(0,1.2fr)_150px_minmax(0,1fr)_minmax(0,1.4fr)_44px]">
                                  <div>
                                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                      Source Column
                                    </label>
                                    <select
                                      value={rule.whenHeaderId || ''}
                                      onChange={(e) => handleConditionalDisplayNameRuleChange(header.id, ruleIndex, 'whenHeaderId', e.target.value)}
                                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                    >
                                      <option value="">Choose a column</option>
                                      {headers
                                        .filter((candidate) => candidate.id !== header.id && `${candidate.label || ''}`.trim())
                                        .map((candidate) => (
                                          <option key={`${header.id}_display_name_source_${candidate.id}`} value={candidate.id}>
                                            {candidate.label}
                                          </option>
                                        ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                      Operator
                                    </label>
                                    <select
                                      value={rule.operator || 'equals'}
                                      onChange={(e) => handleConditionalDisplayNameRuleChange(header.id, ruleIndex, 'operator', e.target.value)}
                                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                    >
                                      <option value="equals">Equals</option>
                                      <option value="notEquals">Not equals</option>
                                      <option value="contains">Contains</option>
                                      <option value="notContains">Not contains</option>
                                      <option value="greaterThan">Greater than</option>
                                      <option value="lessThan">Less than</option>
                                      <option value="isEmpty">Is empty</option>
                                      <option value="isNotEmpty">Is not empty</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                      Match Value
                                    </label>
                                    <input
                                      value={rule.value || ''}
                                      disabled={rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty'}
                                      onChange={(e) => handleConditionalDisplayNameRuleChange(header.id, ruleIndex, 'value', e.target.value)}
                                      placeholder={rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty' ? 'No value needed' : 'Value to compare'}
                                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                    />
                                  </div>
                                  <div>
                                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                      Display Name
                                    </label>
                                    <input
                                      value={rule.displayName || ''}
                                      onChange={(e) => handleConditionalDisplayNameRuleChange(header.id, ruleIndex, 'displayName', e.target.value)}
                                      placeholder="Agenda"
                                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                    />
                                  </div>
                                  <div className="flex items-end">
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveConditionalDisplayNameRule(header.id, ruleIndex)}
                                      className="inline-flex h-[52px] w-full items-center justify-center rounded-2xl border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                                      aria-label="Remove display name rule"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
                              No conditional display names configured.
                            </div>
                          )}
                        </div>

                        {!isLogSheet && (
                          <div>
                            <label className="mb-2 block text-[11px] font-semibold tracking-wide text-slate-500">
                              system field
                            </label>
                            <select
                              value={normalizeLogSystemField(header.systemField)}
                              onChange={(e) => handleHeaderSystemFieldChange(header.id, e.target.value)}
                              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                            >
                              {LOG_SYSTEM_FIELD_OPTIONS.map((option) => (
                                <option key={`${header.id}_system_${option.value || 'none'}`} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <p className="mt-2 text-xs text-slate-500">
                              Use this only on log sheets. Each system field can be assigned to one column at most.
                            </p>
                          </div>
                        )}

                        <div>
                          <label className="mb-2 block text-[11px] font-semibold tracking-wide text-slate-500">
                            column access
                          </label>
                          <select
                            value={normalizeColumnAccess(header.columnAccess)}
                            onChange={(e) => handleHeaderColumnAccessChange(header.id, e.target.value)}
                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            {COLUMN_ACCESS_OPTIONS.map((option) => (
                              <option key={`${header.id}_access_${option.value}`} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <p className="mt-2 text-xs text-slate-500">
                            Hidden columns never appear in the sheet viewer. Private columns are owner-only. Restricted columns require a signed-in user. Public columns follow normal sheet/workspace access.
                          </p>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <label className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={Boolean(header.isRowLabel)}
                              onChange={(e) => handleHeaderRowLabelChange(header.id, e.target.checked)}
                              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                            />
                              <span>
                                <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Row Label
                                </span>
                                <span className="mt-1 block text-sm text-slate-700">
                                  Use this column&apos;s content as the row label in the sheet sidebar, saved chats, and other row references.
                                </span>
                              </span>
                          </label>
                        </div>

                        <div className="grid gap-3 lg:grid-cols-[minmax(220px,1fr)_minmax(220px,1fr)]">
                          <div>
                            <label className="mb-2 block text-[11px] font-semibold tracking-wide text-slate-500">
                              field text style
                            </label>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => handleHeaderTextStyleChange(header.id, 'textBold', !header.textBold)}
                                className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl border transition ${
                                  header.textBold
                                    ? 'border-[#008080]/35 bg-[#008080]/10 text-[#0f4f4f]'
                                    : 'border-slate-200 bg-white text-slate-600 hover:border-[#008080]/35 hover:text-[#008080]'
                                }`}
                                aria-label="Toggle bold"
                                title="Bold"
                              >
                                <Bold className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleHeaderTextStyleChange(header.id, 'textItalic', !header.textItalic)}
                                className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl border transition ${
                                  header.textItalic
                                    ? 'border-[#008080]/35 bg-[#008080]/10 text-[#0f4f4f]'
                                    : 'border-slate-200 bg-white text-slate-600 hover:border-[#008080]/35 hover:text-[#008080]'
                                }`}
                                aria-label="Toggle italic"
                                title="Italic"
                              >
                                <Italic className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleHeaderTextStyleChange(header.id, 'textUnderline', !header.textUnderline)}
                                className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl border transition ${
                                  header.textUnderline
                                    ? 'border-[#008080]/35 bg-[#008080]/10 text-[#0f4f4f]'
                                    : 'border-slate-200 bg-white text-slate-600 hover:border-[#008080]/35 hover:text-[#008080]'
                                }`}
                                aria-label="Toggle underline"
                                title="Underline"
                              >
                                <Underline className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="mb-2 block text-[11px] font-semibold tracking-wide text-slate-500">
                              field alignment
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { value: 'left', icon: AlignLeft, label: 'Left' },
                                { value: 'center', icon: AlignCenter, label: 'Centre' },
                                { value: 'right', icon: AlignRight, label: 'Right' }
                              ].map((option) => {
                                const Icon = option.icon;
                                const isActive = normalizeHeaderTextAlign(header.textAlign) === option.value;
                                return (
                                  <button
                                    key={`${header.id}_${option.value}`}
                                    type="button"
                                    onClick={() => handleHeaderTextStyleChange(header.id, 'textAlign', option.value)}
                                    className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-semibold transition ${
                                      isActive
                                        ? 'border-[#008080]/35 bg-[#008080]/10 text-[#0f4f4f]'
                                        : 'border-slate-200 bg-white text-slate-600 hover:border-[#008080]/35 hover:text-[#008080]'
                                    }`}
                                    aria-label={`Align ${option.label}`}
                                  >
                                    <Icon className="h-4 w-4" />
                                    <span>{option.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="mb-2 block text-[11px] font-semibold tracking-wide text-slate-500">
                            render text color
                          </label>
                          <div className="grid gap-3 lg:grid-cols-[minmax(180px,220px)_minmax(0,1fr)] lg:items-end">
                            <input
                              value={header.renderColor || ''}
                              onChange={(e) => handleHeaderRenderColorChange(header.id, e.target.value)}
                              placeholder="008080 or #008080"
                              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                            />
                            <p className="text-xs text-slate-500">
                              Applies in render mode for the default layout and preset cards. Accepts 3 or 6 digit hex with or without #.
                            </p>
                          </div>
                        </div>
                      </div>
                    </HeaderSection>

                    {NUMERIC_HEADER_TYPES.has(header.type) && (
                      <HeaderSection
                        title="Base Values"
                        description="Set base numbers and optional base items for numeric-style columns."
                      >
                        <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
                          <div>
                            <div className="mb-2 flex items-center gap-2">
                              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Base Number
                              </label>
                              <button
                                type="button"
                                onClick={() => setOpenBaseInfoHeaderId((prev) => (prev === header.id ? null : header.id))}
                                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                                aria-label="Show base number help"
                              >
                                <Info className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <input
                              type="number"
                              step="any"
                              min="0"
                              value={header.baseValue ?? ''}
                              onChange={(e) => handleHeaderBaseValueChange(header.id, e.target.value)}
                              placeholder={header.type === 'rating' ? '5' : '10'}
                              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Base Item(s)
                            </label>
                            <input
                              type="text"
                              value={header.baseItems ?? ''}
                              onChange={(e) => handleHeaderBaseItemsChange(header.id, e.target.value)}
                              placeholder="Fractions, decimals, geometry"
                              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                            />
                            <p className="mt-2 text-xs text-slate-500">
                              Comma separated. Useful for topics assessed, attendance categories, or qualitative grouping.
                            </p>
                          </div>
                        </div>
                        <div>
                          {openBaseInfoHeaderId === header.id && (
                            <div className="rounded-2xl border border-[#008080]/20 bg-[#008080]/10 px-4 py-3 text-sm text-slate-600">
                              Use a base number for columns like marks, attendance, class size, or quotas. Formulas can then reference either the column value or the column base.
                            </div>
                          )}
                        </div>
                      </HeaderSection>
                    )}

                    <HeaderSection
                      title="Defaults"
                      description="Choose how this column should be pre-filled for new rows."
                    >
                    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Default Value Mode
                          </label>
                          <select
                            value={header.defaultValueMode || 'none'}
                            onChange={(e) => handleHeaderDefaultValueModeChange(header.id, e.target.value)}
                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            {DEFAULT_VALUE_MODE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Default Value
                          </label>
                          {header.type === 'checkbox' ? (
                            <label className="flex h-[52px] items-center gap-3 rounded-lg border border-slate-200 px-4">
                              <input
                                type="checkbox"
                                checked={Boolean(header.defaultValue)}
                                disabled={header.defaultValueMode === 'none'}
                                onChange={(e) => handleHeaderDefaultValueChange(header.id, e.target.checked)}
                                className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                              />
                              <span className="text-sm text-slate-700">Checked by default</span>
                            </label>
                          ) : header.type === 'select' || header.type === 'buttons' || header.type === 'yesno' || header.type === 'rog' ? (
                            <select
                              value={`${header.defaultValue ?? ''}`}
                              disabled={header.defaultValueMode === 'none'}
                              onChange={(e) => handleHeaderDefaultValueChange(header.id, e.target.value)}
                              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                            >
                              <option value="">No default</option>
                              {(resolvedHeaderOptionsById[header.id] || []).map((option) => (
                                <option key={`${header.id}_default_${option}`} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : header.type === 'multiselect' || header.type === 'list' ? (
                            <div className="space-y-3">
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={headerDefaultListDrafts[header.id] || ''}
                                  disabled={header.defaultValueMode === 'none'}
                                  onChange={(e) => handleHeaderDefaultListDraftChange(header.id, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleAddHeaderDefaultListItem(header.id);
                                    }
                                  }}
                                  placeholder={header.type === 'multiselect'
                                    ? 'Add default option or paste comma-separated values'
                                    : 'Add default list item or paste comma-separated values'}
                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleAddHeaderDefaultListItem(header.id)}
                                  disabled={header.defaultValueMode === 'none'}
                                  className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-[#008080]/35 hover:text-[#008080] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Add
                                </button>
                              </div>
                              <p className="text-xs text-slate-500">
                                {header.type === 'multiselect'
                                  ? 'Paste a comma-separated or line-separated list, then click `Add` to split it into individual default options.'
                                  : 'Paste a comma-separated or line-separated list, then click `Add` to split it into individual default items.'}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {normalizeListItems(header.defaultValue).length ? normalizeListItems(header.defaultValue).map((item, index) => (
                                  <span key={`${header.id}_default_list_${index}`} className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                                    {item}
                                    {header.defaultValueMode !== 'none' && (
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveHeaderDefaultListItem(header.id, index)}
                                        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-500 transition hover:bg-white hover:text-rose-600"
                                        aria-label={`Remove ${item}`}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    )}
                                  </span>
                                )) : (
                                  <span className="text-sm text-slate-400">
                                    {header.type === 'multiselect' ? 'No default options' : 'No default list items'}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : header.type === 'textarea' ? (
                            <textarea
                              rows={4}
                              value={header.defaultValue ?? ''}
                              disabled={header.defaultValueMode === 'none'}
                              onChange={(e) => handleHeaderDefaultValueChange(header.id, e.target.value)}
                              placeholder="Default value"
                              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                            />
                          ) : (
                            <div className="space-y-2">
                              <input
                                type={NUMERIC_HEADER_TYPES.has(header.type) ? 'number' : 'text'}
                                step={header.type === 'currency' ? '0.01' : 'any'}
                                value={header.defaultValue ?? ''}
                                disabled={header.defaultValueMode === 'none'}
                                onChange={(e) => handleHeaderDefaultValueChange(header.id, e.target.value)}
                                placeholder={
                                  header.type === 'date'
                                    ? 'YYYY-MM-DD or TODAY()'
                                    : header.type === 'time'
                                      ? 'HH:MM or TIMENOW()'
                                      : header.type === 'datetime'
                                        ? 'YYYY-MM-DDTHH:MM or NOW()'
                                        : 'Default value'
                                }
                                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                              />
                              {(header.type === 'date' || header.type === 'time' || header.type === 'datetime') && (
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    disabled={header.defaultValueMode === 'none'}
                                    onClick={() => handleHeaderDefaultValueChange(
                                      header.id,
                                      header.type === 'date' ? 'TODAY()' : header.type === 'time' ? 'TIMENOW()' : 'NOW()'
                                    )}
                                    className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-[#008080]/35 hover:text-[#008080] disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    {header.type === 'date' ? 'Use TODAY()' : header.type === 'time' ? 'Use TIMENOW()' : 'Use NOW()'}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500">
                        `Editable default` pre-fills new rows but still allows changes. `Fixed value` locks the field in the sheet viewer.
                      </p>
                    </div>
                    </HeaderSection>

                    <HeaderSection
                      title="Calculations"
                      description="Configure formulas and footer summaries for this column."
                    >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Column Logic
                        </label>
                        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
                          {['formula', 'summary'].map((mode) => {
                            const isActive = (headerEditorModes[header.id] || 'formula') === mode;
                            return (
                              <button
                                key={`${header.id}_${mode}`}
                                type="button"
                                onClick={() => handleHeaderEditorModeChange(header.id, mode)}
                                className={`rounded-xl px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition ${
                                  isActive
                                    ? 'bg-slate-900 text-white'
                                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                                }`}
                              >
                                {mode}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {(headerEditorModes[header.id] || 'formula') === 'formula' ? (
                        <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Formula Type
                            </label>
                            <select
                              value={header.formulaConfig?.type || ''}
                              onChange={(e) => handleHeaderFormulaTypeChange(header.id, e.target.value)}
                              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                            >
                              <option value="">No formula</option>
                              {VISUAL_FORMULA_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Formula Preview
                            </label>
                            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
                              <div className="flex min-w-max items-center gap-2">
                                {header.formulaConfig ? (
                                  (header.formulaConfig.type === 'lookup'
                                    ? getLookupFormulaSummary(header.formulaConfig, headers, availableOptionSourceHeadersBySheetId, headerAvailableSheets)
                                    : header.formulaConfig.type === 'filter_list'
                                      ? getFilterListFormulaSummary(header.formulaConfig, headers, availableOptionSourceHeadersBySheetId, headerAvailableSheets)
                                    : getFormulaPreviewTokens(header.formulaConfig, headers)
                                  ).map((token, tokenIndex) => (
                                    <span
                                      key={`${header.id}_token_${tokenIndex}`}
                                      className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${
                                        token.kind === 'column'
                                          ? 'bg-white text-slate-700 ring-1 ring-slate-200'
                                          : token.kind === 'operator'
                                            ? 'bg-[#008080]/15 text-[#0f4f4f]'
                                            : token.kind === 'separator'
                                              ? 'bg-amber-100 text-amber-700'
                                              : 'bg-slate-900 text-white'
                                      }`}
                                    >
                                      {token.label}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-sm text-slate-400">No formula selected</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {header.formulaConfig && (
                          <>
                            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
                              <div className="space-y-3">
                                {header.formulaConfig.type === 'percentage' ? (
                                  <>
                                    <div className="grid gap-3 lg:grid-cols-[180px_minmax(0,1fr)]">
                                      <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Numerator Type
                                        </label>
                                        <select
                                          value={header.formulaConfig.numeratorType || 'single'}
                                          onChange={(e) => handlePercentageNumeratorTypeChange(header.id, e.target.value)}
                                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                        >
                                          <option value="single">Single value</option>
                                          <option value="sum">Sum</option>
                                          <option value="subtract">Subtract</option>
                                          <option value="multiply">Multiply</option>
                                          <option value="divide">Divide</option>
                                          <option value="average">Average</option>
                                          <option value="min">Lowest value</option>
                                          <option value="max">Highest value</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Add Numerator Column
                                        </label>
                                        <select
                                          defaultValue=""
                                          onChange={(e) => {
                                            handleFormulaOperandAdd(header.id, e.target.value);
                                            e.target.value = '';
                                          }}
                                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                        >
                                          <option value="">Choose a numerator column</option>
                                          {getFormulaOperandChoices(header, headers).map((choice) => (
                                            <option key={choice.value} value={choice.value}>
                                              {choice.label}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                    <div>
                                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Denominator Column
                                      </label>
                                      <select
                                        value={header.formulaConfig.denominator || ''}
                                        onChange={(e) => handlePercentageDenominatorChange(header.id, e.target.value)}
                                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                      >
                                        <option value="">Choose a denominator column</option>
                                        {getFormulaOperandChoices(header, headers).map((choice) => (
                                          <option key={`denominator_${choice.value}`} value={choice.value}>
                                            {choice.label}
                                          </option>
                                        ))}
                                      </select>
                                      <p className="mt-2 text-xs text-slate-500">
                                        Example: choose `Sum` for the numerator, add A, B, and C, then choose D as the denominator to build `Percentage(Sum(A+B+C) of D)`.
                                      </p>
                                    </div>
                                  </>
                                ) : header.formulaConfig.type === 'lookup' ? (
                                  <div className="grid gap-3 lg:grid-cols-2">
                                    <div>
                                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Source Table
                                      </label>
                                      <select
                                        value={header.formulaConfig.lookupSheetId || ''}
                                        onChange={(e) => handleLookupFormulaChange(header.id, 'lookupSheetId', e.target.value)}
                                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                      >
                                        <option value="">Choose a table</option>
                                        {workspaceScopedHeaderAvailableSheets
                                          .map((sheetItem) => (
                                            <option key={`${header.id}_lookup_sheet_${sheetItem.id}`} value={sheetItem.id}>
                                              {sheetItem.title}
                                            </option>
                                          ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Current Row Match Column
                                      </label>
                                      <select
                                        value={header.formulaConfig.lookupMatchCurrentHeaderId || ''}
                                        onChange={(e) => handleLookupFormulaChange(header.id, 'lookupMatchCurrentHeaderId', e.target.value)}
                                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                      >
                                        <option value="">Choose a column</option>
                                        {headers
                                          .filter((candidate) => candidate.id !== header.id && `${candidate.label || ''}`.trim())
                                          .map((candidate) => (
                                            <option key={`${header.id}_lookup_current_${candidate.id}`} value={candidate.id}>
                                              {candidate.label}
                                            </option>
                                          ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Source Table Match Column
                                      </label>
                                      <select
                                        value={header.formulaConfig.lookupMatchSourceHeaderId || ''}
                                        onChange={(e) => handleLookupFormulaChange(header.id, 'lookupMatchSourceHeaderId', e.target.value)}
                                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                      >
                                        <option value="">Choose a source column</option>
                                        {(availableOptionSourceHeadersBySheetId[header.formulaConfig.lookupSheetId] || []).map((candidate) => (
                                          <option key={`${header.id}_lookup_source_${candidate.id}`} value={candidate.id}>
                                            {candidate.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Returned Column
                                      </label>
                                      <select
                                        value={header.formulaConfig.lookupReturnHeaderId || ''}
                                        onChange={(e) => handleLookupFormulaChange(header.id, 'lookupReturnHeaderId', e.target.value)}
                                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                      >
                                        <option value="">Choose a returned column</option>
                                        {(availableOptionSourceHeadersBySheetId[header.formulaConfig.lookupSheetId] || []).map((candidate) => (
                                          <option key={`${header.id}_lookup_return_${candidate.id}`} value={candidate.id}>
                                            {candidate.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                ) : header.formulaConfig.type === 'filter_list' ? (
                                  <div className="space-y-3">
                                    <div className="grid gap-3 lg:grid-cols-2">
                                      <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Source Table
                                        </label>
                                        <select
                                          value={header.formulaConfig.filterListSheetId || ''}
                                          onChange={(e) => handleFilterListFormulaChange(header.id, 'filterListSheetId', e.target.value)}
                                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                      >
                                        <option value="">Choose a table</option>
                                        {workspaceScopedHeaderAvailableSheets
                                            .map((sheetItem) => (
                                              <option key={`${header.id}_filter_list_sheet_${sheetItem.id}`} value={sheetItem.id}>
                                                {sheetItem.title}
                                              </option>
                                            ))}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Returned Column
                                        </label>
                                        <select
                                          value={header.formulaConfig.filterListReturnHeaderId || ''}
                                          onChange={(e) => handleFilterListFormulaChange(header.id, 'filterListReturnHeaderId', e.target.value)}
                                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                        >
                                          <option value="">Choose a returned column</option>
                                          {(availableOptionSourceHeadersBySheetId[header.formulaConfig.filterListSheetId] || []).map((candidate) => (
                                            <option key={`${header.id}_filter_list_return_${candidate.id}`} value={candidate.id}>
                                              {candidate.label}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>

                                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                                      <div className="flex items-center justify-between gap-3">
                                        <div>
                                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            Filters
                                          </p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            Match source table columns to values from the current row.
                                          </p>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => handleFilterListRuleAdd(header.id)}
                                          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                                        >
                                          <Plus className="h-4 w-4" />
                                          Add filter
                                        </button>
                                      </div>

                                      <div className="mt-3 space-y-3">
                                        {(Array.isArray(header.formulaConfig.filterListFilters) ? header.formulaConfig.filterListFilters : []).length === 0 ? (
                                          <p className="text-sm text-slate-400">
                                            No filters yet. Add a filter to limit the returned list by current row values.
                                          </p>
                                        ) : (
                                          (Array.isArray(header.formulaConfig.filterListFilters) ? header.formulaConfig.filterListFilters : []).map((filterRule, filterIndex) => (
                                            <div key={`${header.id}_filter_rule_${filterIndex}`} className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-3 lg:grid-cols-[minmax(0,1fr)_180px_minmax(0,1fr)_auto] lg:items-end">
                                              <div>
                                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                  Source Table Column
                                                </label>
                                                <select
                                                  value={filterRule.sourceHeaderId || ''}
                                                  onChange={(e) => handleFilterListRuleChange(header.id, filterIndex, 'sourceHeaderId', e.target.value)}
                                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                                >
                                                  <option value="">Choose a source column</option>
                                                  {(availableOptionSourceHeadersBySheetId[header.formulaConfig.filterListSheetId] || []).map((candidate) => (
                                                    <option key={`${header.id}_filter_source_${filterIndex}_${candidate.id}`} value={candidate.id}>
                                                      {candidate.label}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                              <div>
                                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                  Operator
                                                </label>
                                                <select
                                                  value={filterRule.operator || 'equals'}
                                                  onChange={(e) => handleFilterListRuleChange(header.id, filterIndex, 'operator', e.target.value)}
                                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                                >
                                                  {FILTER_LIST_OPERATOR_OPTIONS.map((option) => (
                                                    <option key={`${header.id}_filter_operator_${filterIndex}_${option.value}`} value={option.value}>
                                                      {option.label}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                              <div>
                                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                  Current Row Column
                                                </label>
                                                <select
                                                  value={filterRule.currentHeaderId || ''}
                                                  onChange={(e) => handleFilterListRuleChange(header.id, filterIndex, 'currentHeaderId', e.target.value)}
                                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                                >
                                                  <option value="">Choose a column</option>
                                                  {headers
                                                    .filter((candidate) => candidate.id !== header.id && `${candidate.label || ''}`.trim())
                                                    .map((candidate) => (
                                                      <option key={`${header.id}_filter_current_${filterIndex}_${candidate.id}`} value={candidate.id}>
                                                        {candidate.label}
                                                      </option>
                                                    ))}
                                                </select>
                                              </div>
                                              <div className="flex justify-end">
                                                <button
                                                  type="button"
                                                  onClick={() => handleFilterListRuleRemove(header.id, filterIndex)}
                                                  className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                                                  aria-label="Remove filter"
                                                >
                                                  <X className="h-4 w-4" />
                                                </button>
                                              </div>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                      {header.formulaConfig.type === 'subtract_list' ? 'Add List Column' : 'Add Source Column'}
                                    </label>
                                    <select
                                      defaultValue=""
                                      onChange={(e) => {
                                        handleFormulaOperandAdd(header.id, e.target.value);
                                        e.target.value = '';
                                      }}
                                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                    >
                                      <option value="">Choose a column</option>
                                      {getFormulaOperandChoices(header, headers).map((choice) => (
                                        <option key={choice.value} value={choice.value}>
                                          {choice.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-end">
                                <button
                                  type="button"
                                  onClick={() => handleClearFormula(header.id)}
                                  className="inline-flex items-center gap-2 rounded-2xl border border-rose-200 px-4 py-3 text-sm font-semibold text-rose-600 hover:bg-rose-50"
                                >
                                  <X className="h-4 w-4" />
                                  Clear
                                </button>
                              </div>
                            </div>

                            {header.formulaConfig.type === 'concat' && (
                              <div>
                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Separator Between Values
                                </label>
                                <input
                                  value={header.formulaConfig.separator ?? ' '}
                                  onChange={(e) => handleFormulaSeparatorChange(header.id, e.target.value)}
                                  placeholder="Space, dash, slash..."
                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                />
                              </div>
                            )}

                            {header.formulaConfig.type === 'duration_days' && (
                              <div>
                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Weekend Handling
                                </label>
                                <select
                                  value={header.formulaConfig.weekendMode || 'include'}
                                  onChange={(e) => handleDurationWeekendModeChange(header.id, e.target.value)}
                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                >
                                  <option value="include">Include weekends</option>
                                  <option value="exclude">Exclude weekends</option>
                                </select>
                                <p className="mt-2 text-xs text-slate-500">
                                  Choose whether Saturdays and Sundays count toward the total duration.
                                </p>
                              </div>
                            )}

                            {header.formulaConfig.type !== 'lookup' && header.formulaConfig.type !== 'filter_list' && (
                            <div>
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                {header.formulaConfig.type === 'percentage'
                                  ? 'Numerator Columns'
                                  : header.formulaConfig.type === 'subtract_list'
                                    ? 'Selected Lists'
                                    : 'Selected Columns'}
                              </p>
                              <div className="space-y-2">
                                {getFormulaOperandList(header.formulaConfig).length === 0 ? (
                                  <p className="text-sm text-slate-400">
                                    {header.formulaConfig.type === 'subtract_list'
                                      ? 'Pick two list-valued columns to subtract one list from another.'
                                      : 'Pick one or more source columns to build the formula visually.'}
                                  </p>
                                ) : (
                                  getFormulaOperandList(header.formulaConfig).map((operandId, operandIndex, operandList) => {
                                    const resolvedOperandId = `${operandId || ''}`.startsWith('base:') ? `${operandId}`.slice(5) : operandId;
                                    const isBaseOperand = `${operandId || ''}`.startsWith('base:');
                                    const operandHeader = headers.find((candidate) => candidate.id === resolvedOperandId);
                                    return (
                                      <div key={`${header.id}_${operandId}`} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
                                        <div className="min-w-0">
                                          <p className="truncate text-sm font-semibold text-slate-700">
                                            {operandHeader?.label || 'Unnamed column'}{isBaseOperand ? ' base' : ''}
                                          </p>
                                          <p className="text-xs text-slate-500">
                                            {getColumnLetter(headers.findIndex((candidate) => candidate.id === resolvedOperandId))}
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <button
                                            type="button"
                                            onClick={() => handleFormulaOperandMove(header.id, operandId, 'left')}
                                            disabled={operandIndex === 0}
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 disabled:opacity-40"
                                            aria-label="Move operand left"
                                          >
                                            <ArrowLeft className="h-4 w-4" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleFormulaOperandMove(header.id, operandId, 'right')}
                                            disabled={operandIndex === operandList.length - 1}
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 disabled:opacity-40"
                                            aria-label="Move operand right"
                                          >
                                            <ArrowRight className="h-4 w-4" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleFormulaOperandRemove(header.id, operandId)}
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                                            aria-label="Remove operand"
                                          >
                                            <X className="h-4 w-4" />
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                            )}

                            {header.formulaConfig.type === 'percentage' && (
                              <div>
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Denominator
                                </p>
                                {header.formulaConfig.denominator ? (
                                  <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
                                    <div className="min-w-0">
                                      <p className="truncate text-sm font-semibold text-slate-700">
                                        {getFormulaOperandLabel(header.formulaConfig.denominator, headers)}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        {getColumnLetter(headers.findIndex((candidate) => candidate.id === (`${header.formulaConfig.denominator || ''}`.startsWith('base:') ? `${header.formulaConfig.denominator}`.slice(5) : header.formulaConfig.denominator)))}
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handlePercentageDenominatorChange(header.id, '')}
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                                      aria-label="Clear denominator"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <p className="text-sm text-slate-400">Choose the total or base column used for the percentage denominator.</p>
                                )}
                              </div>
                            )}

                            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Stored Formula</p>
                              <div className="mt-2 overflow-x-auto">
                                <code className="block min-w-max text-sm text-slate-700">
                                  {buildFormulaFromConfig(header.formulaConfig, headers) || 'Select enough columns to generate a formula.'}
                                </code>
                              </div>
                            </div>

                            <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Conditional Formula Results</p>
                                  <p className="mt-1 text-xs text-slate-500">
                                    Override the default formula when a previous column matches a condition.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleAddConditionalFormulaRule(header.id)}
                                  className="inline-flex items-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-[#008080]/35 hover:text-[#008080]"
                                >
                                  <Plus className="h-4 w-4" />
                                  Add Branch
                                </button>
                              </div>

                              {(header.conditionalFormulaRules || []).length === 0 ? (
                                <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
                                  No conditional branches yet. The default formula above is used for all rows.
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {(header.conditionalFormulaRules || []).map((rule, ruleIndex) => (
                                    <div key={`${header.id}_formula_rule_${ruleIndex}`} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
                                      <div className="grid gap-3 lg:grid-cols-[minmax(0,1.2fr)_180px_minmax(0,1fr)_auto]">
                                        <div>
                                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            When Column
                                          </label>
                                          <select
                                            value={rule.headerId || ''}
                                            onChange={(e) => handleConditionalFormulaRuleChange(header.id, ruleIndex, 'headerId', e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                          >
                                            <option value="">Choose a column</option>
                                            {headers
                                              .filter((candidate) => candidate.id !== header.id && `${candidate.label || ''}`.trim())
                                              .map((candidate) => (
                                                <option key={`${header.id}_formula_condition_${candidate.id}`} value={candidate.id}>
                                                  {candidate.label}
                                                </option>
                                              ))}
                                          </select>
                                        </div>
                                        <div>
                                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            Operator
                                          </label>
                                          <select
                                            value={rule.operator || 'equals'}
                                            onChange={(e) => handleConditionalFormulaRuleChange(header.id, ruleIndex, 'operator', e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                          >
                                            <option value="equals">Equals</option>
                                            <option value="notEquals">Not equals</option>
                                            <option value="contains">Contains</option>
                                            <option value="notContains">Not contains</option>
                                            <option value="greaterThan">Greater than</option>
                                            <option value="lessThan">Less than</option>
                                            <option value="isEmpty">Is empty</option>
                                            <option value="isNotEmpty">Is not empty</option>
                                          </select>
                                        </div>
                                        <div>
                                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            Match Value
                                          </label>
                                          <input
                                            value={rule.value || ''}
                                            disabled={rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty'}
                                            onChange={(e) => handleConditionalFormulaRuleChange(header.id, ruleIndex, 'value', e.target.value)}
                                            placeholder={rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty' ? 'No value needed' : 'Value to compare'}
                                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                          />
                                        </div>
                                        <div className="flex items-end">
                                          <button
                                            type="button"
                                            onClick={() => handleConditionalFormulaRuleRemove(header.id, ruleIndex)}
                                            className="inline-flex items-center gap-2 rounded-2xl border border-rose-200 px-4 py-3 text-sm font-semibold text-rose-600 hover:bg-rose-50"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                            Remove
                                          </button>
                                        </div>
                                      </div>

                                      <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
                                        <div>
                                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            Result Formula Type
                                          </label>
                                          <select
                                            value={rule.formulaConfig?.type || ''}
                                            onChange={(e) => handleConditionalFormulaTypeChange(header.id, ruleIndex, e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                          >
                                            <option value="">No formula</option>
                                            {VISUAL_FORMULA_OPTIONS.map((option) => (
                                              <option key={`${header.id}_rule_type_${ruleIndex}_${option.value}`} value={option.value}>
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
                                          <div className="flex min-w-max items-center gap-2">
                                            {rule.formulaConfig ? (
                                              (rule.formulaConfig.type === 'lookup'
                                                ? getLookupFormulaSummary(rule.formulaConfig, headers, availableOptionSourceHeadersBySheetId, headerAvailableSheets)
                                                : getFormulaPreviewTokens(rule.formulaConfig, headers)
                                              ).map((token, tokenIndex) => (
                                                <span
                                                  key={`${header.id}_rule_preview_${ruleIndex}_${tokenIndex}`}
                                                  className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${
                                                    token.kind === 'column'
                                                      ? 'bg-white text-slate-700 ring-1 ring-slate-200'
                                                      : token.kind === 'operator'
                                                        ? 'bg-[#008080]/15 text-[#0f4f4f]'
                                                        : token.kind === 'separator'
                                                          ? 'bg-amber-100 text-amber-700'
                                                          : 'bg-slate-900 text-white'
                                                  }`}
                                                >
                                                  {token.label}
                                                </span>
                                              ))
                                            ) : (
                                              <span className="text-sm text-slate-400">No branch formula selected</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      {rule.formulaConfig && (
                                        <>
                                          {rule.formulaConfig.type === 'percentage' ? (
                                            <div className="grid gap-3 lg:grid-cols-[180px_minmax(0,1fr)_minmax(0,1fr)]">
                                              <div>
                                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                  Numerator Type
                                                </label>
                                                <select
                                                  value={rule.formulaConfig.numeratorType || 'single'}
                                                  onChange={(e) => handleConditionalPercentageNumeratorTypeChange(header.id, ruleIndex, e.target.value)}
                                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                                >
                                                  <option value="single">Single value</option>
                                                  <option value="sum">Sum</option>
                                                  <option value="subtract">Subtract</option>
                                                  <option value="multiply">Multiply</option>
                                                  <option value="divide">Divide</option>
                                                  <option value="average">Average</option>
                                                  <option value="min">Lowest value</option>
                                                  <option value="max">Highest value</option>
                                                </select>
                                              </div>
                                              <div>
                                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                  Add Numerator Column
                                                </label>
                                                <select
                                                  defaultValue=""
                                                  onChange={(e) => {
                                                    handleConditionalFormulaOperandAdd(header.id, ruleIndex, e.target.value);
                                                    e.target.value = '';
                                                  }}
                                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                                >
                                                  <option value="">Choose a numerator column</option>
                                                  {getFormulaOperandChoices(header, headers, rule.formulaConfig).map((choice) => (
                                                    <option key={`${header.id}_rule_add_${ruleIndex}_${choice.value}`} value={choice.value}>
                                                      {choice.label}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                              <div>
                                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                  Denominator Column
                                                </label>
                                                <select
                                                  value={rule.formulaConfig.denominator || ''}
                                                  onChange={(e) => handleConditionalPercentageDenominatorChange(header.id, ruleIndex, e.target.value)}
                                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                                >
                                                  <option value="">Choose a denominator column</option>
                                                  {getFormulaOperandChoices(header, headers, rule.formulaConfig).map((choice) => (
                                                    <option key={`${header.id}_rule_denominator_${ruleIndex}_${choice.value}`} value={choice.value}>
                                                      {choice.label}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            </div>
                                          ) : rule.formulaConfig.type === 'lookup' ? (
                                            <div className="grid gap-3 lg:grid-cols-2">
                                              <div>
                                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                  Source Table
                                                </label>
                                                <select
                                                  value={rule.formulaConfig.lookupSheetId || ''}
                                                  onChange={(e) => handleConditionalLookupFormulaChange(header.id, ruleIndex, 'lookupSheetId', e.target.value)}
                                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                                >
                                                  <option value="">Choose a table</option>
                                                  {workspaceScopedHeaderAvailableSheets
                                                    .map((sheetItem) => (
                                                      <option key={`${header.id}_rule_lookup_sheet_${ruleIndex}_${sheetItem.id}`} value={sheetItem.id}>
                                                        {sheetItem.title}
                                                      </option>
                                                    ))}
                                                </select>
                                              </div>
                                              <div>
                                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                  Current Row Match Column
                                                </label>
                                                <select
                                                  value={rule.formulaConfig.lookupMatchCurrentHeaderId || ''}
                                                  onChange={(e) => handleConditionalLookupFormulaChange(header.id, ruleIndex, 'lookupMatchCurrentHeaderId', e.target.value)}
                                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                                >
                                                  <option value="">Choose a column</option>
                                                  {headers
                                                    .filter((candidate) => candidate.id !== header.id && `${candidate.label || ''}`.trim())
                                                    .map((candidate) => (
                                                      <option key={`${header.id}_rule_lookup_current_${ruleIndex}_${candidate.id}`} value={candidate.id}>
                                                        {candidate.label}
                                                      </option>
                                                    ))}
                                                </select>
                                              </div>
                                              <div>
                                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                  Source Table Match Column
                                                </label>
                                                <select
                                                  value={rule.formulaConfig.lookupMatchSourceHeaderId || ''}
                                                  onChange={(e) => handleConditionalLookupFormulaChange(header.id, ruleIndex, 'lookupMatchSourceHeaderId', e.target.value)}
                                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                                >
                                                  <option value="">Choose a source column</option>
                                                  {(availableOptionSourceHeadersBySheetId[rule.formulaConfig.lookupSheetId] || []).map((candidate) => (
                                                    <option key={`${header.id}_rule_lookup_source_${ruleIndex}_${candidate.id}`} value={candidate.id}>
                                                      {candidate.label}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                              <div>
                                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                  Returned Column
                                                </label>
                                                <select
                                                  value={rule.formulaConfig.lookupReturnHeaderId || ''}
                                                  onChange={(e) => handleConditionalLookupFormulaChange(header.id, ruleIndex, 'lookupReturnHeaderId', e.target.value)}
                                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                                >
                                                  <option value="">Choose a returned column</option>
                                                  {(availableOptionSourceHeadersBySheetId[rule.formulaConfig.lookupSheetId] || []).map((candidate) => (
                                                    <option key={`${header.id}_rule_lookup_return_${ruleIndex}_${candidate.id}`} value={candidate.id}>
                                                      {candidate.label}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            </div>
                                          ) : (
                                            <div>
                                              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                Add Source Column
                                              </label>
                                              <select
                                                defaultValue=""
                                                onChange={(e) => {
                                                  handleConditionalFormulaOperandAdd(header.id, ruleIndex, e.target.value);
                                                  e.target.value = '';
                                                }}
                                                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                              >
                                                <option value="">Choose a column</option>
                                                {getFormulaOperandChoices(header, headers, rule.formulaConfig).map((choice) => (
                                                  <option key={`${header.id}_rule_operand_${ruleIndex}_${choice.value}`} value={choice.value}>
                                                    {choice.label}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                          )}

                                          {rule.formulaConfig.type === 'concat' && (
                                            <div>
                                              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                Separator Between Values
                                              </label>
                                              <input
                                                value={rule.formulaConfig.separator ?? ' '}
                                                onChange={(e) => handleConditionalFormulaSeparatorChange(header.id, ruleIndex, e.target.value)}
                                                placeholder="Space, dash, slash..."
                                                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                              />
                                            </div>
                                          )}

                                          {rule.formulaConfig.type === 'duration_days' && (
                                            <div>
                                              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                Weekend Handling
                                              </label>
                                              <select
                                                value={rule.formulaConfig.weekendMode || 'include'}
                                                onChange={(e) => handleConditionalDurationWeekendModeChange(header.id, ruleIndex, e.target.value)}
                                                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                              >
                                                <option value="include">Include weekends</option>
                                                <option value="exclude">Exclude weekends</option>
                                              </select>
                                            </div>
                                          )}

                                          {rule.formulaConfig.type !== 'lookup' && (
                                          <div>
                                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                              {rule.formulaConfig.type === 'percentage' ? 'Numerator Columns' : 'Selected Columns'}
                                            </p>
                                            <div className="space-y-2">
                                              {getFormulaOperandList(rule.formulaConfig).length === 0 ? (
                                                <p className="text-sm text-slate-400">Pick source columns for this branch formula.</p>
                                              ) : (
                                                getFormulaOperandList(rule.formulaConfig).map((operandId) => {
                                                  const resolvedOperandId = `${operandId || ''}`.startsWith('base:') ? `${operandId}`.slice(5) : operandId;
                                                  const operandHeader = headers.find((candidate) => candidate.id === resolvedOperandId);
                                                  return (
                                                    <div key={`${header.id}_rule_operand_tag_${ruleIndex}_${operandId}`} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
                                                      <div className="min-w-0">
                                                        <p className="truncate text-sm font-semibold text-slate-700">
                                                          {operandHeader?.label || 'Unnamed column'}
                                                        </p>
                                                      </div>
                                                      <button
                                                        type="button"
                                                        onClick={() => handleConditionalFormulaOperandRemove(header.id, ruleIndex, operandId)}
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                                                        aria-label="Remove operand"
                                                      >
                                                        <X className="h-4 w-4" />
                                                      </button>
                                                    </div>
                                                  );
                                                })
                                              )}
                                            </div>
                                          </div>
                                          )}

                                          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3">
                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Stored Branch Formula</p>
                                            <div className="mt-2 overflow-x-auto">
                                              <code className="block min-w-max text-sm text-slate-700">
                                                {buildFormulaFromConfig(rule.formulaConfig, headers) || 'Select enough columns to generate a branch formula.'}
                                              </code>
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        </div>
                      ) : (
                        <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
                            <div>
                              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Summary Type
                              </label>
                              <select
                                value={header.summaryType || 'none'}
                                onChange={(e) => handleHeaderSummaryTypeChange(header.id, e.target.value)}
                                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                              >
                                {getAllowedSummaryTypes(header.type).map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Summary Preview</p>
                              <p className="mt-2 text-sm text-slate-700">
                                {header.summaryType && header.summaryType !== 'none'
                                  ? `${SUMMARY_OPTIONS.find((option) => option.value === header.summaryType)?.label || 'Summary'} will appear in the footer for this column.`
                                  : 'No summary footer is configured for this column.'}
                              </p>
                            </div>
                          </div>
                          <p className="text-xs text-slate-500">
                            Summary values are calculated from the currently visible rows in the sheet viewer, so filters update the footer totals automatically.
                          </p>
                        </div>
                      )}
                      <p className="text-xs text-slate-500">
                        {(headerEditorModes[header.id] || 'formula') === 'formula'
                          ? 'Choose the formula on the left, then add source columns in order. The visual preview scrolls horizontally for longer formulas.'
                          : 'Use summaries for spreadsheet-style column totals, averages, minimums, maximums, or counts.'}
                      </p>
                    </div>
                    </HeaderSection>

                    {(header.type === 'select' || header.type === 'buttons' || header.type === 'multiselect') && (
                      <HeaderSection
                        title="Options"
                        description="Manage manual options, imported options, and conditional option rules."
                      >
                    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3">
                        {(() => {
                          const baseOptionSource = getHeaderOptionSourceDraft(header.optionSource);
                          const baseSourceHeaders = availableOptionSourceHeadersBySheetId[baseOptionSource.sheetId] || [];
                          const baseSourceFilters = Array.isArray(baseOptionSource.sourceFilters) ? baseOptionSource.sourceFilters : [];

                          return (
                            <>
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Options From Resolved List Header
                          </label>
                          <select
                            value={`${header.optionsFromHeaderId || ''}`.trim()}
                            onChange={(e) => handleHeaderOptionsFromHeaderChange(header.id, e.target.value)}
                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            <option value="">No resolved list header</option>
                            {headers
                              .filter((candidate) => (
                                candidate.id !== header.id &&
                                (candidate.type === 'list' || candidate.type === 'multiselect')
                              ))
                              .map((candidate) => (
                                <option key={`${header.id}_options_from_${candidate.id}`} value={candidate.id}>
                                  {candidate.label || 'Untitled column'} ({candidate.type})
                                </option>
                              ))}
                          </select>
                          <p className="mt-2 text-xs text-slate-500">
                            Use a helper header whose formula resolves to a list, then map that list into this field&apos;s options.
                          </p>
                        </div>

                        <div className="grid gap-3 lg:grid-cols-2">
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Source Sheet
                            </label>
                            <select
                              value={getHeaderOptionSourceDraft(header.optionSource).sheetId}
                              onChange={(e) => handleHeaderOptionSourceChange(header.id, 'sheetId', e.target.value)}
                              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                            >
                              <option value="">Manual options only</option>
                              {headerAvailableSheets.map((sheetItem) => (
                                <option key={`${header.id}_source_sheet_${sheetItem.id}`} value={sheetItem.id}>
                                  {sheetItem.title || 'Untitled sheet'}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Source Column
                            </label>
                            <select
                              value={getHeaderOptionSourceDraft(header.optionSource).headerId}
                              disabled={!getHeaderOptionSourceDraft(header.optionSource).sheetId}
                              onChange={(e) => handleHeaderOptionSourceChange(header.id, 'headerId', e.target.value)}
                              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                            >
                              <option value="">Choose a column</option>
                              {(availableOptionSourceHeadersBySheetId[getHeaderOptionSourceDraft(header.optionSource).sheetId] || []).map((sourceHeader) => (
                                <option key={`${header.id}_source_header_${sourceHeader.id}`} value={sourceHeader.id}>
                                  {sourceHeader.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500">
                          Imported values are merged with any manual options below. Multi-select source columns are flattened into individual choices.
                        </p>

                        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Source Row Filters</p>
                              <p className="mt-1 text-xs text-slate-500">
                                Add one or more source-row filters and combine them with AND or OR logic.
                              </p>
                            </div>
                            <button
                              type="button"
                              disabled={!baseOptionSource.sheetId}
                              onClick={() => handleAddHeaderOptionSourceFilter(header.id)}
                              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-[#008080]/35 hover:text-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Add Filter
                            </button>
                          </div>

                          {baseSourceFilters.length > 1 && (
                            <div className="mt-3 max-w-[220px]">
                              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Filter Logic
                              </label>
                              <select
                                value={baseOptionSource.filterLogic || 'and'}
                                onChange={(e) => handleHeaderOptionSourceFilterLogicChange(header.id, e.target.value)}
                                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                              >
                                <option value="and">Match all filters (AND)</option>
                                <option value="or">Match any filter (OR)</option>
                              </select>
                            </div>
                          )}

                          {baseSourceFilters.length ? (
                            <div className="mt-3 space-y-3">
                              {baseSourceFilters.map((sourceFilter, filterIndex) => (
                                <div key={`${header.id}_base_source_filter_${filterIndex}`} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3">
                                  <div className="grid gap-3 lg:grid-cols-[minmax(0,1.15fr)_160px_minmax(0,1fr)_44px]">
                                    <div>
                                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Source Match Column
                                      </label>
                                      <select
                                        value={sourceFilter?.sourceHeaderId || ''}
                                        onChange={(e) => handleHeaderOptionSourceFilterChange(header.id, filterIndex, 'sourceHeaderId', e.target.value)}
                                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                      >
                                        <option value="">Choose a source column</option>
                                        {baseSourceHeaders.map((sourceHeader) => (
                                          <option key={`${header.id}_base_filter_source_${filterIndex}_${sourceHeader.id}`} value={sourceHeader.id}>
                                            {sourceHeader.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Operator
                                      </label>
                                      <select
                                        value={sourceFilter?.operator || 'equals'}
                                        onChange={(e) => handleHeaderOptionSourceFilterChange(header.id, filterIndex, 'operator', e.target.value)}
                                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                      >
                                        <option value="equals">Equals</option>
                                        <option value="notEquals">Not equals</option>
                                        <option value="contains">Contains</option>
                                        <option value="notContains">Not contains</option>
                                        <option value="greaterThan">Greater than</option>
                                        <option value="lessThan">Less than</option>
                                        <option value="isEmpty">Is empty</option>
                                        <option value="isNotEmpty">Is not empty</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Compare Source Match To
                                      </label>
                                      <select
                                        value={sourceFilter?.valueMode || 'header'}
                                        disabled={!sourceFilter?.sourceHeaderId}
                                        onChange={(e) => handleHeaderOptionSourceFilterChange(header.id, filterIndex, 'valueMode', e.target.value)}
                                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                      >
                                        <option value="header">Current row column</option>
                                        <option value="static">Fixed value</option>
                                      </select>
                                    </div>
                                    <div className="flex items-end">
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveHeaderOptionSourceFilter(header.id, filterIndex)}
                                        className="inline-flex h-[52px] w-full items-center justify-center rounded-2xl border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                                        aria-label="Remove source filter"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>

                                  {sourceFilter?.sourceHeaderId && (
                                    <div>
                                      {sourceFilter?.valueMode === 'static' ? (
                                        <>
                                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            Fixed Match Value
                                          </label>
                                          <input
                                            value={sourceFilter?.value || ''}
                                            disabled={sourceFilter?.operator === 'isEmpty' || sourceFilter?.operator === 'isNotEmpty'}
                                            onChange={(e) => handleHeaderOptionSourceFilterChange(header.id, filterIndex, 'value', e.target.value)}
                                            placeholder={sourceFilter?.operator === 'isEmpty' || sourceFilter?.operator === 'isNotEmpty' ? 'No value needed' : 'Value from the source row filter'}
                                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                          />
                                        </>
                                      ) : (
                                        <>
                                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            Current Row Column
                                          </label>
                                          <select
                                            value={sourceFilter?.headerId || ''}
                                            disabled={sourceFilter?.operator === 'isEmpty' || sourceFilter?.operator === 'isNotEmpty'}
                                            onChange={(e) => handleHeaderOptionSourceFilterChange(header.id, filterIndex, 'headerId', e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                          >
                                            <option value="">Choose a column</option>
                                            {headers
                                              .filter((candidate) => candidate.id !== header.id && `${candidate.label || ''}`.trim())
                                              .map((candidate) => (
                                                <option key={`${header.id}_base_filter_header_${filterIndex}_${candidate.id}`} value={candidate.id}>
                                                  {candidate.label}
                                                </option>
                                              ))}
                                          </select>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-3 text-xs text-slate-500">No source row filters configured.</p>
                          )}
                        </div>

                        <div className="flex gap-3">
                          <input
                            value={header.pendingOption || ''}
                            onChange={(e) => handleHeaderPendingOptionChange(header.id, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddHeaderOption(header.id);
                              }
                            }}
                            placeholder="Add option or paste comma-separated options"
                            className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          />
                          <button
                            type="button"
                            onClick={() => handleAddHeaderOption(header.id)}
                            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:border-[#008080]/35 hover:text-[#008080]"
                          >
                            <Plus className="h-4 w-4" />
                            Add
                          </button>
                        </div>
                        <p className="text-xs text-slate-500">
                          Paste a comma-separated or line-separated list, then click `Add` to split it into individual options.
                        </p>
                        {(header.options || []).length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {header.options.map((option) => (
                              <span
                                key={option}
                                className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700"
                              >
                                {option}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveHeaderOption(header.id, option)}
                                  className="text-slate-500 hover:text-rose-600"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        {!!(resolvedHeaderOptionsById[header.id] || []).length && (
                          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-3 py-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Resolved Options</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {(resolvedHeaderOptionsById[header.id] || []).map((option) => (
                                <span
                                  key={`${header.id}_resolved_${option}`}
                                  className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm"
                                >
                                  {option}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                            </>
                          );
                        })()}
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Conditional Options</p>
                              <p className="mt-1 text-xs text-slate-500">
                                When a row value matches, these rule options replace the base options above. Matching rules merge.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleAddConditionalOptionRule(header.id)}
                              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-[#008080]/35 hover:text-[#008080]"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Add Rule
                            </button>
                          </div>

                          {(header.conditionalOptions || []).length > 0 ? (
                            <div className="mt-3 space-y-3">
                              {(header.conditionalOptions || []).map((rule, ruleIndex) => {
                                const ruleSource = getHeaderOptionSourceDraft(rule.optionSource);
                                const ruleSourceHeaders = availableOptionSourceHeadersBySheetId[ruleSource.sheetId] || [];
                                const ruleSourceFilters = Array.isArray(ruleSource.sourceFilters) ? ruleSource.sourceFilters : [];
                                return (
                                  <div key={`${header.id}_conditional_option_${ruleIndex}`} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3">
                                    <div className="grid gap-3 lg:grid-cols-[minmax(0,1.2fr)_160px_minmax(0,1fr)_44px]">
                                      <div>
                                        <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                          When Column
                                        </label>
                                        <select
                                          value={rule.whenHeaderId || ''}
                                          onChange={(e) => handleConditionalOptionRuleChange(header.id, ruleIndex, 'whenHeaderId', e.target.value)}
                                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                        >
                                          <option value="">Choose a column</option>
                                          {headers
                                            .filter((candidate) => candidate.id !== header.id && `${candidate.label || ''}`.trim())
                                            .map((candidate) => (
                                              <option key={`${header.id}_conditional_option_source_${candidate.id}`} value={candidate.id}>
                                                {candidate.label}
                                              </option>
                                            ))}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                          Operator
                                        </label>
                                        <select
                                          value={rule.operator || 'equals'}
                                          onChange={(e) => handleConditionalOptionRuleChange(header.id, ruleIndex, 'operator', e.target.value)}
                                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                        >
                                          <option value="equals">Equals</option>
                                          <option value="notEquals">Not equals</option>
                                          <option value="contains">Contains</option>
                                          <option value="notContains">Not contains</option>
                                          <option value="greaterThan">Greater than</option>
                                          <option value="lessThan">Less than</option>
                                          <option value="isEmpty">Is empty</option>
                                          <option value="isNotEmpty">Is not empty</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                          Match Value
                                        </label>
                                        <input
                                          value={rule.value || ''}
                                          disabled={rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty'}
                                          onChange={(e) => handleConditionalOptionRuleChange(header.id, ruleIndex, 'value', e.target.value)}
                                          placeholder={rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty' ? 'No value needed' : 'Value to compare'}
                                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                        />
                                      </div>
                                      <div className="flex items-end">
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveConditionalOptionRule(header.id, ruleIndex)}
                                          className="inline-flex h-[52px] w-full items-center justify-center rounded-2xl border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                                          aria-label="Remove conditional option rule"
                                        >
                                          <X className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>

                                    <div className="grid gap-3 lg:grid-cols-2">
                                      <div>
                                        <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                          Rule Source Sheet
                                        </label>
                                        <select
                                          value={ruleSource.sheetId}
                                          onChange={(e) => handleConditionalOptionRuleSourceChange(header.id, ruleIndex, 'sheetId', e.target.value)}
                                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                        >
                                          <option value="">No source sheet</option>
                                          {headerAvailableSheets.map((sheetItem) => (
                                            <option key={`${header.id}_conditional_rule_sheet_${ruleIndex}_${sheetItem.id}`} value={sheetItem.id}>
                                              {sheetItem.title || 'Untitled sheet'}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                          Rule Source Column
                                        </label>
                                        <select
                                          value={ruleSource.headerId}
                                          disabled={!ruleSource.sheetId}
                                          onChange={(e) => handleConditionalOptionRuleSourceChange(header.id, ruleIndex, 'headerId', e.target.value)}
                                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                        >
                                          <option value="">Choose a column</option>
                                          {ruleSourceHeaders.map((sourceHeader) => (
                                            <option key={`${header.id}_conditional_rule_source_${ruleIndex}_${sourceHeader.id}`} value={sourceHeader.id}>
                                              {sourceHeader.label}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>

                                    <div>
                                      <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                        Rule Manual Options
                                      </label>
                                      <div className="flex gap-3">
                                        <input
                                          value={rule.pendingOption || ''}
                                          onChange={(e) => handleConditionalOptionRulePendingOptionChange(header.id, ruleIndex, e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              handleAddConditionalOptionRuleOption(header.id, ruleIndex);
                                            }
                                          }}
                                          placeholder="Add option or paste comma-separated options"
                                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => handleAddConditionalOptionRuleOption(header.id, ruleIndex)}
                                          className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:border-[#008080]/35 hover:text-[#008080]"
                                        >
                                          <Plus className="h-4 w-4" />
                                          Add
                                        </button>
                                      </div>
                                      <p className="mt-2 text-xs text-slate-500">
                                        Paste a comma-separated or line-separated list, then click `Add` to split it into individual rule options.
                                      </p>
                                      {(rule.options || []).length > 0 && (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                          {rule.options.map((option) => (
                                            <span
                                              key={`${header.id}_rule_${ruleIndex}_manual_${option}`}
                                              className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700"
                                            >
                                              {option}
                                              <button
                                                type="button"
                                                onClick={() => handleRemoveConditionalOptionRuleOption(header.id, ruleIndex, option)}
                                                className="text-slate-500 hover:text-rose-600"
                                              >
                                                <X className="h-3.5 w-3.5" />
                                              </button>
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>

                                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-3">
                                      <div className="flex items-start justify-between gap-3">
                                        <div>
                                          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Rule Source Filters</p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            Filter imported rule options with one or more source columns using AND or OR logic.
                                          </p>
                                        </div>
                                        <button
                                          type="button"
                                          disabled={!ruleSource.sheetId}
                                          onClick={() => handleAddConditionalOptionRuleSourceFilter(header.id, ruleIndex)}
                                          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-[#008080]/35 hover:text-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                        >
                                          <Plus className="h-3.5 w-3.5" />
                                          Add Filter
                                        </button>
                                      </div>

                                      {ruleSourceFilters.length > 1 && (
                                        <div className="mt-3 max-w-[220px]">
                                          <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                            Filter Logic
                                          </label>
                                          <select
                                            value={ruleSource.filterLogic || 'and'}
                                            onChange={(e) => handleConditionalOptionRuleSourceFilterLogicChange(header.id, ruleIndex, e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                          >
                                            <option value="and">Match all filters (AND)</option>
                                            <option value="or">Match any filter (OR)</option>
                                          </select>
                                        </div>
                                      )}

                                      {ruleSourceFilters.length ? (
                                        <div className="mt-3 space-y-3">
                                          {ruleSourceFilters.map((sourceFilter, filterIndex) => (
                                            <div key={`${header.id}_conditional_rule_source_filter_${ruleIndex}_${filterIndex}`} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3">
                                              <div className="grid gap-3 lg:grid-cols-[minmax(0,1.15fr)_160px_minmax(0,1fr)_44px]">
                                                <div>
                                                  <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                                    Source Match Column
                                                  </label>
                                                  <select
                                                    value={sourceFilter?.sourceHeaderId || ''}
                                                    onChange={(e) => handleConditionalOptionRuleSourceFilterChange(header.id, ruleIndex, filterIndex, 'sourceHeaderId', e.target.value)}
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                                  >
                                                    <option value="">Choose a source column</option>
                                                    {ruleSourceHeaders.map((sourceHeader) => (
                                                      <option key={`${header.id}_conditional_rule_filter_source_${ruleIndex}_${filterIndex}_${sourceHeader.id}`} value={sourceHeader.id}>
                                                        {sourceHeader.label}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </div>
                                                <div>
                                                  <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                                    Operator
                                                  </label>
                                                  <select
                                                    value={sourceFilter?.operator || 'equals'}
                                                    onChange={(e) => handleConditionalOptionRuleSourceFilterChange(header.id, ruleIndex, filterIndex, 'operator', e.target.value)}
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                                  >
                                                    <option value="equals">Equals</option>
                                                    <option value="notEquals">Not equals</option>
                                                    <option value="contains">Contains</option>
                                                    <option value="notContains">Not contains</option>
                                                    <option value="greaterThan">Greater than</option>
                                                    <option value="lessThan">Less than</option>
                                                    <option value="isEmpty">Is empty</option>
                                                    <option value="isNotEmpty">Is not empty</option>
                                                  </select>
                                                </div>
                                                <div>
                                                  <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                                    Compare Source Match To
                                                  </label>
                                                  <select
                                                    value={sourceFilter?.valueMode || 'header'}
                                                    disabled={!sourceFilter?.sourceHeaderId}
                                                    onChange={(e) => handleConditionalOptionRuleSourceFilterChange(header.id, ruleIndex, filterIndex, 'valueMode', e.target.value)}
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                                  >
                                                    <option value="header">Current row column</option>
                                                    <option value="static">Fixed value</option>
                                                  </select>
                                                </div>
                                                <div className="flex items-end">
                                                  <button
                                                    type="button"
                                                    onClick={() => handleRemoveConditionalOptionRuleSourceFilter(header.id, ruleIndex, filterIndex)}
                                                    className="inline-flex h-[52px] w-full items-center justify-center rounded-2xl border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                                                    aria-label="Remove rule source filter"
                                                  >
                                                    <X className="h-4 w-4" />
                                                  </button>
                                                </div>
                                              </div>

                                              {sourceFilter?.sourceHeaderId && (
                                                <div>
                                                  {sourceFilter?.valueMode === 'static' ? (
                                                    <>
                                                      <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                                        Fixed Match Value
                                                      </label>
                                                      <input
                                                        value={sourceFilter?.value || ''}
                                                        disabled={sourceFilter?.operator === 'isEmpty' || sourceFilter?.operator === 'isNotEmpty'}
                                                        onChange={(e) => handleConditionalOptionRuleSourceFilterChange(header.id, ruleIndex, filterIndex, 'value', e.target.value)}
                                                        placeholder={sourceFilter?.operator === 'isEmpty' || sourceFilter?.operator === 'isNotEmpty' ? 'No value needed' : 'Value from the source row filter'}
                                                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                                      />
                                                    </>
                                                  ) : (
                                                    <>
                                                      <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                                        Current Row Column
                                                      </label>
                                                      <select
                                                        value={sourceFilter?.headerId || ''}
                                                        disabled={sourceFilter?.operator === 'isEmpty' || sourceFilter?.operator === 'isNotEmpty'}
                                                        onChange={(e) => handleConditionalOptionRuleSourceFilterChange(header.id, ruleIndex, filterIndex, 'headerId', e.target.value)}
                                                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                                      >
                                                        <option value="">Choose a column</option>
                                                        {headers
                                                          .filter((candidate) => candidate.id !== header.id && `${candidate.label || ''}`.trim())
                                                          .map((candidate) => (
                                                            <option key={`${header.id}_conditional_rule_filter_header_${ruleIndex}_${filterIndex}_${candidate.id}`} value={candidate.id}>
                                                              {candidate.label}
                                                            </option>
                                                          ))}
                                                      </select>
                                                    </>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="mt-3 text-xs text-slate-500">No rule source filters configured.</p>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="mt-3 text-xs text-slate-500">No conditional option rules configured.</p>
                          )}
                        </div>
                      </div>
                      </HeaderSection>
                    )}

                    <HeaderSection
                      title="Conditional Behavior"
                      description="Show, hide, enable, or disable the column based on other fields."
                    >
                    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Conditional Behavior</p>
                          <p className="mt-1 text-xs text-slate-500">
                            Change this column when other column values match.
                          </p>
                        </div>
                        {header.conditionalLogic?.rules?.length ? (
                          <button
                            type="button"
                            onClick={() => handleClearConditionalLogic(header.id)}
                            className="inline-flex items-center gap-1 rounded-xl border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                          >
                            <X className="h-3.5 w-3.5" />
                            Clear
                          </button>
                        ) : null}
                      </div>

                      <div className="grid gap-3 lg:grid-cols-2">
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Behavior
                          </label>
                          <select
                            value={header.conditionalLogic?.action || 'show'}
                            onChange={(e) => handleConditionalLogicChange(header.id, 'action', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            <option value="show">Show when rule matches</option>
                            <option value="hide">Hide when rule matches</option>
                            <option value="enable">Enable when rule matches</option>
                            <option value="disable">Disable when rule matches</option>
                          </select>
                        </div>
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Match Type
                          </label>
                          <select
                            value={header.conditionalLogic?.condition || 'all'}
                            onChange={(e) => handleConditionalLogicChange(header.id, 'condition', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            <option value="all">All rules</option>
                            <option value="any">Any rule</option>
                          </select>
                        </div>
                      </div>

                      {(header.conditionalLogic?.rules || []).length > 0 ? (
                        <div className="space-y-3">
                          {(header.conditionalLogic?.rules || []).map((rule, ruleIndex) => (
                            <div key={`${header.id}_conditional_rule_${ruleIndex}`} className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 lg:grid-cols-[minmax(0,1.4fr)_160px_minmax(0,1fr)_44px]">
                              <div>
                                <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Source Column
                                </label>
                                <select
                                  value={rule.headerId || ''}
                                  onChange={(e) => handleConditionalRuleChange(header.id, ruleIndex, 'headerId', e.target.value)}
                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                >
                                  <option value="">Choose a column</option>
                                  {headers
                                    .filter((candidate) => candidate.id !== header.id && `${candidate.label || ''}`.trim())
                                    .map((candidate) => (
                                      <option key={`${header.id}_logic_source_${candidate.id}`} value={candidate.id}>
                                        {candidate.label}
                                      </option>
                                    ))}
                                </select>
                              </div>
                              <div>
                                <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Operator
                                </label>
                                <select
                                  value={rule.operator || 'equals'}
                                  onChange={(e) => handleConditionalRuleChange(header.id, ruleIndex, 'operator', e.target.value)}
                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                >
                                  <option value="equals">Equals</option>
                                  <option value="notEquals">Not equals</option>
                                  <option value="contains">Contains</option>
                                  <option value="notContains">Not contains</option>
                                  <option value="greaterThan">Greater than</option>
                                  <option value="lessThan">Less than</option>
                                  <option value="isEmpty">Is empty</option>
                                  <option value="isNotEmpty">Is not empty</option>
                                </select>
                              </div>
                              <div>
                                <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Match Value
                                </label>
                                <input
                                  value={rule.value || ''}
                                  disabled={rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty'}
                                  onChange={(e) => handleConditionalRuleChange(header.id, ruleIndex, 'value', e.target.value)}
                                  placeholder={rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty' ? 'No value needed' : 'Value to compare'}
                                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100 disabled:text-slate-400"
                                />
                              </div>
                              <div className="flex items-end">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveConditionalRule(header.id, ruleIndex)}
                                  className="inline-flex h-[52px] w-full items-center justify-center rounded-2xl border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                                  aria-label="Remove conditional rule"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                          No conditional rules yet. Add a rule to control whether this column is shown or editable.
                        </div>
                      )}

                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => handleAddConditionalRule(header.id)}
                          className="inline-flex items-center gap-2 rounded-2xl border border-dashed border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:border-[#008080]/35 hover:text-[#008080]"
                        >
                          <Plus className="h-4 w-4" />
                          Add Logic Rule
                        </button>
                        <p className="text-xs text-slate-500">
                          Preview: {previewHeaderStatesById[header.id]?.hidden
                            ? 'hidden'
                            : previewHeaderStatesById[header.id]?.readOnly
                              ? 'read only'
                              : 'active'}
                        </p>
                      </div>
                    </div>
                    </HeaderSection>

                    <button
                      type="button"
                      onClick={() => handleInsertHeader(index + 1)}
                      className="absolute -bottom-3 -right-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#008080] shadow-[0_10px_24px_-16px_rgba(0,128,128,0.45)] transition hover:border-[#008080] hover:bg-[#008080]/10"
                      aria-label={`Insert header after ${header.label || `column ${index + 1}`}`}
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddHeader}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:border-[#008080]/35 hover:text-[#008080]"
                >
                  <Plus className="h-4 w-4" />
                  Add Header
                </button>
              </div>
            </section>
            )}
          </div>

          <div>
            <div className="mb-2 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Spreadsheet Grid</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Preview the first row and resize columns directly in the table.
                </p>
              </div>
              {csvColumns.length > 0 && (
                <button
                  type="button"
                  onClick={handleDownloadCsvTemplate}
                  className="inline-flex items-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#008080]/35 hover:text-[#008080]"
                >
                  <Download className="h-4 w-4" />
                  Download CSV Template
                </button>
              )}
            </div>

            <section className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_60px_-30px_rgba(15,23,42,0.32)]">
              <div className="overflow-auto">
                <table
                  className="w-full border-collapse"
                  style={{ minWidth: `${Math.max(tableWidth, COLUMN_MIN_WIDTH)}px` }}
                >
                  <thead>
                    <tr>
                      {sanitizedHeaders.map((header, index) => (
                        <th
                          key={`${header.id}-letter`}
                          className="border-r border-b border-slate-200 bg-slate-50 px-6 py-2 text-center text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400 last:border-r-0"
                          style={{ minWidth: `${header.width}px`, width: `${header.width}px` }}
                        >
                          {getColumnLetter(index)}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {sanitizedHeaders.map((header) => {
                        const previewState = previewHeaderStatesById[header.id] || { hidden: false, readOnly: false };
                        return (
                          <th
                            key={header.id}
                            className="group relative border-r border-b border-slate-200 bg-white px-6 py-3 text-left text-[11px] font-bold uppercase tracking-[0.24em] text-slate-700 last:border-r-0"
                            style={{ minWidth: `${header.width}px`, width: `${header.width}px` }}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span>{header.label}</span>
                              <div className="flex items-center gap-2">
                                {header.isRowLabel && (
                                  <span className="rounded-full bg-[#008080]/10 px-2 py-1 text-[9px] tracking-[0.16em] text-[#008080]">Label</span>
                                )}
                                {normalizeColumnAccess(header.columnAccess) !== 'public' && (
                                  <span className="rounded-full bg-slate-100 px-2 py-1 text-[9px] tracking-[0.16em] text-slate-500">
                                    {normalizeColumnAccess(header.columnAccess)}
                                  </span>
                                )}
                                {previewState.hidden ? (
                                  <span className="rounded-full bg-slate-100 px-2 py-1 text-[9px] tracking-[0.16em] text-slate-500">Hidden</span>
                                ) : previewState.readOnly ? (
                                  <span className="rounded-full bg-amber-100 px-2 py-1 text-[9px] tracking-[0.16em] text-amber-700">Locked</span>
                                ) : null}
                              </div>
                            </div>
                            <button
                              type="button"
                              aria-label={`Resize ${header.label} column`}
                              onMouseDown={(event) => handleResizeStart(event, header.id)}
                              className="absolute inset-y-0 right-0 z-10 cursor-col-resize opacity-0 transition group-hover:opacity-100"
                              style={{ width: `${RESIZE_HANDLE_WIDTH}px` }}
                            >
                              <span className="absolute inset-y-1 right-1 w-px bg-[#008080]" />
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr className="bg-white transition-colors hover:bg-slate-50/50">
                      {sanitizedHeaders.map((header) => {
                        const previewState = previewHeaderStatesById[header.id] || { hidden: false, readOnly: false };
                        if (previewState.hidden) {
                          return (
                            <td
                              key={header.id}
                              className="border-r border-slate-100 px-4 py-3 align-top last:border-r-0"
                              style={{ minWidth: `${header.width}px`, width: `${header.width}px` }}
                            >
                              <div className="flex min-h-[42px] items-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-400">
                                Hidden by logic
                              </div>
                            </td>
                          );
                        }

                        const sharedInputClass = `w-full rounded-lg border px-3 py-2 text-sm outline-none transition ${
                          hasHeaderFormula(header) || previewState.readOnly
                            ? 'border-dashed border-slate-200 bg-slate-50 text-slate-500'
                            : 'border-slate-200 text-slate-900 focus:border-[#008080]'
                        }`;

                        return (
                          <td
                            key={header.id}
                            className="border-r border-slate-100 px-4 py-3 align-top last:border-r-0"
                            style={{ minWidth: `${header.width}px`, width: `${header.width}px` }}
                          >
                            {header.type === 'buttons' || header.type === 'yesno' || header.type === 'rog' ? (
                              <div className={`flex min-h-[42px] flex-wrap gap-2 rounded-xl p-2 ${getRogOverlayClass(header, previewRowValues[header.id])}`}>
                                {(resolvedHeaderOptionsById[header.id] || []).map((option) => {
                                  return (
                                    <button
                                      key={`${header.id}_preview_button_${option}`}
                                      type="button"
                                      onClick={() => handleFirstRowChange(header.id, option)}
                                      disabled={hasHeaderFormula(header) || previewState.readOnly}
                                      className={`${getChoiceButtonClasses({
                                        headerType: header.type,
                                        option,
                                        selectedValue: previewRowValues[header.id],
                                        disabled: hasHeaderFormula(header) || previewState.readOnly
                                      })} ${(hasHeaderFormula(header) || previewState.readOnly) ? 'opacity-60' : ''}`}
                                    >
                                      {option}
                                    </button>
                                  );
                                })}
                                {!(resolvedHeaderOptionsById[header.id] || []).length && (
                                  <div className="flex min-h-[42px] items-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-400">
                                    {header.type === 'rog' ? 'ROG choices will appear here' : 'Add options to preview buttons'}
                                  </div>
                                )}
                              </div>
                            ) : header.type === 'profile' ? (() => {
                              const profile = getProfileDisplayData(header, previewRowValues, sanitizedHeaders);
                              return (
                                <div className="flex min-h-[56px] items-center">
                                  <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2">
                                    {profile.imageUrl ? (
                                      <img
                                        src={profile.imageUrl}
                                        alt={profile.label || header.label || 'Profile'}
                                        className="h-10 w-10 rounded-full border border-slate-200 object-cover"
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                        }}
                                      />
                                    ) : (
                                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold uppercase text-slate-500">
                                        {(profile.label || header.label || '?').slice(0, 1)}
                                      </div>
                                    )}
                                    <span className="text-sm text-slate-700">
                                      {profile.label || 'Choose source columns and preview values'}
                                    </span>
                                  </div>
                                </div>
                              );
                            })() : header.type === 'image' ? (
                              <div className="space-y-3">
                                <input
                                  value={previewRowValues[header.id] ?? ''}
                                  onChange={(e) => handleFirstRowChange(header.id, e.target.value)}
                                  placeholder="Paste image URL"
                                  readOnly={hasHeaderFormula(header) || previewState.readOnly}
                                  className={`${sharedInputClass} ${getColumnAlignmentClass(normalizeHeaderTextAlign(header.textAlign))}`}
                                  style={getColumnTextStyle(header)}
                                />
                                {`${previewRowValues[header.id] || ''}`.trim() ? (
                                  <img
                                    src={previewRowValues[header.id]}
                                    alt={header.label || 'Preview'}
                                    className="h-24 w-full rounded-xl border border-slate-200 object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                ) : null}
                              </div>
                            ) : header.type === 'address' ? (
                              <div className="space-y-3">
                                <div className="relative">
                                  <input
                                    value={previewRowValues[header.id] ?? ''}
                                    onChange={(e) => handleFirstRowChange(header.id, e.target.value)}
                                    placeholder="Street, suburb, city"
                                    readOnly={hasHeaderFormula(header) || previewState.readOnly}
                                    className={`${sharedInputClass} ${getColumnAlignmentClass(normalizeHeaderTextAlign(header.textAlign))} pr-10`}
                                    style={getColumnTextStyle(header)}
                                  />
                                  {getMapSearchUrl(previewRowValues[header.id]) && (
                                    <a
                                      href={getMapSearchUrl(previewRowValues[header.id])}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#008080] transition hover:text-[#0f4f4f]"
                                      aria-label="Open map"
                                      title="Open map"
                                    >
                                      <MapPin className="h-4 w-4" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            ) : normalizeSheetFormInputMode(header.formInputMode) !== 'default' && canUseSensorInputMode(header.type) ? (
                              <div className="min-w-[260px]">
                                <SmartSensorField
                                  type={getSheetSensorFieldType(header.formInputMode)}
                                  fieldKey={header.id}
                                  value={`${previewRowValues[header.id] ?? ''}`}
                                  label={header.label || 'Scanner field'}
                                  placeholder={
                                    header.formInputMode === 'barcode'
                                      ? 'Scan barcode or type value...'
                                      : header.formInputMode === 'qrcode'
                                        ? 'Scan QR code or type value...'
                                        : 'Tap NFC tag or type value...'
                                  }
                                  textSizeClass="text-sm"
                                  alignClass={getColumnAlignmentClass(normalizeHeaderTextAlign(header.textAlign))}
                                  onValueChange={(nextValue) => handleFirstRowChange(header.id, nextValue)}
                                  readOnly={hasHeaderFormula(header) || previewState.readOnly}
                                />
                              </div>
                            ) : header.type === 'youtube' ? (
                              <div className="space-y-3">
                                <input
                                  value={previewRowValues[header.id] ?? ''}
                                  onChange={(e) => handleFirstRowChange(header.id, e.target.value)}
                                  placeholder="Paste YouTube URL"
                                  readOnly={hasHeaderFormula(header) || previewState.readOnly}
                                  className={`${sharedInputClass} ${getColumnAlignmentClass(normalizeHeaderTextAlign(header.textAlign))}`}
                                  style={getColumnTextStyle(header)}
                                />
                                {`${previewRowValues[header.id] || ''}`.trim() ? (
                                  <a
                                    href={previewRowValues[header.id]}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#008080] hover:text-[#0f4f4f]"
                                  >
                                    Open video
                                  </a>
                                ) : null}
                              </div>
                            ) : header.type === 'document' ? (
                              <div className="space-y-3">
                                <input
                                  value={previewRowValues[header.id] ?? ''}
                                  onChange={(e) => handleFirstRowChange(header.id, e.target.value)}
                                  placeholder="Paste document URL"
                                  readOnly={hasHeaderFormula(header) || previewState.readOnly}
                                  className={`${sharedInputClass} ${getColumnAlignmentClass(normalizeHeaderTextAlign(header.textAlign))}`}
                                  style={getColumnTextStyle(header)}
                                />
                                {`${previewRowValues[header.id] || ''}`.trim() ? (
                                  <a
                                    href={previewRowValues[header.id]}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#008080] hover:text-[#0f4f4f]"
                                  >
                                    Open document
                                  </a>
                                ) : null}
                              </div>
                            ) : header.type === 'percent' ? (
                              <div className="relative">
                                <input
                                  value={hasHeaderFormula(header) ? formatDisplayValue(header, previewRowValues[header.id]) : (previewRowValues[header.id] ?? '')}
                                  onChange={(e) => handleFirstRowChange(header.id, e.target.value)}
                                  placeholder={header.label}
                                  readOnly={hasHeaderFormula(header) || previewState.readOnly}
                                  className={`${sharedInputClass} ${getColumnAlignmentClass(normalizeHeaderTextAlign(header.textAlign))} pr-8`}
                                  style={getColumnTextStyle(header)}
                                />
                                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
                              </div>
                            ) : header.type === 'textarea' ? (
                              <textarea
                                rows={4}
                                value={hasHeaderFormula(header) || DECIMAL_FIELD_TYPES.has(header.type)
                                  ? (hasHeaderFormula(header) ? formatDisplayValue(header, previewRowValues[header.id]) : (previewRowValues[header.id] ?? ''))
                                  : (previewRowValues[header.id] ?? '')}
                                onChange={(e) => handleFirstRowChange(header.id, e.target.value)}
                                placeholder={header.label}
                                readOnly={hasHeaderFormula(header) || previewState.readOnly}
                                className={`${sharedInputClass} ${getColumnAlignmentClass(normalizeHeaderTextAlign(header.textAlign))}`}
                                style={getColumnTextStyle(header)}
                              />
                            ) : header.type === 'list' ? (
                              <div className="space-y-3">
                                <div className="flex gap-2">
                                  <input
                                    value={previewListDrafts[header.id] || ''}
                                    onChange={(e) => handlePreviewListDraftChange(header.id, e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddPreviewListItem(header.id);
                                      }
                                    }}
                                    placeholder="Add list item"
                                    readOnly={hasHeaderFormula(header) || previewState.readOnly}
                                    className={`${sharedInputClass} ${getColumnAlignmentClass(normalizeHeaderTextAlign(header.textAlign))}`}
                                    style={getColumnTextStyle(header)}
                                  />
                                  {!hasHeaderFormula(header) && !previewState.readOnly && (
                                    <button
                                      type="button"
                                      onClick={() => handleAddPreviewListItem(header.id)}
                                      className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#008080]/35 hover:text-[#008080]"
                                    >
                                      Add
                                    </button>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {normalizeListItems(previewRowValues[header.id]).length ? normalizeListItems(previewRowValues[header.id]).map((item, index) => (
                                    <span key={`${header.id}_preview_list_${index}`} className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                                      {item}
                                      {!hasHeaderFormula(header) && !previewState.readOnly && (
                                        <button
                                          type="button"
                                          onClick={() => handleRemovePreviewListItem(header.id, index)}
                                          className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-500 transition hover:bg-white hover:text-rose-600"
                                          aria-label={`Remove ${item}`}
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      )}
                                    </span>
                                  )) : (
                                    <span className="text-sm text-slate-400">No list items yet</span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <input
                                value={hasHeaderFormula(header) || DECIMAL_FIELD_TYPES.has(header.type)
                                  ? (hasHeaderFormula(header) ? formatDisplayValue(header, previewRowValues[header.id]) : (previewRowValues[header.id] ?? ''))
                                  : (previewRowValues[header.id] ?? '')}
                                onChange={(e) => handleFirstRowChange(header.id, e.target.value)}
                                placeholder={header.label}
                                readOnly={hasHeaderFormula(header) || previewState.readOnly}
                                className={`${sharedInputClass} ${getColumnAlignmentClass(normalizeHeaderTextAlign(header.textAlign))}`}
                                style={getColumnTextStyle(header)}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
            
            {activeBuilderPanel === 'ai' && (
            <section className="rounded-3xl border-2 border-slate-300 bg-slate-50/55 p-6 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.32)]">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">AI Skills</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Configure reusable AI report behavior for this sheet. The viewer will use this skill when people generate reports from row data and related logs.
                </p>
              </div>

              <div className="mt-5 space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Report generator</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Build a consistent report from the active row, the visible sheet sample, and the linked activity logs.
                      </p>
                    </div>
                    <label className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700">
                      <input
                        type="checkbox"
                        checked={reportSkill.enabled}
                        onChange={(e) => setReportSkill((prev) => ({ ...prev, enabled: e.target.checked }))}
                        className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                      />
                      Enabled
                    </label>
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Report title</label>
                      <input
                        value={reportSkill.title}
                        onChange={(e) => setReportSkill((prev) => ({ ...prev, title: e.target.value }))}
                        placeholder="Progress report"
                        className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Objective</label>
                      <input
                        value={reportSkill.objective}
                        onChange={(e) => setReportSkill((prev) => ({ ...prev, objective: e.target.value }))}
                        placeholder="Summarize delivery status, risks, and next steps."
                        className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                      />
                    </div>
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Scope</label>
                      <select
                        value={reportSkill.scope}
                        onChange={(e) => setReportSkill((prev) => ({ ...prev, scope: e.target.value }))}
                        className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                      >
                        {REPORT_SCOPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Tone</label>
                      <select
                        value={reportSkill.tone}
                        onChange={(e) => setReportSkill((prev) => ({ ...prev, tone: e.target.value }))}
                        className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                      >
                        {REPORT_TONE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Output format</label>
                      <select
                        value={reportSkill.format}
                        onChange={(e) => setReportSkill((prev) => ({ ...prev, format: e.target.value }))}
                        className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                      >
                        {REPORT_FORMAT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Visible row sample limit</label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={reportSkill.maxVisibleRows}
                        onChange={(e) => setReportSkill((prev) => ({ ...prev, maxVisibleRows: e.target.value }))}
                        className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">Log sample limit</label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={reportSkill.maxLogs}
                        onChange={(e) => setReportSkill((prev) => ({ ...prev, maxLogs: e.target.value }))}
                        className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="mb-2 block text-sm font-medium text-slate-700">Focus columns</label>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {sanitizedHeaders.filter((header) => header.label).length ? sanitizedHeaders.filter((header) => header.label).map((header) => (
                        <label key={`report_focus_${header.id}`} className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={reportSkill.focusHeaderIds.includes(header.id)}
                            onChange={() => setReportSkill((prev) => ({
                              ...prev,
                              focusHeaderIds: prev.focusHeaderIds.includes(header.id)
                                ? prev.focusHeaderIds.filter((item) => item !== header.id)
                                : [...prev.focusHeaderIds, header.id]
                            }))}
                            className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                          />
                          {header.label}
                        </label>
                      )) : (
                        <div className="rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
                          Add headers first to choose report focus columns.
                        </div>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      Leave all columns unchecked to let the AI use every visible column.
                    </p>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-3 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={reportSkill.includeVisibleRows}
                        onChange={(e) => setReportSkill((prev) => ({ ...prev, includeVisibleRows: e.target.checked }))}
                        className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                      />
                      Include visible rows sample
                    </label>
                    <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-3 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={reportSkill.includeRelatedLogs}
                        onChange={(e) => setReportSkill((prev) => ({ ...prev, includeRelatedLogs: e.target.checked }))}
                        className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                      />
                      Include related logs
                    </label>
                    <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-3 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={reportSkill.includeSheetLogs}
                        onChange={(e) => setReportSkill((prev) => ({ ...prev, includeSheetLogs: e.target.checked }))}
                        className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                      />
                      Add sheet-wide logs
                    </label>
                  </div>

                  <div className="mt-4">
                    <label className="mb-1 block text-sm font-medium text-slate-700">Extra instructions</label>
                    <textarea
                      rows={5}
                      value={reportSkill.instructions}
                      onChange={(e) => setReportSkill((prev) => ({ ...prev, instructions: e.target.value }))}
                      placeholder="Mention sections to include, how risks should be worded, or whether next actions should be explicit."
                      className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Automations</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Define manual or scheduled automations now. Scheduled execution can be wired next without changing the sheet schema.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setAutomations((prev) => [...prev, createDefaultAutomation('report')])}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      <Plus className="h-4 w-4" />
                      Report automation
                    </button>
                    <button
                      type="button"
                      onClick={() => setAutomations((prev) => [...prev, createDefaultAutomation('email_digest')])}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      <Plus className="h-4 w-4" />
                      Email digest
                    </button>
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  {automations.length ? automations.map((automation) => (
                    <div key={automation.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <input
                            value={automation.name}
                            onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, name: e.target.value } : item))}
                            placeholder="Weekly manager digest"
                            className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-900 outline-none transition focus:border-[#008080]"
                          />
                        </div>
                        <label className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm font-semibold text-slate-700">
                          <input
                            type="checkbox"
                            checked={automation.enabled}
                            onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, enabled: e.target.checked } : item))}
                            className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                          />
                          Enabled
                        </label>
                        <button
                          type="button"
                          onClick={() => setAutomations((prev) => prev.filter((item) => item.id !== automation.id))}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                          aria-label="Remove automation"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="mt-4 grid gap-4 md:grid-cols-3">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-slate-700">Type</label>
                          <select
                            value={automation.type}
                            onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? {
                              ...item,
                              type: e.target.value,
                              scope: e.target.value === 'email_digest' ? 'sheet-overview' : item.scope,
                              includeSheetLogs: e.target.value === 'email_digest' ? true : item.includeSheetLogs
                            } : item))}
                            className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            {AUTOMATION_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-slate-700">Trigger</label>
                          <select
                            value={automation.triggerType}
                            onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, triggerType: e.target.value } : item))}
                            className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            {AUTOMATION_TRIGGER_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-slate-700">Scope</label>
                          <select
                            value={automation.scope}
                            onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, scope: e.target.value } : item))}
                            className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            {REPORT_SCOPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {automation.triggerType === 'schedule' && (
                        <div className="mt-4 grid gap-4 md:grid-cols-4">
                          <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Cadence</label>
                            <select
                              value={automation.scheduleCadence}
                              onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, scheduleCadence: e.target.value } : item))}
                              className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                            >
                              {AUTOMATION_SCHEDULE_CADENCE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Day</label>
                            <select
                              value={automation.scheduleDay}
                              onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, scheduleDay: e.target.value } : item))}
                              disabled={automation.scheduleCadence !== 'weekly'}
                              className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100"
                            >
                              {AUTOMATION_WEEKDAY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Time</label>
                            <input
                              type="time"
                              value={automation.scheduleTime}
                              onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, scheduleTime: e.target.value } : item))}
                              className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Timezone</label>
                            <input
                              value={automation.timezone}
                              onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, timezone: e.target.value } : item))}
                              placeholder="Africa/Johannesburg"
                              className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                            />
                          </div>
                        </div>
                      )}

                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-slate-700">Tone</label>
                          <select
                            value={automation.tone}
                            onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, tone: e.target.value } : item))}
                            className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            {REPORT_TONE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-slate-700">Output format</label>
                          <select
                            value={automation.format}
                            onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, format: e.target.value } : item))}
                            className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            {REPORT_FORMAT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {automation.type === 'email_digest' && (
                        <div className="mt-4">
                          <label className="mb-1 block text-sm font-medium text-slate-700">Recipient emails</label>
                          <input
                            value={automation.recipientsText}
                            onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, recipientsText: e.target.value } : item))}
                            placeholder="manager@example.com, teamlead@example.com"
                            className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          />
                        </div>
                      )}

                      <div className="mt-4">
                        <label className="mb-2 block text-sm font-medium text-slate-700">Focus columns</label>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {sanitizedHeaders.filter((header) => header.label).map((header) => (
                            <label key={`${automation.id}_${header.id}`} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                              <input
                                type="checkbox"
                                checked={automation.focusHeaderIds.includes(header.id)}
                                onChange={() => setAutomations((prev) => prev.map((item) => item.id === automation.id ? {
                                  ...item,
                                  focusHeaderIds: item.focusHeaderIds.includes(header.id)
                                    ? item.focusHeaderIds.filter((value) => value !== header.id)
                                    : [...item.focusHeaderIds, header.id]
                                } : item))}
                                className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                              />
                              {header.label}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 md:grid-cols-3">
                        <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={automation.includeVisibleRows}
                            onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, includeVisibleRows: e.target.checked } : item))}
                            className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                          />
                          Include visible rows
                        </label>
                        <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={automation.includeRelatedLogs}
                            onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, includeRelatedLogs: e.target.checked } : item))}
                            className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                          />
                          Include related logs
                        </label>
                        <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={automation.includeSheetLogs}
                            onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, includeSheetLogs: e.target.checked } : item))}
                            className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                          />
                          Include sheet logs
                        </label>
                      </div>

                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-slate-700">Visible row limit</label>
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={automation.maxVisibleRows}
                            onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, maxVisibleRows: e.target.value } : item))}
                            className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-slate-700">Log limit</label>
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={automation.maxLogs}
                            onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, maxLogs: e.target.value } : item))}
                            className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <label className="mb-1 block text-sm font-medium text-slate-700">Instructions</label>
                        <textarea
                          rows={4}
                          value={automation.instructions}
                          onChange={(e) => setAutomations((prev) => prev.map((item) => item.id === automation.id ? { ...item, instructions: e.target.value } : item))}
                          placeholder={automation.type === 'email_digest' ? 'Summarize the week and highlight risks for the manager.' : 'Generate a focused report with next actions.'}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        />
                      </div>
                    </div>
                  )) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
                      No automations yet. Add a report or email digest automation to start defining reusable sheet workflows.
                    </div>
                  )}
                </div>
              </div>
            </section>
            )}
            {activeBuilderPanel === 'forms' && (
            <section className="rounded-3xl border-2 border-slate-300 bg-slate-50/55 p-6 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.32)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Forms</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Configure the shareable sheet-backed form separately from general sheet setup.
                  </p>
                </div>
                {isEditing && sheetFormConfig.enabled && (
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    Live at /sheet-form/{id}
                  </span>
                )}
              </div>

              <div className="mt-5 space-y-4">
                <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <input
                    type="checkbox"
                    checked={sheetFormConfig.enabled}
                    onChange={(e) => setSheetFormConfig((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                      enabledHeaderIds: e.target.checked && (!Array.isArray(prev.enabledHeaderIds) || prev.enabledHeaderIds.length === 0)
                        ? eligibleFormHeaders.map((header) => header.id)
                        : prev.enabledHeaderIds,
                      formLayout: e.target.checked && (!Array.isArray(prev.formLayout) || prev.formLayout.length === 0)
                        ? sheetFormService.getDefaultLayout(eligibleFormHeaders)
                        : prev.formLayout
                    }))}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">Enable linked response form</span>
                    <span className="mt-1 block text-xs text-slate-500">
                      This publishes a clean form that writes rows into the sheet without exposing the full sheet UI.
                    </span>
                  </span>
                </label>

                {sheetFormConfig.enabled && (
                  <>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Form Access</label>
                        <select
                          value={sheetFormConfig.accessMode}
                          onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, accessMode: e.target.value }))}
                          disabled={isSheetFormAccessLocked}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="public">Public</option>
                          <option value="internal">Internal (sign-in required)</option>
                        </select>
                        <p className="mt-2 text-xs text-slate-500">
                          Public forms may collect responses without exposing the backing sheet. Internal forms require sign-in and sheet access.
                        </p>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Submission Mode</label>
                        <select
                          value={normalizeSheetFormSubmissionMode(sheetFormConfig.submissionMode)}
                          onChange={(e) => setSheetFormConfig((prev) => ({
                            ...prev,
                            submissionMode: normalizeSheetFormSubmissionMode(e.target.value),
                            minimumRepeatableRows: normalizeMinimumRepeatableRows(prev.minimumRepeatableRows)
                          }))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="singleRow">Single row</option>
                          <option value="modular">Modular</option>
                        </select>
                        <p className="mt-2 text-xs text-slate-500">
                          Modular mode lets one submission combine shared fields with a repeatable detail module that creates several sheet rows.
                        </p>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div className="max-w-xl">
                          <p className="text-sm font-semibold text-slate-800">Availability Period</p>
                          <p className="mt-1 text-xs text-slate-500">
                            Leave both fields empty to keep this form available whenever the link can be opened.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSheetFormConfig((prev) => ({
                            ...prev,
                            availabilityStartAt: '',
                            availabilityEndAt: ''
                          }))}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                        >
                          <RotateCcw className="h-4 w-4" />
                          Clear Period
                        </button>
                      </div>
                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="mb-2 block text-sm font-medium text-slate-700">Start Date & Time</label>
                          <input
                            type="datetime-local"
                            value={sheetFormConfig.availabilityStartAt || ''}
                            onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, availabilityStartAt: e.target.value }))}
                            className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          />
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-medium text-slate-700">End Date & Time</label>
                          <input
                            type="datetime-local"
                            value={sheetFormConfig.availabilityEndAt || ''}
                            onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, availabilityEndAt: e.target.value }))}
                            className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={sheetFormConfig.showShellHeader !== false}
                          onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, showShellHeader: e.target.checked }))}
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                        />
                        <span>
                          <span className="block text-sm font-semibold text-slate-800">Show form shell header</span>
                          <span className="mt-1 block text-xs text-slate-500">
                            Toggle the sticky top header with branding, share actions, and sign-in/profile controls.
                          </span>
                        </span>
                      </label>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={sheetFormConfig.showFormIntro !== false}
                          onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, showFormIntro: e.target.checked }))}
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                        />
                        <span>
                          <span className="block text-sm font-semibold text-slate-800">Show form intro section</span>
                          <span className="mt-1 block text-xs text-slate-500">
                            Toggle the form card that shows the title, description, visibility, and linked sheet name above the fields.
                          </span>
                        </span>
                      </label>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                      <div className="flex items-start gap-4">
                        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                          {normalizeSheetFormLogoUrl(sheetFormConfig.logoUrl) ? (
                            <img
                              src={sheetFormConfig.logoUrl}
                              alt="Form logo preview"
                              className="h-full w-full object-contain p-2"
                            />
                          ) : (
                            <span className="px-3 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                              Default Logo
                            </span>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-800">Custom form logo</p>
                          <p className="mt-1 text-xs text-slate-500">
                            Upload a logo for the published form header and footer. Clear it to fall back to the default ParrotSheets branding.
                          </p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                disabled={uploadingFormLayoutItemId === 'form_logo'}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleSheetFormLogoUpload(file);
                                  }
                                  e.target.value = '';
                                }}
                              />
                              <ImagePlus className="h-4 w-4" />
                              {uploadingFormLayoutItemId === 'form_logo' ? 'Uploading...' : 'Upload Logo'}
                            </label>
                            <button
                              type="button"
                              onClick={() => setSheetFormConfig((prev) => ({ ...prev, logoUrl: '' }))}
                              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                            >
                              <RotateCcw className="h-4 w-4" />
                              Use Default
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Submit Button</label>
                        <input
                          value={sheetFormConfig.submitLabel}
                          onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, submitLabel: e.target.value }))}
                          placeholder="Submit"
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        />
                      </div>
                      {normalizeSheetFormSubmissionMode(sheetFormConfig.submissionMode) === 'modular' && (
                        <div>
                          <label className="mb-2 block text-sm font-medium text-slate-700">Initial Detail Rows</label>
                          <input
                            type="number"
                            min="1"
                            max="25"
                            value={normalizeMinimumRepeatableRows(sheetFormConfig.minimumRepeatableRows)}
                            onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, minimumRepeatableRows: normalizeMinimumRepeatableRows(e.target.value) }))}
                            className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          />
                          <p className="mt-2 text-xs text-slate-500">
                            Controls how many detail rows are created when the module starts.
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">Standard Prefill Selector</p>
                          <p className="mt-1 text-xs text-slate-500">
                            Make the source-row selector a permanent part of this published form instead of only showing it when launched from a row action.
                          </p>
                        </div>
                        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                          <input
                            type="checkbox"
                            checked={normalizeSheetFormPrefillSelectorConfig(sheetFormConfig.prefillSelectorConfig).enabled}
                            onChange={(e) => setSheetFormConfig((prev) => ({
                              ...prev,
                              prefillSelectorConfig: {
                                ...normalizeSheetFormPrefillSelectorConfig(prev.prefillSelectorConfig),
                                enabled: e.target.checked
                              }
                            }))}
                            className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                          />
                          <span>Enable</span>
                        </label>
                      </div>

                      {!id ? (
                        <p className="mt-4 rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
                          Save the target sheet first, then choose which source action should power the selector.
                        </p>
                      ) : availableFormPrefillSelectorSources.length === 0 ? (
                        <p className="mt-4 rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
                          No saved `Add row` actions in this workspace currently target this sheet with selector prefills enabled.
                        </p>
                      ) : (
                        <div className="mt-4 grid gap-4 md:grid-cols-2">
                          <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700">Source Sheet</label>
                            <select
                              value={normalizeSheetFormPrefillSelectorConfig(sheetFormConfig.prefillSelectorConfig).sourceSheetId}
                              onChange={(e) => {
                                const nextSource = availableFormPrefillSelectorSources.find((source) => source.sheetId === e.target.value) || null;
                                setSheetFormConfig((prev) => ({
                                  ...prev,
                                  prefillSelectorConfig: {
                                    ...normalizeSheetFormPrefillSelectorConfig(prev.prefillSelectorConfig),
                                    sourceSheetId: e.target.value,
                                    actionId: nextSource?.actions?.[0]?.id || ''
                                  }
                                }));
                              }}
                              className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                            >
                              <option value="">Select source sheet</option>
                              {availableFormPrefillSelectorSources.map((source) => (
                                <option key={`form_prefill_source_${source.sheetId}`} value={source.sheetId}>{source.sheetTitle}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700">Source Action</label>
                            <select
                              value={normalizeSheetFormPrefillSelectorConfig(sheetFormConfig.prefillSelectorConfig).actionId}
                              onChange={(e) => setSheetFormConfig((prev) => ({
                                ...prev,
                                prefillSelectorConfig: {
                                  ...normalizeSheetFormPrefillSelectorConfig(prev.prefillSelectorConfig),
                                  actionId: e.target.value
                                }
                              }))}
                              disabled={!selectedFormPrefillSelectorSource}
                              className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100"
                            >
                              {!selectedFormPrefillSelectorSource ? (
                                <option value="">Select source sheet first</option>
                              ) : (
                                <>
                                  <option value="">Select source action</option>
                                  {selectedFormPrefillSelectorSource.actions.map((action) => (
                                    <option key={`form_prefill_action_${action.id}`} value={action.id}>{action.label}</option>
                                  ))}
                                </>
                              )}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    {normalizeSheetFormSubmissionMode(sheetFormConfig.submissionMode) === 'modular' && (
                      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                        <label className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={sheetFormConfig.allowDrafts === true}
                            onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, allowDrafts: e.target.checked }))}
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                          />
                          <span>
                            <span className="block text-sm font-semibold text-slate-800">Allow authenticated draft saving</span>
                            <span className="mt-1 block text-xs text-slate-500">
                              Signed-in users can save progress and resume later. Drafts are only available for modular forms.
                            </span>
                          </span>
                        </label>
                      </div>
                    )}

                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Form Title</label>
                        <input
                          value={sheetFormConfig.title}
                          onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, title: e.target.value }))}
                          placeholder={title || 'Weekly Check-In'}
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Success Title</label>
                        <input
                          value={sheetFormConfig.successTitle}
                          onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, successTitle: e.target.value }))}
                          placeholder="Submission received"
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Form Description</label>
                        <textarea
                          rows={4}
                          value={sheetFormConfig.description}
                          onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, description: e.target.value }))}
                          placeholder="Tell respondents what this form is for."
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Success Message</label>
                        <textarea
                          rows={4}
                          value={sheetFormConfig.successMessage}
                          onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, successMessage: e.target.value }))}
                          placeholder="Thanks. Your response has been captured."
                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        />
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">
                            {normalizeSheetFormSubmissionMode(sheetFormConfig.submissionMode) === 'modular' ? 'Shared Module Layout' : 'Form Layout'}
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            {normalizeSheetFormSubmissionMode(sheetFormConfig.submissionMode) === 'modular'
                              ? 'Arrange the fields that appear once per submission. These shared values will be copied into each generated row.'
                              : 'Arrange fields, insert divider labels, and add page breaks. Half and third widths let fields share a row in the published form.'}
                          </p>
                          {availableFormHeadersToAdd.length > 0 && (
                            <p className="mt-2 text-xs font-medium text-amber-700">
                              {availableFormHeadersToAdd.length} eligible field{availableFormHeadersToAdd.length === 1 ? '' : 's'} not yet assigned.
                            </p>
                          )}
                        </div>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                          {selectedFormFieldCount} shared
                        </span>
                      </div>

                      {eligibleFormHeaders.length === 0 ? (
                        <div className="mt-3 rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
                          Add at least one eligible column to publish a response form.
                        </div>
                      ) : (
                        <div className="mt-3 space-y-4">
                          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4">
                            <div className="flex flex-wrap items-end gap-3">
                              <div className="min-w-[220px] flex-1">
                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  {normalizeSheetFormSubmissionMode(sheetFormConfig.submissionMode) === 'modular' ? 'Add shared field' : 'Add field'}
                                </label>
                                <select
                                  value={pendingFormHeaderId}
                                  onChange={(e) => setPendingFormHeaderId(e.target.value)}
                                  disabled={availableFormHeadersToAdd.length === 0}
                                  className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100"
                                >
                                  {availableFormHeadersToAdd.length === 0 ? (
                                    <option value="">All eligible fields already added</option>
                                  ) : (
                                    <>
                                      <option value="">Select a field...</option>
                                      {availableFormHeadersToAdd.map((header) => (
                                        <option key={`sheet_form_available_${header.id}`} value={header.id}>{header.label}</option>
                                      ))}
                                    </>
                                  )}
                                </select>
                              </div>
                              <button
                                type="button"
                                onClick={handleAddFormField}
                                disabled={!pendingFormHeaderId}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <Plus className="h-4 w-4" />
                                {normalizeSheetFormSubmissionMode(sheetFormConfig.submissionMode) === 'modular' ? 'Add Shared Field' : 'Add Field'}
                              </button>
                              <button
                                type="button"
                                onClick={handleAddSectionDivider}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                              >
                                <Plus className="h-4 w-4" />
                                Add Divider
                              </button>
                              <button
                                type="button"
                                onClick={handleAddTextBlock}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                              >
                                <Plus className="h-4 w-4" />
                                Add Text Block
                              </button>
                              <button
                                type="button"
                                onClick={handleAddImageSlide}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                              >
                                <Plus className="h-4 w-4" />
                                Add Image Slide
                              </button>
                              <button
                                type="button"
                                onClick={handleAddPageBreak}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                              >
                                <Plus className="h-4 w-4" />
                                Add Page Break
                              </button>
                            </div>
                            {availableFormHeadersToAdd.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {availableFormHeadersToAdd.map((header) => (
                                  <button
                                    key={`sheet_form_toggle_${header.id}`}
                                    type="button"
                                    onClick={() => {
                                      setPendingFormHeaderId(header.id);
                                      handleToggleFormHeader(header.id);
                                    }}
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                                  >
                                    <Plus className="h-4 w-4" />
                                    {header.label}
                                  </button>
                                ))}
                              </div>
                            )}
                            <p className="mt-3 text-xs text-slate-500">
                              Formula fields, profile columns, and system columns stay out of the form and still resolve on row save.
                            </p>
                          </div>

                          <div className="space-y-2">
                            {sheetFormLayoutItems.map((item, index) => {
                              if (item.type === 'field') {
                                const header = eligibleFormHeaders.find((candidate) => candidate.id === item.headerId);
                                if (!header) return null;
                                const isRequired = sheetFormConfig.requiredHeaderIds.includes(header.id);

                                return (
                                  <div key={`sheet_form_layout_${item.id}`} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                      <div>
                                        <p className="text-sm font-semibold text-slate-800">{header.label}</p>
                                        <p className="mt-1 text-xs text-slate-500">{header.type}</p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={() => handleMoveFormLayoutItem(item.id, -1)}
                                          disabled={index === 0}
                                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                          aria-label="Move form item up"
                                        >
                                          <ArrowUp className="h-4 w-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleMoveFormLayoutItem(item.id, 1)}
                                          disabled={index === sheetFormLayoutItems.length - 1}
                                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                          aria-label="Move form item down"
                                        >
                                          <ArrowDown className="h-4 w-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveFormLayoutItem(item.id)}
                                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-500 transition hover:bg-rose-50"
                                          aria-label="Remove field from form"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                                      <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Width</label>
                                        <select
                                          value={item.width || 'full'}
                                          onChange={(e) => handleUpdateFormLayoutItem(item.id, { width: e.target.value })}
                                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                        >
                                          {SHEET_FORM_WIDTH_OPTIONS.map((option) => (
                                            <option key={`sheet_form_width_${option.value}`} value={option.value}>{option.label}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <label className="flex items-center gap-2 text-sm text-slate-700">
                                        <input
                                          type="checkbox"
                                          checked={isRequired}
                                          onChange={() => handleToggleRequiredFormHeader(header.id)}
                                          className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                                        />
                                          <span>Required</span>
                                      </label>
                                    </div>
                                  </div>
                                );
                              }

                              if (item.type === 'sectionDivider') {
                                return (
                                  <div key={`sheet_form_layout_${item.id}`} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                      <div>
                                        <p className="text-sm font-semibold text-slate-800">Section divider</p>
                                        <p className="mt-1 text-xs text-slate-500">Shows a labelled divider line in the form.</p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={() => handleMoveFormLayoutItem(item.id, -1)}
                                          disabled={index === 0}
                                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                          aria-label="Move divider up"
                                        >
                                          <ArrowUp className="h-4 w-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleMoveFormLayoutItem(item.id, 1)}
                                          disabled={index === sheetFormLayoutItems.length - 1}
                                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                          aria-label="Move divider down"
                                        >
                                          <ArrowDown className="h-4 w-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveFormLayoutItem(item.id)}
                                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-500 transition hover:bg-rose-50"
                                          aria-label="Remove divider"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                    <div>
                                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Divider Label</label>
                                      <input
                                        value={item.label || ''}
                                        onChange={(e) => handleUpdateFormLayoutItem(item.id, { label: e.target.value })}
                                        placeholder="Section"
                                        className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                      />
                                    </div>
                                  </div>
                                );
                              }

                              if (item.type === 'textBlock') {
                                return (
                                  <div key={`sheet_form_layout_${item.id}`} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                      <div>
                                        <p className="text-sm font-semibold text-slate-800">Text block</p>
                                        <p className="mt-1 text-xs text-slate-500">Add formatted copy between form fields.</p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={() => handleMoveFormLayoutItem(item.id, -1)}
                                          disabled={index === 0}
                                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                          aria-label="Move text block up"
                                        >
                                          <ArrowUp className="h-4 w-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleMoveFormLayoutItem(item.id, 1)}
                                          disabled={index === sheetFormLayoutItems.length - 1}
                                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                          aria-label="Move text block down"
                                        >
                                          <ArrowDown className="h-4 w-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveFormLayoutItem(item.id)}
                                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-500 transition hover:bg-rose-50"
                                          aria-label="Remove text block"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                    <div>
                                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Rich Text</label>
                                      <SheetFormRichTextEditor
                                        itemId={item.id}
                                        value={item.contentHtml || ''}
                                        onChange={(contentHtml) => handleUpdateFormLayoutItem(item.id, { contentHtml })}
                                      />
                                    </div>
                                  </div>
                                );
                              }

                              if (item.type === 'imageSlide') {
                                const slides = sanitizeSheetFormSlideItems(item.slides);
                                const isUploadingSlides = uploadingFormLayoutItemId === item.id;

                                return (
                                  <div key={`sheet_form_layout_${item.id}`} className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                      <div>
                                        <p className="text-sm font-semibold text-slate-800">Image slide block</p>
                                        <p className="mt-1 text-xs text-slate-500">Show property, vehicle, or event photos between fields.</p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={() => handleMoveFormLayoutItem(item.id, -1)}
                                          disabled={index === 0}
                                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                          aria-label="Move image slide up"
                                        >
                                          <ArrowUp className="h-4 w-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleMoveFormLayoutItem(item.id, 1)}
                                          disabled={index === sheetFormLayoutItems.length - 1}
                                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                          aria-label="Move image slide down"
                                        >
                                          <ArrowDown className="h-4 w-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveFormLayoutItem(item.id)}
                                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-500 transition hover:bg-rose-50"
                                          aria-label="Remove image slide"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>

                                    <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
                                      <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Slide mode</label>
                                        <select
                                          value={normalizeSheetFormSlideMode(item.slideMode)}
                                          onChange={(e) => handleUpdateFormLayoutItem(item.id, { slideMode: e.target.value })}
                                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                        >
                                          {SHEET_FORM_SLIDE_MODE_OPTIONS.map((option) => (
                                            <option key={`${item.id}_slide_mode_${option.value}`} value={option.value}>{option.label}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Auto advance seconds</label>
                                        <input
                                          type="number"
                                          min="2"
                                          max="30"
                                          value={normalizeSheetFormSlideIntervalSeconds(item.autoAdvanceSeconds)}
                                          disabled={normalizeSheetFormSlideMode(item.slideMode) !== 'auto'}
                                          onChange={(e) => handleUpdateFormLayoutItem(item.id, { autoAdvanceSeconds: e.target.value })}
                                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100"
                                        />
                                      </div>
                                      <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-4 py-3 text-sm font-semibold transition ${
                                        isUploadingSlides
                                          ? 'border-slate-200 bg-slate-100 text-slate-400'
                                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                                      }`}>
                                        <input
                                          type="file"
                                          accept="image/*"
                                          multiple
                                          className="hidden"
                                          disabled={isUploadingSlides}
                                          onChange={(e) => {
                                            const files = e.target.files;
                                            if (files?.length) {
                                              handleUploadFormSlideImages(item.id, files);
                                            }
                                            e.target.value = '';
                                          }}
                                        />
                                        <Plus className="h-4 w-4" />
                                        <span>{isUploadingSlides ? 'Uploading...' : 'Add images'}</span>
                                      </label>
                                    </div>

                                    {slides.length ? (
                                      <div className="grid gap-3 md:grid-cols-3">
                                        {slides.map((slide, slideIndex) => (
                                          <div key={slide.id} className="rounded-2xl border border-slate-200 bg-white p-3">
                                            <img
                                              src={slide.url}
                                              alt={slide.alt || `Slide ${slideIndex + 1}`}
                                              className="h-36 w-full rounded-xl object-cover"
                                            />
                                            <input
                                              value={slide.alt || ''}
                                              onChange={(e) => handleUpdateFormLayoutItem(item.id, {
                                                slides: slides.map((currentSlide) => (
                                                  currentSlide.id === slide.id
                                                    ? { ...currentSlide, alt: e.target.value }
                                                    : currentSlide
                                                ))
                                              })}
                                              placeholder="Optional caption / alt text"
                                              className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                            />
                                            <button
                                              type="button"
                                              onClick={() => handleUpdateFormLayoutItem(item.id, {
                                                slides: slides.filter((currentSlide) => currentSlide.id !== slide.id)
                                              })}
                                              className="mt-3 inline-flex items-center gap-2 rounded-full border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50"
                                            >
                                              <Trash2 className="h-3.5 w-3.5" />
                                              Remove image
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
                                        Upload two or more images to create a slide block.
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              return (
                                <div key={`sheet_form_layout_${item.id}`} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 md:flex-row md:items-center md:justify-between">
                                  <div>
                                    <p className="text-sm font-semibold text-slate-800">Page break</p>
                                    <p className="mt-1 text-xs text-slate-500">Starts a new page in the published form.</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleMoveFormLayoutItem(item.id, -1)}
                                      disabled={index === 0}
                                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                      aria-label="Move page break up"
                                    >
                                      <ArrowUp className="h-4 w-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleMoveFormLayoutItem(item.id, 1)}
                                      disabled={index === sheetFormLayoutItems.length - 1}
                                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                      aria-label="Move page break down"
                                    >
                                      <ArrowDown className="h-4 w-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveFormLayoutItem(item.id)}
                                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-500 transition hover:bg-rose-50"
                                      aria-label="Remove page break"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {normalizeSheetFormSubmissionMode(sheetFormConfig.submissionMode) === 'modular' && (
                      <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-slate-800">Detail Module Fields</p>
                            <p className="mt-1 text-xs text-slate-500">
                              These fields belong to the repeatable detail module and create one sheet row per completed entry.
                            </p>
                            {availableRepeatableFormHeadersToAdd.length > 0 && (
                              <p className="mt-2 text-xs font-medium text-amber-700">
                                {availableRepeatableFormHeadersToAdd.length} eligible field{availableRepeatableFormHeadersToAdd.length === 1 ? '' : 's'} available for the detail module.
                              </p>
                            )}
                          </div>
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                            {selectedRepeatableFormFieldCount} repeatable
                          </span>
                        </div>

                        {eligibleFormHeaders.length === 0 ? (
                          <div className="mt-3 rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
                            Add at least one eligible column to configure the detail module.
                          </div>
                        ) : (
                          <div className="mt-3 space-y-4">
                            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4">
                              <div className="flex flex-wrap items-end gap-3">
                                <div className="min-w-[220px] flex-1">
                                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Add detail field</label>
                                  <select
                                    value={pendingRepeatableFormHeaderId}
                                    onChange={(e) => setPendingRepeatableFormHeaderId(e.target.value)}
                                    disabled={availableRepeatableFormHeadersToAdd.length === 0}
                                    className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100"
                                  >
                                    {availableRepeatableFormHeadersToAdd.length === 0 ? (
                                      <option value="">All eligible fields already assigned</option>
                                    ) : (
                                      <>
                                        <option value="">Select a field...</option>
                                        {availableRepeatableFormHeadersToAdd.map((header) => (
                                          <option key={`sheet_form_repeatable_available_${header.id}`} value={header.id}>{header.label}</option>
                                        ))}
                                      </>
                                    )}
                                  </select>
                                </div>
                                <button
                                  type="button"
                                  onClick={handleAddRepeatableFormField}
                                  disabled={!pendingRepeatableFormHeaderId}
                                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <Plus className="h-4 w-4" />
                                  Add Detail Field
                                </button>
                                <button
                                  type="button"
                                  onClick={handleAddRepeatableSectionDivider}
                                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                                >
                                  <Plus className="h-4 w-4" />
                                  Add Divider
                                </button>
                                <button
                                  type="button"
                                  onClick={handleAddRepeatableTextBlock}
                                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                                >
                                  <Plus className="h-4 w-4" />
                                  Add Text Block
                                </button>
                                <button
                                  type="button"
                                  onClick={handleAddRepeatableImageSlide}
                                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                                >
                                  <Plus className="h-4 w-4" />
                                  Add Image Slide
                                </button>
                                <button
                                  type="button"
                                  onClick={handleAddRepeatablePageBreak}
                                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                                >
                                  <Plus className="h-4 w-4" />
                                  Add Page Break
                                </button>
                              </div>
                              {availableRepeatableFormHeadersToAdd.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {availableRepeatableFormHeadersToAdd.map((header) => (
                                    <button
                                      key={`sheet_form_repeatable_toggle_${header.id}`}
                                      type="button"
                                      onClick={() => {
                                        setPendingRepeatableFormHeaderId(header.id);
                                        handleToggleRepeatableFormHeader(header.id);
                                      }}
                                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                                    >
                                      <Plus className="h-4 w-4" />
                                      {header.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                              <p className="mt-3 text-xs text-slate-500">
                                Use the shared module for values like school, year, or term. Use the detail module for grade, subject, enrolment, and passed.
                              </p>
                            </div>

                            <div className="space-y-2">
                              {repeatableFormLayoutItems.map((item, index) => {
                                if (item.type === 'field') {
                                  const header = eligibleFormHeaders.find((candidate) => candidate.id === item.headerId);
                                  if (!header) return null;
                                  const isRequired = (Array.isArray(sheetFormConfig.repeatableRequiredHeaderIds) ? sheetFormConfig.repeatableRequiredHeaderIds : []).includes(header.id);
                                  const fieldWidth = normalizeSheetFormFieldWidth(item.width || sheetFormConfig.repeatableFieldWidths?.[header.id]);

                                  return (
                                    <div key={`sheet_form_repeatable_layout_${item.id}`} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                      <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                          <p className="text-sm font-semibold text-slate-800">{header.label}</p>
                                          <p className="mt-1 text-xs text-slate-500">{header.type}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <button
                                            type="button"
                                            onClick={() => handleMoveRepeatableLayoutItem(item.id, -1)}
                                            disabled={index === 0}
                                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                            aria-label="Move detail item up"
                                          >
                                            <ArrowUp className="h-4 w-4" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleMoveRepeatableLayoutItem(item.id, 1)}
                                            disabled={index === repeatableFormLayoutItems.length - 1}
                                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                            aria-label="Move detail item down"
                                          >
                                            <ArrowDown className="h-4 w-4" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveRepeatableLayoutItem(item.id)}
                                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-500 transition hover:bg-rose-50"
                                            aria-label="Remove detail field from form"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </button>
                                        </div>
                                      </div>
                                      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                                        <div>
                                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Width</label>
                                          <select
                                            value={fieldWidth}
                                            onChange={(e) => handleUpdateRepeatableFormFieldWidth(header.id, e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                          >
                                            {SHEET_FORM_WIDTH_OPTIONS.map((option) => (
                                              <option key={`sheet_form_repeatable_width_${header.id}_${option.value}`} value={option.value}>{option.label}</option>
                                            ))}
                                          </select>
                                        </div>
                                        <label className="flex items-center gap-2 text-sm text-slate-700">
                                          <input
                                            type="checkbox"
                                            checked={isRequired}
                                            onChange={() => handleToggleRequiredRepeatableFormHeader(header.id)}
                                            className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                                          />
                                          <span>Required</span>
                                        </label>
                                      </div>
                                    </div>
                                  );
                                }

                                const moveControls = (
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleMoveRepeatableLayoutItem(item.id, -1)}
                                      disabled={index === 0}
                                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                      aria-label="Move detail layout item up"
                                    >
                                      <ArrowUp className="h-4 w-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleMoveRepeatableLayoutItem(item.id, 1)}
                                      disabled={index === repeatableFormLayoutItems.length - 1}
                                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                      aria-label="Move detail layout item down"
                                    >
                                      <ArrowDown className="h-4 w-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveRepeatableLayoutItem(item.id)}
                                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-500 transition hover:bg-rose-50"
                                      aria-label="Remove detail layout item"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                );

                                if (item.type === 'sectionDivider') {
                                  return (
                                    <div key={`sheet_form_repeatable_layout_${item.id}`} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                      <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                          <p className="text-sm font-semibold text-slate-800">Section divider</p>
                                          <p className="mt-1 text-xs text-slate-500">Shows a labelled divider line in each detail entry.</p>
                                        </div>
                                        {moveControls}
                                      </div>
                                      <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Divider Label</label>
                                        <input
                                          value={item.label || ''}
                                          onChange={(e) => handleUpdateRepeatableLayoutItem(item.id, { label: e.target.value })}
                                          placeholder="Section"
                                          className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                        />
                                      </div>
                                    </div>
                                  );
                                }

                                if (item.type === 'textBlock') {
                                  return (
                                    <div key={`sheet_form_repeatable_layout_${item.id}`} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                      <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                          <p className="text-sm font-semibold text-slate-800">Text block</p>
                                          <p className="mt-1 text-xs text-slate-500">Add formatted copy inside each detail entry.</p>
                                        </div>
                                        {moveControls}
                                      </div>
                                      <SheetFormRichTextEditor
                                        itemId={item.id}
                                        value={item.contentHtml || ''}
                                        onChange={(contentHtml) => handleUpdateRepeatableLayoutItem(item.id, { contentHtml })}
                                      />
                                    </div>
                                  );
                                }

                                if (item.type === 'imageSlide') {
                                  const slides = sanitizeSheetFormSlideItems(item.slides);
                                  const isUploadingSlides = uploadingFormLayoutItemId === item.id;
                                  return (
                                    <div key={`sheet_form_repeatable_layout_${item.id}`} className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                      <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                          <p className="text-sm font-semibold text-slate-800">Image slide block</p>
                                          <p className="mt-1 text-xs text-slate-500">Show images inside each detail entry.</p>
                                        </div>
                                        {moveControls}
                                      </div>
                                      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
                                        <div>
                                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Slide mode</label>
                                          <select
                                            value={normalizeSheetFormSlideMode(item.slideMode)}
                                            onChange={(e) => handleUpdateRepeatableLayoutItem(item.id, { slideMode: e.target.value })}
                                            className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                          >
                                            {SHEET_FORM_SLIDE_MODE_OPTIONS.map((option) => (
                                              <option key={`${item.id}_repeatable_slide_mode_${option.value}`} value={option.value}>{option.label}</option>
                                            ))}
                                          </select>
                                        </div>
                                        <div>
                                          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Auto advance seconds</label>
                                          <input
                                            type="number"
                                            min="2"
                                            max="30"
                                            value={normalizeSheetFormSlideIntervalSeconds(item.autoAdvanceSeconds)}
                                            disabled={normalizeSheetFormSlideMode(item.slideMode) !== 'auto'}
                                            onChange={(e) => handleUpdateRepeatableLayoutItem(item.id, { autoAdvanceSeconds: e.target.value })}
                                            className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080] disabled:bg-slate-100"
                                          />
                                        </div>
                                        <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-4 py-3 text-sm font-semibold transition ${
                                          isUploadingSlides
                                            ? 'border-slate-200 bg-slate-100 text-slate-400'
                                            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                                        }`}>
                                          <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            className="hidden"
                                            disabled={isUploadingSlides}
                                            onChange={(e) => {
                                              const files = e.target.files;
                                              if (files?.length) {
                                                handleUploadFormSlideImages(item.id, files);
                                              }
                                              e.target.value = '';
                                            }}
                                          />
                                          <Plus className="h-4 w-4" />
                                          <span>{isUploadingSlides ? 'Uploading...' : 'Add images'}</span>
                                        </label>
                                      </div>
                                      {slides.length ? (
                                        <div className="grid gap-3 md:grid-cols-3">
                                          {slides.map((slide, slideIndex) => (
                                            <div key={slide.id} className="rounded-2xl border border-slate-200 bg-white p-3">
                                              <img
                                                src={slide.url}
                                                alt={slide.alt || `Slide ${slideIndex + 1}`}
                                                className="h-36 w-full rounded-xl object-cover"
                                              />
                                              <input
                                                value={slide.alt || ''}
                                                onChange={(e) => handleUpdateRepeatableLayoutItem(item.id, {
                                                  slides: slides.map((currentSlide) => (
                                                    currentSlide.id === slide.id
                                                      ? { ...currentSlide, alt: e.target.value }
                                                      : currentSlide
                                                  ))
                                                })}
                                                placeholder="Optional caption / alt text"
                                                className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                              />
                                              <button
                                                type="button"
                                                onClick={() => handleUpdateRepeatableLayoutItem(item.id, {
                                                  slides: slides.filter((currentSlide) => currentSlide.id !== slide.id)
                                                })}
                                                className="mt-3 inline-flex items-center gap-2 rounded-full border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50"
                                              >
                                                <Trash2 className="h-3.5 w-3.5" />
                                                Remove image
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
                                          Upload two or more images to create a slide block.
                                        </div>
                                      )}
                                    </div>
                                  );
                                }

                                return (
                                  <div key={`sheet_form_repeatable_layout_${item.id}`} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 md:flex-row md:items-center md:justify-between">
                                    <div>
                                      <p className="text-sm font-semibold text-slate-800">Page break</p>
                                      <p className="mt-1 text-xs text-slate-500">Starts a new page inside the detail module.</p>
                                    </div>
                                    {moveControls}
                                  </div>
                                );
                              })}
                              {selectedRepeatableFormFieldCount === 0 && (
                                <div className="rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
                                  Select at least one detail field before saving modular mode.
                                </div>
                              )}
                            </div>
                            <div className="grid gap-4 md:grid-cols-2">
                              <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700">Detail Entry Mode</label>
                                <select
                                  value={normalizeRepeatableEntryMode(sheetFormConfig.repeatableEntryMode)}
                                  onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, repeatableEntryMode: normalizeRepeatableEntryMode(e.target.value) }))}
                                  className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                >
                                  <option value="stepper">Step-by-step</option>
                                  <option value="allAtOnce">All at once</option>
                                </select>
                              </div>
                              <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700">Add Prompt</label>
                                <input
                                  value={sheetFormConfig.repeatableAddPrompt || ''}
                                  onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, repeatableAddPrompt: e.target.value }))}
                                  placeholder="Add another grade?"
                                  className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                />
                              </div>
                              <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700">Add Button Label</label>
                                <input
                                  value={sheetFormConfig.repeatableAddLabel || ''}
                                  onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, repeatableAddLabel: e.target.value }))}
                                  placeholder="Add another grade"
                                  className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                />
                              </div>
                              <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700">Done Button Label</label>
                                <input
                                  value={sheetFormConfig.repeatableDoneLabel || ''}
                                  onChange={(e) => setSheetFormConfig((prev) => ({ ...prev, repeatableDoneLabel: e.target.value }))}
                                  placeholder="Continue"
                                  className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {isEditing && (
                      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                        <button
                          type="button"
                          onClick={() => handleCopySheetFormLink(id)}
                          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                        >
                          <Copy className="h-4 w-4" />
                          Copy Link
                        </button>
                        <button
                          type="button"
                          onClick={() => window.open(getSheetFormUrl(id), '_blank', 'noopener,noreferrer')}
                          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Open Form
                        </button>
                        <p className="text-xs text-slate-500">
                          Save this sheet to publish the latest form snapshot.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
            )}
            {activeBuilderPanel === 'analytics' && (
              <section className="rounded-3xl border-2 border-slate-300 bg-slate-50/55 p-6 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.32)]">
                <SheetAnalyticsBuilder analytics={analytics} setAnalytics={setAnalytics} headers={sanitizedHeaders} sheetId={id} primaryColor={sheetPrimaryColor} />
              </section>
            )}
            {activeBuilderPanel === 'legacy-charts' && (
            <section className="rounded-3xl border-2 border-slate-300 bg-slate-50/55 p-6 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.32)]">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Charts</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Add charts to visualize your sheet data. Charts will appear in the sheet viewer.
                </p>
              </div>

              <div className="mt-5 space-y-4">
                {charts.map((chart) => {
                  const numericHeaders = headers.filter((h) => NUMERIC_HEADER_TYPES.has(h.type) && h.label);
                  return (
                    <div
                      key={chart.id}
                      className="relative space-y-4 rounded-2xl border-2 border-slate-300 bg-white p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Chart</span>
                        <button
                          type="button"
                          onClick={() => setCharts((prev) => prev.filter((c) => c.id !== chart.id))}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                          aria-label="Remove chart"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">Title</label>
                        <input
                          value={chart.title}
                          onChange={(e) => setCharts((prev) => prev.map((c) => c.id === chart.id ? { ...c, title: e.target.value } : c))}
                          placeholder="Chart title"
                          className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">Chart Type</label>
                        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
                          {[
                            { type: 'bar', icon: BarChart3, label: 'Bar' },
                            { type: 'line', icon: TrendingUp, label: 'Line' },
                            { type: 'pie', icon: PieChartIcon, label: 'Pie' }
                          ].map(({ type, icon: Icon, label }) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => setCharts((prev) => prev.map((c) => c.id === chart.id ? { ...c, type, valueHeaderIds: type === 'pie' ? c.valueHeaderIds.slice(0, 1) : c.valueHeaderIds } : c))}
                              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                                chart.type === type
                                  ? 'bg-slate-900 text-white'
                                  : 'text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              <Icon className="h-3.5 w-3.5" />
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">Label Column (Categories / X-Axis)</label>
                        <select
                          value={chart.labelHeaderId}
                          onChange={(e) => setCharts((prev) => prev.map((c) => c.id === chart.id ? { ...c, labelHeaderId: e.target.value } : c))}
                          className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                        >
                          <option value="">Select a column...</option>
                          {headers.filter((h) => h.label).map((h) => (
                            <option key={h.id} value={h.id}>{h.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Value Column{chart.type !== 'pie' ? '(s)' : ''} (Numeric)
                        </label>
                        {numericHeaders.length === 0 ? (
                          <p className="text-sm text-slate-400">No numeric columns available. Add a Number, Currency, Percent, or Rating column first.</p>
                        ) : chart.type === 'pie' ? (
                          <select
                            value={chart.valueHeaderIds[0] || ''}
                            onChange={(e) => setCharts((prev) => prev.map((c) => c.id === chart.id ? { ...c, valueHeaderIds: e.target.value ? [e.target.value] : [] } : c))}
                            className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                          >
                            <option value="">Select a column...</option>
                            {numericHeaders.map((h) => (
                              <option key={h.id} value={h.id}>{h.label}</option>
                            ))}
                          </select>
                        ) : (
                          <div className="space-y-2">
                            {numericHeaders.map((h) => (
                              <label key={h.id} className="flex items-center gap-2 text-sm text-slate-700">
                                <input
                                  type="checkbox"
                                  checked={chart.valueHeaderIds.includes(h.id)}
                                  onChange={() => {
                                    setCharts((prev) => prev.map((c) => {
                                      if (c.id !== chart.id) return c;
                                      const ids = c.valueHeaderIds.includes(h.id)
                                        ? c.valueHeaderIds.filter((vid) => vid !== h.id)
                                        : [...c.valueHeaderIds, h.id];
                                      return { ...c, valueHeaderIds: ids };
                                    }));
                                  }}
                                  className="h-4 w-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                                />
                                {h.label}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">Color Scheme</label>
                        <div className="inline-flex gap-2">
                          {[
                            { scheme: 'default', colors: ['#008080', '#2563eb', '#7c3aed'] },
                            { scheme: 'warm', colors: ['#dc2626', '#ea580c', '#d97706'] },
                            { scheme: 'cool', colors: ['#0284c7', '#0891b2', '#0d9488'] },
                            { scheme: 'vibrant', colors: ['#7c3aed', '#db2777', '#ea580c'] }
                          ].map(({ scheme, colors }) => (
                            <button
                              key={scheme}
                              type="button"
                              onClick={() => setCharts((prev) => prev.map((c) => c.id === chart.id ? { ...c, colorScheme: scheme } : c))}
                              className={`flex items-center gap-0.5 rounded-lg border-2 px-2 py-1.5 transition ${
                                chart.colorScheme === scheme
                                  ? 'border-[#008080] bg-[#008080]/5'
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                              title={scheme}
                            >
                              {colors.map((color) => (
                                <span
                                  key={color}
                                  className="inline-block h-4 w-4 rounded-full"
                                  style={{ backgroundColor: color }}
                                />
                              ))}
                            </button>
                          ))}
                        </div>
                      </div>

                      {chart.title && chart.labelHeaderId && chart.valueHeaderIds.length > 0 && (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Preview</p>
                          <SheetChart
                            chart={chart}
                            headers={sanitizedHeaders}
                            rows={[{ id: 'preview', values: firstRowValues, resolvedValues: previewRowValues }]}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={() => setCharts((prev) => [...prev, {
                    id: createChartId(),
                    type: 'bar',
                    title: '',
                    labelHeaderId: '',
                    valueHeaderIds: [],
                    colorScheme: 'default'
                  }])}
                  className="inline-flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-3 text-sm font-semibold text-slate-600 transition hover:border-[#008080] hover:text-[#008080]"
                >
                  <Plus className="h-4 w-4" />
                  Add Chart
                </button>
              </div>
            </section>
            )}
          </div>
        </section>

        <footer className="px-6 pb-[5px] text-center text-xs text-slate-400 lg:px-10">
          &copy; ParrotSheets 2026
        </footer>
      </div>
    </div>
  );
};

export default SheetBuilder;
